import type {
  SubcontractStatus,
  OutsourcedTripsStats,
} from "@/types/trip-list";

/**
 * Base URL của API.
 *
 * Mặc định là chuỗi rỗng = same-origin: dev đi qua proxy của Vite, prod đi qua
 * Caddy, cả hai đều đưa `/api/v1/*` về NestJS. Nhờ vậy cookie `vgt.rt` luôn là
 * same-origin và không cần CORS.
 *
 * `VITE_API_URL` chỉ để ghi đè khi thực sự cần trỏ sang host khác. Biến `VITE_*`
 * được nhúng vào bundle lúc build và ai xem cũng thấy — không bao giờ đặt secret
 * vào đây.
 */
const API_URL = import.meta.env.VITE_API_URL || "";

// ==================== TYPES ====================

export interface UserProfile {
  id: string;
  email: string;
  fullName: string;
  phone: string | null;
  avatar: string | null;
  isActive: boolean;
  workStatus: string;
  employeeCode: string | null;
  position: string | null;
  hireDate: string | null;
  address: string | null;
  lastLoginAt: string | null;
  createdAt: string;
  updatedAt: string;
  departmentId: string | null;
  department: { id: string; name: string; code: string } | null;
  // branchId/branchName = chi nhánh của PHÉP GÁN role (null = toàn cục)
  roles: {
    name: string;
    displayName: string;
    level: string;
    department: string | null;
    branchId?: string | null;
    branchName?: string | null;
  }[];
  branches: { id: string; name: string; code: string; isPrimary: boolean }[];
  permissions: {
    module: string;
    action: string;
    scope: string;
    branchId?: string | null;
  }[];
}

export interface UserItem {
  id: string;
  email: string;
  fullName: string;
  phone?: string;
  employeeCode?: string;
  position?: string;
  hireDate?: string;
  address?: string;
  isActive: boolean;
  workStatus: string;
  createdAt: string;
  lastLoginAt?: string;
  department?: { id: string; name: string; code: string };
  roles: {
    id: string;
    name: string;
    displayName: string;
    level: string;
    branchId?: string | null;
    branchName?: string | null;
  }[];
  branches: { id: string; name: string; code: string; isPrimary: boolean }[];
  // Thông tin cá nhân HR
  firstName?: string;
  lastName?: string;
  dateOfBirth?: string;
  gender?: string;
  hometown?: string;
  permanentAddress?: string;
  nationalId?: string;
  socialInsuranceNo?: string;
  insuranceSalary?: number | string;
  educationLevel?: string;
}

export interface UserListResponse {
  data: UserItem[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface DropdownOptions {
  roles: { id: string; name: string; displayName: string; level: string }[];
  branches: { id: string; name: string; code: string }[];
  departments: { id: string; name: string; code: string }[];
}

// ==================== HELPERS ====================

/**
 * Module-level access token holder. Token KHÔNG bao giờ được ghi vào localStorage/cookie
 * accessible từ JS — chỉ tồn tại trong memory để giảm rủi ro XSS.
 *
 * AuthContext gọi `setApiAccessToken(token)` sau khi login/refresh thành công.
 * `apiFetch` đọc token qua `getApiAccessToken()` và auto refresh khi gặp 401.
 */
let _accessToken: string | null = null;

export function setApiAccessToken(token: string | null): void {
  _accessToken = token;
}

export function getApiAccessToken(): string | null {
  return _accessToken;
}

/**
 * Kết quả refresh:
 *  - `{ token }`         → thành công
 *  - `{ expired: true }` → phiên thực sự hết hạn (BE trả 401/403) → phải logout
 *  - `{ expired: false }`→ lỗi tạm thời (mất mạng, BE 5xx) → KHÔNG logout,
 *    cookie còn nguyên, lần gọi sau thử lại. Quan trọng cho tài xế đi vào
 *    vùng sóng yếu đúng lúc access token hết hạn.
 */
export type RefreshResult = { token: string | null; expired: boolean };

/**
 * Single-flight refresh: nhiều request 401 đồng thời sẽ chia sẻ cùng một promise refresh
 * thay vì gọi `/api/auth/refresh` song song nhiều lần.
 */
let _refreshingResult: Promise<RefreshResult> | null = null;

async function refreshAccessTokenOnce(): Promise<RefreshResult> {
  if (_refreshingResult) return _refreshingResult;

  _refreshingResult = (async () => {
    try {
      // FE-22: dùng cùng API_URL với mọi lời gọi khác trong file — đường tương
      // đối chỉ đúng khi web và api cùng origin (mô hình mặc định qua Caddy).
      // Nếu một ngày tách domain riêng cho API (VITE_API_URL khác rỗng), gọi
      // đường tương đối sẽ 404 trong khi phần còn lại của app vẫn hoạt động,
      // rất khó nhận ra nguyên nhân.
      const res = await fetch(`${API_URL}/api/v1/auth/session/refresh`, {
        method: "POST",
        credentials: "include",
        cache: "no-store",
      });
      if (!res.ok) {
        // 401/403 = phiên chết thật; các mã khác (5xx, 429...) = tạm thời
        return {
          token: null,
          expired: res.status === 401 || res.status === 403,
        };
      }
      const data = (await res.json()) as { accessToken?: string };
      if (!data.accessToken) return { token: null, expired: false };
      _accessToken = data.accessToken;
      return { token: data.accessToken, expired: false };
    } catch {
      // Network error — offline/chập chờn, không phải hết phiên
      return { token: null, expired: false };
    } finally {
      _refreshingResult = null;
    }
  })();

  return _refreshingResult;
}

/**
 * Cửa ra cho những nơi KHÔNG đi qua `apiFetch` nhưng cần access token mới —
 * hiện chỉ có socket thông báo (`lib/notifications/socket.ts`), vì handshake
 * Socket.IO chỉ xác thực một lần lúc bắt tay. Dùng chung promise single-flight
 * với `apiFetch` nên không bao giờ sinh thêm lời gọi refresh song song.
 *
 * Giữ nguyên ngữ nghĩa `RefreshResult`: `expired: true` mới là phiên chết thật.
 */
export function refreshApiAccessToken(): Promise<RefreshResult> {
  return refreshAccessTokenOnce();
}

/**
 * @deprecated Kept for backwards compat with helpers gọi token để upload file.
 * Sẽ được xóa khi các helper upload chuyển sang `apiFetch`-style.
 */
function getToken(): string {
  return _accessToken ?? "";
}

/**
 * Header chi nhánh đang chọn. `apiFetch` tự gắn; các fetch thô (upload multipart,
 * tải PDF, reports) PHẢI spread helper này vào headers — thiếu nó backend coi như
 * chưa chọn chi nhánh và quyền gắn chi nhánh sẽ bị 403.
 */
export function branchHeader(): Record<string, string> {
  if (typeof window === "undefined") return {};
  try {
    // FE-20 (audit 07-frontend.md): trình duyệt chặn storage (chế độ riêng
    // tư nghiêm ngặt của Safari, cấu hình doanh nghiệp...) khiến
    // `localStorage.getItem` NÉM lỗi thay vì trả null — không try/catch thì
    // MỌI request qua `apiFetch` hỏng theo, kể cả những request không cần
    // chi nhánh nào.
    const branch = localStorage.getItem("vgt.activeBranchId");
    return branch ? { "X-Active-Branch-Id": branch } : {};
  } catch {
    return {};
  }
}

/** Mạng treo bao lâu thì bỏ cuộc và báo lỗi, thay vì để nút quay vô hạn. */
const REQUEST_TIMEOUT_MS = 30_000;

/**
 * Lỗi API có kèm HTTP status — cho phép UI phân biệt 403 (thiếu quyền) với
 * lỗi validate/500. Mọi catch cũ chỉ đọc `err.message` vẫn chạy nguyên.
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    /** Thân JSON của response lỗi (vd `blockers` của 409 ghi nhận bán xe). */
    public readonly payload?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export function isForbiddenError(err: unknown): boolean {
  return err instanceof ApiError && err.status === 403;
}

/**
 * Gom thông điệp lỗi từ response về một chuỗi đọc được.
 *
 * NestJS ValidationPipe trả `message` là MẢNG chuỗi; `err.message` khi đó là
 * mảng nên rơi vào nhánh mặc định và người dùng chỉ thấy "Lỗi hệ thống" mà
 * không biết trường nào sai.
 */
function readErrorMessage(payload: unknown, fallback: string): string {
  const msg = (payload as { message?: unknown })?.message;
  if (Array.isArray(msg)) return msg.filter(Boolean).join(". ");
  if (typeof msg === "string" && msg.trim()) return msg;
  return fallback;
}

export async function apiFetch<T>(
  path: string,
  opts?: RequestInit,
): Promise<T> {
  const buildHeaders = (token: string | null): Record<string, string> => {
    const h: Record<string, string> = {
      "Content-Type": "application/json",
      ...branchHeader(),
    };
    if (token) h["Authorization"] = `Bearer ${token}`;
    return { ...h, ...((opts?.headers as Record<string, string>) ?? {}) };
  };

  const send = (token: string | null) =>
    fetch(`${API_URL}${path}`, {
      ...opts,
      headers: buildHeaders(token),
      // Giữ signal do caller truyền nếu có; không thì tự đặt hạn.
      signal: opts?.signal ?? AbortSignal.timeout(REQUEST_TIMEOUT_MS),
    }).catch((e: unknown) => {
      if (
        (e as Error)?.name === "TimeoutError" ||
        (e as Error)?.name === "AbortError"
      ) {
        throw new Error(
          "Máy chủ không phản hồi. Kiểm tra kết nối mạng và thử lại.",
        );
      }
      throw e;
    });

  let res = await send(_accessToken);

  // Auto refresh on 401 (chỉ cho non-auth endpoints để tránh recursion)
  if (
    res.status === 401 &&
    !path.startsWith("/api/v1/auth/") &&
    typeof window !== "undefined"
  ) {
    const refresh = await refreshAccessTokenOnce();
    if (refresh.token) {
      res = await send(refresh.token);
    } else if (refresh.expired) {
      // Phiên hết hạn THẬT → AuthProvider clear state + redirect login.
      window.dispatchEvent(new CustomEvent("vgt:session-expired"));
    } else {
      // Lỗi tạm thời (mạng / BE 5xx / refresh bị rate-limit). Trước đây rơi
      // xuống dưới và ném lỗi dựa trên response 401 CŨ, người dùng thấy "Lỗi
      // hệ thống" mơ hồ rồi bấm lại thì được — đúng kiểu "lúc được lúc không".
      throw new Error(
        "Phiên đăng nhập cần được làm mới nhưng chưa thực hiện được. Vui lòng thử lại sau giây lát.",
      );
    }
  }

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const fallback =
      res.status === 403
        ? "Bạn không có quyền thực hiện thao tác này"
        : "Lỗi hệ thống";
    throw new ApiError(readErrorMessage(err, fallback), res.status, err);
  }

  // 204 No Content (vd `DELETE /notifications/:id`): không có thân để parse —
  // `res.json()` sẽ ném SyntaxError DÙ server đã xử lý thành công, khiến
  // mutation báo lỗi và UI không làm mới.
  if (res.status === 204) return undefined as T;

  return res.json();
}

// ==================== AUTH API ====================
//
// SEC-14 / FE-14 (29/09/2026): `loginApi`, `getProfileApi`, `refreshTokenApi`
// gọi `/auth/login`, `/auth/profile` (GET không cần auth ở đây), `/auth/refresh`
// kiểu cũ — đã gỡ vì không còn nơi nào trong `apps/web` gọi chúng (confirmed
// bằng grep toàn repo). Luồng hiện tại: `POST /api/v1/auth/session/login` từ
// `login-form.tsx`, silent refresh từ `auth-context.tsx`, và `apiFetch` cho các
// endpoint cần Bearer — không hàm nào trong ba hàm trên còn được gọi.

export async function updateProfileApi(data: {
  fullName?: string;
  phone?: string;
  position?: string;
  address?: string;
}): Promise<UserProfile> {
  return apiFetch<UserProfile>("/api/v1/auth/profile", {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function changeOwnPasswordApi(data: {
  currentPassword: string;
  newPassword: string;
}): Promise<{ message: string }> {
  return apiFetch<{ message: string }>("/api/v1/auth/change-password", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

// ==================== USERS API ====================

export interface UserQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  roleId?: string;
  branchId?: string;
  departmentId?: string;
  isActive?: string;
  workStatus?: string;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
}

export async function getUsersApi(
  params: UserQueryParams,
): Promise<UserListResponse> {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([key, val]) => {
    if (val !== undefined && val !== "") qs.set(key, String(val));
  });
  return apiFetch<UserListResponse>(`/api/v1/users?${qs}`);
}

export async function getUserApi(id: string): Promise<UserItem> {
  return apiFetch<UserItem>(`/api/v1/users/${id}`);
}

export async function getUserOptionsApi(): Promise<DropdownOptions> {
  return apiFetch<DropdownOptions>("/api/v1/users/options");
}

export async function createUserApi(data: {
  email: string;
  password: string;
  fullName: string;
  phone?: string;
  // employeeCode: server tự sinh theo số thứ tự, client không gửi (19/10/2026)
  position?: string;
  hireDate?: string;
  address?: string;
  departmentId?: string;
  roleIds?: string[];
  roleAssignments?: { roleId: string; branchId?: string | null }[];
  branches?: { branchId: string; isPrimary: boolean }[];
}): Promise<UserItem> {
  return apiFetch<UserItem>("/api/v1/users", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function updateUserApi(
  id: string,
  data: {
    fullName?: string;
    phone?: string;
    isActive?: boolean;
    workStatus?: string;
    // employeeCode: không sửa được qua API (19/10/2026)
    position?: string;
    hireDate?: string;
    address?: string;
    departmentId?: string;
    roleIds?: string[];
    roleAssignments?: { roleId: string; branchId?: string | null }[];
    branches?: { branchId: string; isPrimary: boolean }[];
    // Thông tin cá nhân HR
    firstName?: string;
    lastName?: string;
    dateOfBirth?: string;
    gender?: string;
    hometown?: string;
    permanentAddress?: string;
    nationalId?: string;
    socialInsuranceNo?: string;
    insuranceSalary?: number;
    educationLevel?: string;
  },
): Promise<UserItem> {
  return apiFetch<UserItem>(`/api/v1/users/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function deleteUserApi(id: string): Promise<{ message: string }> {
  return apiFetch<{ message: string }>(`/api/v1/users/${id}`, {
    method: "DELETE",
  });
}

export async function toggleUserActiveApi(
  id: string,
): Promise<{ message: string; isActive: boolean }> {
  return apiFetch<{ message: string; isActive: boolean }>(
    `/api/v1/users/${id}/toggle-active`,
    {
      method: "PUT",
    },
  );
}

export async function changeUserPasswordApi(
  id: string,
  newPassword: string,
): Promise<{ message: string }> {
  return apiFetch<{ message: string }>(`/api/v1/users/${id}/password`, {
    method: "PUT",
    body: JSON.stringify({ newPassword }),
  });
}

export async function bulkToggleActiveApi(
  userIds: string[],
  isActive: boolean,
): Promise<{ message: string; affectedCount: number }> {
  return apiFetch<{ message: string; affectedCount: number }>(
    "/api/v1/users/bulk-toggle-active",
    {
      method: "POST",
      body: JSON.stringify({ userIds, isActive }),
    },
  );
}

export async function bulkAssignBranchApi(
  userIds: string[],
  branchId: string,
  isPrimary?: boolean,
): Promise<{ message: string }> {
  return apiFetch<{ message: string }>("/api/v1/users/bulk-assign-branch", {
    method: "POST",
    body: JSON.stringify({ userIds, branchId, isPrimary }),
  });
}

export function exportUsersUrl(params: UserQueryParams): string {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([key, val]) => {
    if (val !== undefined && val !== "") qs.set(key, String(val));
  });
  return `${API_URL}/api/v1/users/export?${qs}`;
}

// ==================== SETTINGS API ====================

export type SettingType =
  | "string"
  | "number"
  | "boolean"
  | "json"
  | "password"
  | "image"
  | "secret";
export type SettingGroup =
  | "company"
  | "document"
  | "business"
  | "ai"
  | "sync"
  | "notification";

/**
 * Tình trạng khoá `secret` (khoá API AI…) — API không bao giờ trả giá trị thật,
 * chỉ đã lưu hay chưa và 4 ký tự cuối.
 */
export interface SettingSecretState {
  stored: boolean;
  /** Bản mã giải được bằng khoá chủ hiện tại của máy chủ. */
  readable: boolean;
  /** "…abcd" của khoá đang dùng (đã lưu, hoặc biến môi trường dự phòng). */
  hint: string | null;
  envFallback: string | null;
  envFallbackSet: boolean;
  /** Máy chủ đã có SETTINGS_SECRET_KEY — chưa có thì không lưu được. */
  canStore: boolean;
}

export interface SettingItem {
  id: string;
  key: string;
  value: any;
  group: SettingGroup | string;
  label: string;
  description: string | null;
  type: SettingType | string;
  createdAt: string;
  updatedAt: string;
  // Metadata từ catalog backend (không lưu DB)
  unit?: string;
  usedIn?: string;
  defaultValue?: any;
  min?: number;
  max?: number;
  optional: boolean;
  /** Chuỗi là danh sách tách phẩy, mỗi phần tử thuộc tập này. */
  listOf?: string[];
  /** Chỉ có ở khoá `secret` — `value` luôn rỗng. */
  secret?: SettingSecretState;
}

export type GroupedSettings = Record<string, SettingItem[]>;

/** Khoá còn trong DB nhưng không còn code nào đọc — backend đề xuất gỡ. */
export interface ObsoleteSettingItem {
  key: string;
  group: string;
  label: string;
  type: string;
  value: any;
  updatedAt: string;
  reason: string;
}

/** Trạng thái tích hợp cấu hình bằng biến môi trường — không có secret. */
export interface SettingsRuntimeStatus {
  email: {
    configured: boolean;
    host: string;
    port: string;
    from: string | null;
    envVars: string[];
  };
  storage: { configured: boolean; provider: string | null; envVars: string[] };
  vietmap: { configured: boolean; envVars: string[] };
  /** Khoá API + model AI cấu hình ở nhóm "AI"; env chỉ còn khoá chủ và khoá dự phòng. */
  ai: { configured: boolean; masterKeyConfigured: boolean; envVars: string[] };
  auth: { accessTokenTtl: string; refreshTokenTtl: string; envVars: string[] };
}

export async function getSettingsGroupedApi(): Promise<GroupedSettings> {
  return apiFetch<GroupedSettings>("/api/v1/settings");
}

export async function getObsoleteSettingsApi(): Promise<ObsoleteSettingItem[]> {
  return apiFetch<ObsoleteSettingItem[]>("/api/v1/settings/obsolete");
}

export async function deleteObsoleteSettingsApi(
  keys?: string[],
): Promise<{ message: string; count: number }> {
  return apiFetch<{ message: string; count: number }>(
    "/api/v1/settings/obsolete",
    {
      method: "DELETE",
      body: JSON.stringify(keys && keys.length > 0 ? { keys } : {}),
    },
  );
}

export async function getSettingsRuntimeApi(): Promise<SettingsRuntimeStatus> {
  return apiFetch<SettingsRuntimeStatus>("/api/v1/settings/runtime");
}

export async function getSettingsListApi(
  group?: string,
): Promise<SettingItem[]> {
  const qs = group ? `?group=${group}` : "";
  return apiFetch<SettingItem[]>(`/api/v1/settings/list${qs}`);
}

export async function updateSettingApi(
  key: string,
  data: { value?: any; label?: string; description?: string },
): Promise<SettingItem> {
  return apiFetch<SettingItem>(`/api/v1/settings/${key}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function bulkUpdateSettingsApi(
  updates: { key: string; value: any }[],
): Promise<{ message: string; count: number }> {
  return apiFetch<{ message: string; count: number }>(
    "/api/v1/settings/bulk-update",
    {
      method: "POST",
      body: JSON.stringify({ updates }),
    },
  );
}

// ==================== CUSTOMERS ====================

export interface CustomerItem {
  id: string;
  code: string;
  name: string;
  shortName?: string;
  /** Tên rút gọn để hiển thị (server tự điền khi trống). */
  displayName?: string | null;
  type: "PERSONAL" | "COMPANY";
  taxCode?: string;
  /** Tên giao dịch quốc tế — từ tra cứu MST, được sửa tay. */
  internationalName?: string | null;
  /** Trạng thái thuế nguyên văn tại thời điểm tra cứu; luôn xem cùng taxCheckedAt. */
  taxStatus?: string | null;
  taxCheckedAt?: string | null;
  phone?: string;
  email?: string;
  address?: string;
  contactPerson?: string;
  bankName?: string;
  bankAccount?: string;
  bankAccountName?: string;
  note?: string;
  isActive: boolean;
  /** % phí quản lý chi hộ áp cho Payment Request; null = dùng mặc định công ty. */
  managementFeeRate?: number | null;
  createdAt: string;
  updatedAt: string;
  branch?: { id: string; name: string; code: string };
}

export interface CustomerListResponse {
  data: CustomerItem[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}

export async function getCustomersApi(
  params?: Record<string, string>,
): Promise<CustomerListResponse> {
  const qs = params ? "?" + new URLSearchParams(params).toString() : "";
  return apiFetch<CustomerListResponse>(`/api/v1/customers${qs}`);
}

export async function getCustomerApi(id: string): Promise<CustomerItem> {
  return apiFetch<CustomerItem>(`/api/v1/customers/${id}`);
}

export async function createCustomerApi(
  data: Partial<CustomerItem>,
): Promise<CustomerItem> {
  return apiFetch<CustomerItem>("/api/v1/customers", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function updateCustomerApi(
  id: string,
  data: Partial<CustomerItem>,
): Promise<CustomerItem> {
  return apiFetch<CustomerItem>(`/api/v1/customers/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function deleteCustomerApi(
  id: string,
): Promise<{ message: string }> {
  return apiFetch<{ message: string }>(`/api/v1/customers/${id}`, {
    method: "DELETE",
  });
}

export async function getCustomerOptionsApi(): Promise<
  { id: string; code: string; name: string; displayName?: string | null }[]
> {
  return apiFetch<
    { id: string; code: string; name: string; displayName?: string | null }[]
  >("/api/v1/customers/options");
}

export const getCustomerNextCodeApi = () =>
  apiFetch<{ code: string }>("/api/v1/customers/next-code");

// ==================== CUSTOMERS — TÀI KHOẢN CỔNG KHÁCH HÀNG (FE-09) ====================

export interface CustomerPortalUserItem {
  id: string;
  email: string;
  fullName: string;
  phone?: string | null;
  isActive: boolean;
  lastLoginAt?: string | null;
  createdAt: string;
}

export const getCustomerPortalUsersApi = (customerId: string) =>
  apiFetch<CustomerPortalUserItem[]>(
    `/api/v1/customers/${customerId}/portal-users`,
  );

export const createCustomerPortalUserApi = (
  customerId: string,
  data: { email: string; password: string; fullName: string; phone?: string },
) =>
  apiFetch<CustomerPortalUserItem>(
    `/api/v1/customers/${customerId}/portal-users`,
    {
      method: "POST",
      body: JSON.stringify(data),
    },
  );

export const updateCustomerPortalUserApi = (
  customerId: string,
  portalUserId: string,
  data: {
    fullName?: string;
    phone?: string;
    isActive?: boolean;
    password?: string;
  },
) =>
  apiFetch<CustomerPortalUserItem>(
    `/api/v1/customers/${customerId}/portal-users/${portalUserId}`,
    { method: "PUT", body: JSON.stringify(data) },
  );

// ==================== TRA CỨU MÃ SỐ THUẾ ====================

/** Bản ghi trong ERP đang giữ MST vừa tra. */
export interface TaxLookupExistingRecord {
  id: string;
  code: string;
  name: string;
}

export interface TaxLookupResult {
  taxCode: string;
  name: string;
  internationalName: string | null;
  shortName: string | null;
  displayName?: string | null;
  address: string | null;
  /** Trạng thái thuế nguyên văn, vd "NNT đang hoạt động" */
  status: string | null;
  isActive: boolean;
  source: string;
  fetchedAt: string;
  /** Khác null nghĩa là MST đã có khách hàng trong ERP → tạo mới sẽ bị chặn 409 */
  existingCustomer: TaxLookupExistingRecord | null;
  /** Khác null nghĩa là MST đã có đối tác trong ERP → tạo mới sẽ bị chặn 409 */
  existingPartner: TaxLookupExistingRecord | null;
}

/**
 * Ngữ cảnh tra cứu. Quyết định endpoint nào được gọi, và do đó quyền nào được
 * kiểm tra ('customers.create' hay 'partners.create') cùng bảng nào được dò trùng.
 */
export type TaxLookupScope = "customer" | "partner";

/**
 * Tra thông tin doanh nghiệp theo MST (nguồn: VietQR, dữ liệu Cục Thuế).
 *
 * Ném ApiError với status 400 (MST sai định dạng), 404 (không tìm thấy),
 * 502 (dịch vụ ngoài lỗi). Mọi trường hợp lỗi đều KHÔNG được chặn người dùng
 * nhập tay — tra cứu chỉ là tiện ích điền nhanh.
 */
export async function lookupTaxCodeApi(
  taxCode: string,
  scope: TaxLookupScope = "customer",
  opts?: {
    /** Id bản ghi đang sửa — backend bỏ qua nó khi dò trùng MST. */
    excludeId?: string;
  },
): Promise<TaxLookupResult> {
  const encoded = encodeURIComponent(taxCode);
  const base =
    scope === "partner"
      ? `/api/v1/tax-lookup/partners/${encoded}`
      : `/api/v1/tax-lookup/${encoded}`;
  const path = opts?.excludeId
    ? `${base}?excludeId=${encodeURIComponent(opts.excludeId)}`
    : base;
  return apiFetch<TaxLookupResult>(path);
}

// ==================== PARTNERS ====================

export interface PartnerItem {
  id: string;
  code: string;
  name: string;
  shortName?: string;
  /** Tên rút gọn để hiển thị (server tự điền khi trống). */
  displayName?: string | null;
  type: "SHIPPING_LINE" | "TRUCKING" | "FORWARDING" | "DEPOT" | "OTHER";
  taxCode?: string;
  phone?: string;
  email?: string;
  address?: string;
  contactPerson?: string;
  bankName?: string;
  bankAccount?: string;
  bankAccountName?: string;
  serviceTypes?: string;
  note?: string;
  isActive: boolean;
  // Hồ sơ năng lực thầu phụ
  vehicleCount?: number;
  capacityNote?: string;
  rating?: number | null;
  isBlocked?: boolean;
  blockedReason?: string | null;
  blockedAt?: string | null;
  createdAt: string;
  updatedAt: string;
  branch?: { id: string; name: string; code: string };
}

export interface PartnerListResponse {
  data: PartnerItem[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}

export async function getPartnersApi(
  params?: Record<string, string>,
): Promise<PartnerListResponse> {
  const qs = params ? "?" + new URLSearchParams(params).toString() : "";
  return apiFetch<PartnerListResponse>(`/api/v1/partners${qs}`);
}

export async function getPartnerApi(id: string): Promise<PartnerItem> {
  return apiFetch<PartnerItem>(`/api/v1/partners/${id}`);
}

export async function createPartnerApi(
  data: Partial<PartnerItem>,
): Promise<PartnerItem> {
  return apiFetch<PartnerItem>("/api/v1/partners", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function updatePartnerApi(
  id: string,
  data: Partial<PartnerItem>,
): Promise<PartnerItem> {
  return apiFetch<PartnerItem>(`/api/v1/partners/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

/** MD-11 — dropdown đối tác cho form hợp đồng (type=PARTNER). */
export async function getPartnerOptionsApi(): Promise<
  { id: string; code: string; name: string; type?: string }[]
> {
  return apiFetch<{ id: string; code: string; name: string; type?: string }[]>(
    "/api/v1/partners/options",
  );
}

// ─── Subcontractor: certificates / rate cards / evaluations / block ───
export interface PartnerCertItem {
  id: string;
  partnerId: string;
  type: string;
  name: string;
  documentNo?: string | null;
  issuer?: string | null;
  issueDate?: string | null;
  expiryDate?: string | null;
  fileKey?: string | null;
  fileName?: string | null;
  note?: string | null;
  createdAt: string;
}
export interface PartnerRateCardItem {
  id: string;
  partnerId: string;
  origin: string;
  destination: string;
  containerSize?: string | null;
  unit: string;
  price: number;
  currency: string;
  validFrom?: string | null;
  validTo?: string | null;
  isActive: boolean;
  note?: string | null;
  createdAt: string;
}
export interface PartnerEvaluationItem {
  id: string;
  partnerId: string;
  period: string;
  scoreQuality: number;
  scoreOnTime: number;
  scorePrice: number;
  scoreHSE: number;
  scoreOverall: number;
  incidentCount: number;
  note?: string | null;
  evaluatedBy?: string | null;
  approvedBy?: string | null;
  approvedAt?: string | null;
  createdAt: string;
}
export interface EligibleSubcontractor {
  id: string;
  code: string;
  name: string;
  displayName?: string | null;
  rating?: number | null;
  hasExpiringSoon?: boolean;
}

export const getPartnerCertsApi = (id: string) =>
  apiFetch<PartnerCertItem[]>(`/api/v1/partners/${id}/certificates`);
export const createPartnerCertApi = (id: string, d: Partial<PartnerCertItem>) =>
  apiFetch<PartnerCertItem>(`/api/v1/partners/${id}/certificates`, {
    method: "POST",
    body: JSON.stringify(d),
  });
export const updatePartnerCertApi = (
  certId: string,
  d: Partial<PartnerCertItem>,
) =>
  apiFetch<PartnerCertItem>(`/api/v1/partners/certificates/${certId}`, {
    method: "PUT",
    body: JSON.stringify(d),
  });
export const deletePartnerCertApi = (certId: string) =>
  apiFetch<{ message: string }>(`/api/v1/partners/certificates/${certId}`, {
    method: "DELETE",
  });
export const getPartnerCertAlertsApi = (days?: number) =>
  apiFetch<
    (PartnerCertItem & {
      expired: boolean;
      partner: { id: string; code: string; name: string };
    })[]
  >(`/api/v1/partners/certificates/alerts${days ? "?days=" + days : ""}`);

export const getPartnerRateCardsApi = (id: string) =>
  apiFetch<PartnerRateCardItem[]>(`/api/v1/partners/${id}/rate-cards`);
export const createPartnerRateCardApi = (
  id: string,
  d: Partial<PartnerRateCardItem>,
) =>
  apiFetch<PartnerRateCardItem>(`/api/v1/partners/${id}/rate-cards`, {
    method: "POST",
    body: JSON.stringify(d),
  });
export const updatePartnerRateCardApi = (
  rateCardId: string,
  d: Partial<PartnerRateCardItem>,
) =>
  apiFetch<PartnerRateCardItem>(`/api/v1/partners/rate-cards/${rateCardId}`, {
    method: "PUT",
    body: JSON.stringify(d),
  });
export const deletePartnerRateCardApi = (rateCardId: string) =>
  apiFetch<{ message: string }>(`/api/v1/partners/rate-cards/${rateCardId}`, {
    method: "DELETE",
  });
export const lookupPartnerRateApi = (
  id: string,
  d: {
    origin: string;
    destination: string;
    containerSize?: string;
    containerCount?: number;
    distanceKm?: number;
    shiftCount?: number;
  },
) =>
  apiFetch<{
    found: boolean;
    rateCard: PartnerRateCardItem | null;
    quantity?: number | null;
    amount?: number | null;
    needsQuantity?: boolean;
  }>(`/api/v1/partners/${id}/rate-lookup`, {
    method: "POST",
    body: JSON.stringify(d),
  });

export const getPartnerEvaluationsApi = (id: string) =>
  apiFetch<PartnerEvaluationItem[]>(`/api/v1/partners/${id}/evaluations`);
export const createPartnerEvaluationApi = (
  id: string,
  d: Partial<PartnerEvaluationItem>,
) =>
  apiFetch<PartnerEvaluationItem>(`/api/v1/partners/${id}/evaluations`, {
    method: "POST",
    body: JSON.stringify(d),
  });
export const approvePartnerEvaluationApi = (evalId: string) =>
  apiFetch<PartnerEvaluationItem>(
    `/api/v1/partners/evaluations/${evalId}/approve`,
    { method: "POST" },
  );

export const blockPartnerApi = (id: string, reason?: string) =>
  apiFetch<{ message: string }>(`/api/v1/partners/${id}/block`, {
    method: "PUT",
    body: JSON.stringify({ reason }),
  });
export const unblockPartnerApi = (id: string) =>
  apiFetch<{ message: string }>(`/api/v1/partners/${id}/unblock`, {
    method: "PUT",
  });
export const getEligibleSubcontractorsApi = () =>
  apiFetch<EligibleSubcontractor[]>(`/api/v1/partners/subcontractors/eligible`);

// ─── Subcontractor trips (lịch sử chuyến thầu phụ) ───
export interface PartnerTripItem {
  id: string;
  code: string;
  status: string;
  containerNo?: string | null;
  subVehiclePlate?: string | null;
  subDriverName?: string | null;
  subDriverPhone?: string | null;
  agreedRate?: number | null;
  serviceConfirmed: boolean;
  serviceConfirmedAt?: string | null;
  startedAt?: string | null;
  completedAt?: string | null;
  distance?: number | null;
  createdAt: string;
  dispatch?: {
    id: string;
    code: string;
    pickupLocation?: string | null;
    deliveryLocation?: string | null;
    order?: {
      id: string;
      code: string;
      customer?: { name: string; displayName?: string | null } | null;
    } | null;
  } | null;
}
export interface PartnerTripStats {
  total: number;
  running: number;
  completed: number;
  pendingConfirm: number;
  confirmedRateTotal: number;
}
export const getPartnerTripsApi = (
  id: string,
  params?: Record<string, string>,
) =>
  apiFetch<{
    data: PartnerTripItem[];
    meta: { total: number; page: number; limit: number; totalPages: number };
  }>(
    `/api/v1/partners/${id}/trips${params ? "?" + new URLSearchParams(params) : ""}`,
  );
export const getPartnerTripStatsApi = (id: string) =>
  apiFetch<PartnerTripStats>(`/api/v1/partners/${id}/trip-stats`);

export async function deletePartnerApi(
  id: string,
): Promise<{ message: string }> {
  return apiFetch<{ message: string }>(`/api/v1/partners/${id}`, {
    method: "DELETE",
  });
}

export const getPartnerNextCodeApi = () =>
  apiFetch<{ code: string }>("/api/v1/partners/next-code");

// ==================== VEHICLES ====================
export interface VehicleItem {
  id: string;
  plateNumber: string;
  /** Mã quản lý XE-0001 (có thể chưa có). */
  code?: string | null;
  fleetStatus?: FleetStatus;
  type?: string;
  brand?: string;
  model?: string;
  year?: number;
  /** Tải trọng — dữ liệu cũ, đơn vị lẫn lộn tấn/kg. Không dùng làm khối lượng bản thân. */
  payload?: number;
  /** Khối lượng bản thân (kg) theo giấy đăng kiểm. */
  curbWeightKg?: number | null;
  vcxDecision?: VcxDecision;
  compliance?: ComplianceSummary;
  /** Tài xế chính đang phân công (chỉ có ở API danh sách). */
  primaryDriver?: { id: string; fullName: string; phone: string | null } | null;
  registrationExp?: string;
  insuranceExp?: string;
  gpsDeviceId?: string;
  status: string;
  note?: string;
  createdAt: string;
  vin?: string;
  engineNumber?: string;
  color?: string;
  fuelType?: string;
  tankCapacity?: number;
  odometer?: number;
  lastOdoUpdate?: string;
  vietmapId?: string;
  vietmapPlate?: string;
  lastGpsLat?: number;
  lastGpsLng?: number;
  lastGpsSpeed?: number;
  lastGpsTime?: string;
  lastGpsAddress?: string;
  registrationNo?: string;
  registrationDate?: string;
  insuranceNo?: string;
  insuranceCompany?: string;
  branchId?: string | null;
  branch?: { id: string; name: string; code: string };
  groupId?: string | null;
  group?: { id: string; name: string; color?: string };
}
export interface MaintenanceTypeItem {
  id: string;
  name: string;
  category: string;
  intervalKm?: number;
  intervalDays?: number;
  description?: string;
  isActive: boolean;
  _count?: { maintenances: number };
}
export interface MaintenanceItem {
  id: string;
  vehicleId: string;
  typeId: string;
  status: string;
  scheduledDate?: string;
  scheduledKm?: number;
  completedDate?: string;
  completedKm?: number;
  cost?: number;
  supplierId?: string;
  note?: string;
  vehicle?: {
    id: string;
    plateNumber: string;
    brand?: string;
    type?: string;
    odometer?: number;
  };
  type?: {
    id: string;
    name: string;
    category: string;
    intervalKm?: number;
    intervalDays?: number;
  };
  supplier?: { id: string; code: string; name: string };
  createdAt: string;
}
export interface InspectionItem {
  id: string;
  vehicleId?: string | null;
  trailerId?: string | null;
  type: string;
  /** 'TNDS' | 'VCX' khi type = INSURANCE; null = hồ sơ cũ chưa phân loại. */
  insuranceKind?: InsuranceKind | null;
  status: string;
  certificateNo?: string;
  issueDate?: string;
  validFrom?: string | null;
  expiryDate: string;
  inspectionCenter?: string;
  cost?: number;
  note?: string;
  cancelledAt?: string | null;
  cancelReason?: string | null;
  renewedFromId?: string | null;
  createdBy?: string | null;
  createdAt?: string;
  vehicle?: { id: string; plateNumber: string; brand?: string; type?: string } | null;
  trailer?: { id: string; plateNumber: string; type?: string } | null;
}

// ─── Hồ sơ theo kỳ: TNDS / VCX / đăng kiểm (apps/api/.../inspections/compliance.ts) ───
export type InsuranceKind = 'TNDS' | 'VCX';
export type VcxDecision = 'NOT_PURCHASED' | null;
export type ComplianceKind = 'TNDS' | 'VCX' | 'REGISTRATION';
export type ComplianceStatus =
  | 'NO_RECORD'
  | 'NOT_PURCHASED'
  | 'UPCOMING'
  | 'VALID'
  | 'EXPIRING'
  | 'EXPIRED';
export interface ComplianceKindSummary {
  kind: ComplianceKind;
  status: ComplianceStatus;
  required: boolean;
  current: InspectionItem | null;
  next: InspectionItem | null;
  daysLeft: number | null;
  overlap: boolean;
  missingFile: boolean;
}
export interface ComplianceSummary {
  tnds: ComplianceKindSummary;
  vcx: ComplianceKindSummary;
  registration: ComplianceKindSummary;
  unclassifiedInsurance: number;
}
export interface ComplianceRecord extends InspectionItem {
  files: FileUploadItem[];
}
export interface ComplianceResponse {
  subject: 'vehicle' | 'trailer';
  subjectId: string;
  vcxDecision: VcxDecision;
  summary: ComplianceSummary;
  records: ComplianceRecord[];
}
export interface FuelLogItem {
  id: string;
  vehicleId: string;
  driverId?: string;
  date: string;
  fuelType: string;
  quantity: number;
  unitPrice: number;
  totalCost: number;
  odometer: number;
  receiptNo?: string;
  note?: string;
  driver?: { id: string; code: string; fullName: string };
  supplier?: { id: string; code: string; name: string };
}
export interface VehicleDocItem {
  id: string;
  vehicleId: string;
  name: string;
  docType: string;
  fileUrl?: string;
  expiryDate?: string;
  note?: string;
}
export interface VehicleDetail extends VehicleItem {
  maintenances: MaintenanceItem[];
  inspections: InspectionItem[];
  fuelLogs: FuelLogItem[];
  documents: VehicleDocItem[];
}
export interface VehicleListResponse {
  data: VehicleItem[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}
export interface VehicleStats {
  total: number;
  active: number;
  maintenance: number;
  inactive: number;
  regExpiringSoon: number;
  insExpiringSoon: number;
}
export interface VehicleAlerts {
  regExpiring: VehicleItem[];
  insExpiring: VehicleItem[];
}

export const getVehiclesApi = (p?: Record<string, string>) =>
  apiFetch<VehicleListResponse>(
    `/api/v1/vehicles${p ? "?" + new URLSearchParams(p) : ""}`,
  );
export const getVehicleApi = (id: string) =>
  apiFetch<VehicleItem>(`/api/v1/vehicles/${id}`);
export const getVehicleDetailApi = (id: string) =>
  apiFetch<VehicleDetail>(`/api/v1/vehicles/${id}/detail`);
export const getVehicleStatsApi = () =>
  apiFetch<VehicleStats>("/api/v1/vehicles/stats");
export const getVehicleAlertsApi = () =>
  apiFetch<VehicleAlerts>("/api/v1/vehicles/alerts");
export const createVehicleApi = (d: Partial<VehicleItem>) =>
  apiFetch<VehicleItem>("/api/v1/vehicles", {
    method: "POST",
    body: JSON.stringify(d),
  });
export const updateVehicleApi = (id: string, d: Partial<VehicleItem>) =>
  apiFetch<VehicleItem>(`/api/v1/vehicles/${id}`, {
    method: "PUT",
    body: JSON.stringify(d),
  });
export const deleteVehicleApi = (id: string) =>
  apiFetch<{ message: string }>(`/api/v1/vehicles/${id}`, { method: "DELETE" });

// Vehicle Groups
export interface VehicleGroupItem {
  id: string;
  name: string;
  vietmapId?: number;
  description?: string;
  color?: string;
  sortOrder: number;
  _count?: { vehicles: number };
}
export const getVehicleGroupsApi = () =>
  apiFetch<VehicleGroupItem[]>("/api/v1/vehicles/groups");
export const syncVehiclesApi = () =>
  apiFetch<{ message: string; total: number; groups: number }>(
    "/api/v1/vietmap/sync-vehicles",
    { method: "POST" },
  );
export const syncOdoApi = () =>
  apiFetch<{
    message?: string;
    total: number;
    updated: number;
    failed: number;
    skipped: number;
    logId?: string;
  }>("/api/v1/maintenance/cron/sync-odo", { method: "POST" });

// ==================== VEHICLE ANALYTICS ====================

export interface VehicleTripItem {
  id: string;
  code: string;
  status: string;
  containerNo: string | null;
  startKm: number | null;
  endKm: number | null;
  distance: number | null;
  startAddress: string | null;
  endAddress: string | null;
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string;
  driver: { id: string; code: string; fullName: string } | null;
  dispatch: {
    code: string;
    order: {
      code: string;
      origin: string | null;
      destination: string | null;
    } | null;
  } | null;
  costs: {
    id: string;
    type: string;
    name: string;
    amount: number;
    receiptNo: string | null;
  }[];
  dispatchCode: string | null;
  orderCode: string | null;
  orderOrigin: string | null;
  orderDestination: string | null;
  totalCost: number;
}

export interface VehicleTripsResponse {
  data: VehicleTripItem[];
  meta: { total: number; page: number; limit: number; totalPages: number };
  /** Số chuyến theo trạng thái trong cùng kỳ (không tính bộ lọc trạng thái). */
  statusCounts?: Record<string, number>;
  summary: {
    totalTrips: number;
    totalDistance: number;
    avgDistancePerTrip: number;
    totalCost: number;
  };
}

export interface MonthlyRevenueItem {
  month: string;
  revenue: number;
  cost: number;
  profit: number;
  trips: number;
  distance: number;
}

export interface VehicleRevenueResponse {
  summary: {
    totalRevenue: number;
    totalCost: number;
    netProfit: number;
    profitMargin: number;
    fuelCost: number;
    tollCost: number;
    maintenanceCost: number;
    otherCost: number;
    totalTrips: number;
    totalDistance: number;
    revenuePerKm: number;
    costPerKm: number;
  };
  monthlyData: MonthlyRevenueItem[];
}

export interface FuelMonthlyTrend {
  month: string;
  avgLPer100Km: number;
  totalLiters: number;
  totalCost: number;
  distance: number;
}

export interface FuelLogEntry {
  id: string;
  date: string;
  fuelType: string;
  quantity: number;
  unitPrice: number;
  totalCost: number;
  odometer: number;
  segmentKm: number | null;
  segmentLPer100Km: number | null;
  driver: { id: string; code: string; fullName: string } | null;
  supplier: { id: string; code: string; name: string } | null;
  receiptNo: string | null;
}

export interface VehicleFuelAnalyticsResponse {
  metrics: {
    avgLPer100Km: number;
    totalLiters: number;
    totalFuelCost: number;
    costPerKm: number;
    totalDistance: number;
    fillCount: number;
  };
  monthlyTrend: FuelMonthlyTrend[];
  logs: FuelLogEntry[];
}

export interface OdoReading {
  date: string;
  odometer: number;
  source: "TRIP_START" | "TRIP_END" | "FUEL_LOG" | "ODO_SYNC";
  referenceId: string;
  referenceCode: string | null;
}

export interface VehicleOdoHistoryResponse {
  currentOdo: number;
  lastOdoUpdate: string | null;
  distanceThisMonth: number;
  avgDailyKm: number;
  readings: OdoReading[];
}

export interface MaintenanceProjectionItem {
  typeId: string;
  typeName: string;
  category: string;
  intervalKm: number | null;
  intervalDays: number | null;
  lastCompletedDate: string | null;
  lastCompletedKm: number | null;
  nextDueKm: number | null;
  nextDueDate: string | null;
  kmRemaining: number | null;
  daysRemaining: number | null;
  urgency: "NORMAL" | "WARNING" | "CRITICAL";
}

export interface MaintenanceProjectionsResponse {
  currentOdo: number;
  avgDailyKm: number;
  projections: MaintenanceProjectionItem[];
}

// Vehicle Analytics API functions
export const getVehicleTripsApi = (
  id: string,
  params?: Record<string, string>,
) =>
  apiFetch<VehicleTripsResponse>(
    `/api/v1/vehicles/${id}/trips${params ? "?" + new URLSearchParams(params) : ""}`,
  );

export const getVehicleRevenueApi = (
  id: string,
  params?: Record<string, string>,
) =>
  apiFetch<VehicleRevenueResponse>(
    `/api/v1/vehicles/${id}/revenue${params ? "?" + new URLSearchParams(params) : ""}`,
  );

export const getVehicleFuelAnalyticsApi = (
  id: string,
  params?: Record<string, string>,
) =>
  apiFetch<VehicleFuelAnalyticsResponse>(
    `/api/v1/vehicles/${id}/fuel-analytics${params ? "?" + new URLSearchParams(params) : ""}`,
  );

export const getVehicleOdoHistoryApi = (
  id: string,
  params?: Record<string, string>,
) =>
  apiFetch<VehicleOdoHistoryResponse>(
    `/api/v1/vehicles/${id}/odo-history${params ? "?" + new URLSearchParams(params) : ""}`,
  );

export const getVehicleMaintenanceProjectionsApi = (id: string) =>
  apiFetch<MaintenanceProjectionsResponse>(
    `/api/v1/vehicles/${id}/maintenance-projections`,
  );

export const getVehicleGpsStatusApi = (id: string) =>
  apiFetch<{ configured: boolean; status: any }>(
    `/api/v1/vehicles/${id}/gps-status`,
  );

// ─── Phân công tài xế theo xe ───
export interface VehicleDriverAssignment {
  id: string;
  vehicleId: string;
  driverId: string;
  role: "PRIMARY" | "SECONDARY";
  fromDate: string;
  toDate: string | null;
  note: string | null;
  branchId: string | null;
  createdAt: string;
  updatedAt: string;
  driver: {
    id: string;
    code: string;
    fullName: string;
    phone: string | null;
    licenseClass: string | null;
    status: string;
  };
  vehicle: { id: string; plateNumber: string; type: string | null };
}

export interface VehicleCurrentDrivers {
  primary: VehicleDriverAssignment | null;
  secondaries: VehicleDriverAssignment[];
}

/** Một dòng trong màn hình kiểm soát phân công — mỗi xe kèm cảnh báo. */
export interface VehicleAssignmentRow {
  vehicleId: string;
  plateNumber: string;
  type: string | null;
  status: string;
  branch: { id: string; name: string } | null;
  primary: VehicleDriverAssignment | null;
  secondaries: VehicleDriverAssignment[];
  issues: {
    /** Xe đang hoạt động nhưng không ai phụ trách chính. */
    noPrimary: boolean;
    /** Tài xế đã nghỉ việc/nghỉ phép nhưng phân công chưa kết thúc. */
    inactiveDrivers: {
      assignmentId: string;
      driverId: string;
      fullName: string;
      status: string;
    }[];
    /** Tài xế của xe này đang phụ trách nhiều xe (nghiệp vụ cho phép, cần rà). */
    multiVehicleDrivers: {
      driverId: string;
      fullName: string;
      vehicleCount: number;
    }[];
  };
}

export interface VehicleAssignmentOverview {
  data: VehicleAssignmentRow[];
  /** Tài xế còn đi làm mà chưa được bố trí xe nào. */
  unassignedDrivers: {
    id: string;
    code: string;
    fullName: string;
    phone: string | null;
    status: string;
    branch: { id: string; name: string } | null;
  }[];
  summary: {
    totalVehicles: number;
    noPrimary: number;
    inactiveDriver: number;
    multiVehicle: number;
    unassignedDrivers: number;
  };
}

export const getVehicleAssignmentOverviewApi = (
  params?: Record<string, string>,
) =>
  apiFetch<VehicleAssignmentOverview>(
    `/api/v1/vehicles/driver-assignments/overview${params ? "?" + new URLSearchParams(params) : ""}`,
  );

export const getVehicleDriverAssignmentsApi = (
  id: string,
  params?: Record<string, string>,
) =>
  apiFetch<{ data: VehicleDriverAssignment[]; meta: { total: number } }>(
    `/api/v1/vehicles/${id}/driver-assignments${params ? "?" + new URLSearchParams(params) : ""}`,
  );

export const getVehicleCurrentDriversApi = (id: string) =>
  apiFetch<VehicleCurrentDrivers>(`/api/v1/vehicles/${id}/current-drivers`);

export const createVehicleDriverAssignmentApi = (
  id: string,
  body: {
    driverId: string;
    role?: "PRIMARY" | "SECONDARY";
    fromDate: string;
    toDate?: string;
    note?: string;
  },
) =>
  apiFetch<VehicleDriverAssignment>(
    `/api/v1/vehicles/${id}/driver-assignments`,
    {
      method: "POST",
      body: JSON.stringify(body),
    },
  );

export const updateVehicleDriverAssignmentApi = (
  assignmentId: string,
  body: {
    role?: "PRIMARY" | "SECONDARY";
    fromDate?: string;
    toDate?: string | null;
    note?: string;
  },
) =>
  apiFetch<VehicleDriverAssignment>(
    `/api/v1/vehicles/driver-assignments/${assignmentId}`,
    {
      method: "PUT",
      body: JSON.stringify(body),
    },
  );

export const endVehicleDriverAssignmentApi = (
  assignmentId: string,
  body: { toDate?: string } = {},
) =>
  apiFetch<VehicleDriverAssignment>(
    `/api/v1/vehicles/driver-assignments/${assignmentId}/end`,
    {
      method: "POST",
      body: JSON.stringify(body),
    },
  );

export const deleteVehicleDriverAssignmentApi = (assignmentId: string) =>
  apiFetch<{ message: string }>(
    `/api/v1/vehicles/driver-assignments/${assignmentId}`,
    { method: "DELETE" },
  );

export const getVehicleGpsHistoryApi = (id: string, date: string) =>
  apiFetch<{ configured: boolean; points: any[]; summary: any }>(
    `/api/v1/vehicles/${id}/gps-history?date=${date}`,
  );

// ==================== MAINTENANCE ====================
export interface MaintenanceStats {
  scheduled: number;
  overdue: number;
  inProgress: number;
  completed: number;
  total: number;
  /** Chi phí bảo trì tháng hiện tại / tháng trước — optional, backend có thể chưa trả. */
  costThisMonth?: number;
  costLastMonth?: number;
}
export interface MaintenanceTypeListResponse {
  data: MaintenanceTypeItem[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}
export const getMaintenanceTypesApi = (p?: Record<string, string>) =>
  apiFetch<MaintenanceTypeListResponse>(
    `/api/v1/maintenance/types${p ? "?" + new URLSearchParams(p) : ""}`,
  );
export const getMaintenanceTypeOptionsApi = () =>
  apiFetch<MaintenanceTypeItem[]>("/api/v1/maintenance/types/options");
export const createMaintenanceTypeApi = (d: Partial<MaintenanceTypeItem>) =>
  apiFetch<MaintenanceTypeItem>("/api/v1/maintenance/types", {
    method: "POST",
    body: JSON.stringify(d),
  });
export const updateMaintenanceTypeApi = (
  id: string,
  d: Partial<MaintenanceTypeItem>,
) =>
  apiFetch<MaintenanceTypeItem>(`/api/v1/maintenance/types/${id}`, {
    method: "PUT",
    body: JSON.stringify(d),
  });
export const deleteMaintenanceTypeApi = (id: string) =>
  apiFetch<{ message: string }>(`/api/v1/maintenance/types/${id}`, {
    method: "DELETE",
  });
// CRUD phiếu bảo trì đã chuyển sang hệ `/maintenance/tickets` (xem mục
// MAINTENANCE TICKETS bên dưới). Hai hàm còn lại chỉ phục vụ dashboard trang chủ.
export const getMaintenanceStatsApi = () =>
  apiFetch<MaintenanceStats>("/api/v1/maintenance/stats");

// ── Báo cáo chi phí bảo trì ──
export interface MaintenanceReportSummary {
  totalCost: number;
  internalCost: number;
  outsourcedCost: number;
  partsCost: number;
  laborCost: number;
  serviceCost: number;
  ticketCount: number;
  overBudgetCount: number;
}
export interface MaintenanceReportMonthlyItem {
  month: string;
  totalCost: number;
  internalCost: number;
  outsourcedCost: number;
  ticketCount: number;
}
export interface MaintenanceReportBranchItem {
  branchId: string;
  branchName: string;
  totalCost: number;
  ticketCount: number;
}
export interface MaintenanceReportKindItem {
  kind: "PART" | "LABOR" | "SERVICE" | "OTHER";
  amount: number;
}
export interface MaintenanceReportOverBudgetItem {
  id: string;
  code: string;
  plateNumber: string;
  quotedAmount: number;
  actualCost: number;
  overPercent: number;
  completedAt: string;
}
export interface MaintenanceReportResponse {
  summary: MaintenanceReportSummary;
  monthly: MaintenanceReportMonthlyItem[];
  byBranch: MaintenanceReportBranchItem[];
  byKind: MaintenanceReportKindItem[];
  overBudget: MaintenanceReportOverBudgetItem[];
}
export const getMaintenanceReportApi = (params?: {
  from?: string;
  to?: string;
  branchId?: string;
}) => {
  // Backend dùng ReportQueryDto chuẩn của khu reports: startDate/endDate.
  const qs: Record<string, string> = {};
  if (params?.from) qs.startDate = params.from;
  if (params?.to) qs.endDate = params.to;
  if (params?.branchId) qs.branchId = params.branchId;
  const query = new URLSearchParams(qs).toString();
  return apiFetch<MaintenanceReportResponse>(
    `/api/v1/reports/maintenance${query ? "?" + query : ""}`,
  );
};

// ==================== INSPECTIONS ====================
export interface InspectionListResponse {
  data: InspectionItem[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}
export const getInspectionsApi = (p?: Record<string, string>) =>
  apiFetch<InspectionListResponse>(
    `/api/v1/inspections${p ? "?" + new URLSearchParams(p) : ""}`,
  );
export const getInspectionApi = (id: string) =>
  apiFetch<InspectionItem>(`/api/v1/inspections/${id}`);
export const createInspectionApi = (d: any) =>
  apiFetch<InspectionItem>("/api/v1/inspections", {
    method: "POST",
    body: JSON.stringify(d),
  });
export const updateInspectionApi = (id: string, d: any) =>
  apiFetch<InspectionItem>(`/api/v1/inspections/${id}`, {
    method: "PUT",
    body: JSON.stringify(d),
  });
export const getComplianceApi = (subject: 'vehicle' | 'trailer', id: string) =>
  apiFetch<ComplianceResponse>(`/api/v1/inspections/compliance/${subject}/${id}`);
export const cancelInspectionApi = (id: string, reason: string) =>
  apiFetch<InspectionItem>(`/api/v1/inspections/${id}/cancel`, {
    method: "POST",
    body: JSON.stringify({ reason }),
  });
export const deleteInspectionApi = (id: string) =>
  apiFetch<{ message: string }>(`/api/v1/inspections/${id}`, {
    method: "DELETE",
  });
export const getInspectionAlertsApi = (days?: number) =>
  apiFetch<InspectionItem[]>(
    `/api/v1/inspections/alerts${days ? "?days=" + days : ""}`,
  );
export const getInspectionTimelineApi = (vehicleId: string) =>
  apiFetch<InspectionItem[]>(`/api/v1/inspections/timeline/${vehicleId}`);
export const checkExpiredInspectionsApi = () =>
  apiFetch<{ expired: number }>("/api/v1/inspections/check-expired", {
    method: "POST",
  });

// ==================== FUEL LOGS ====================
export interface FuelLogListResponse {
  data: FuelLogItem[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}
export interface FuelAnalytics {
  vehicles: {
    vehicle: { id: string; plateNumber: string };
    totalLiters: number;
    totalCost: number;
    distanceKm: number;
    avgConsumption: number | null;
    avgCostPerKm: number | null;
    fillCount: number;
  }[];
  summary: {
    totalLiters: number;
    totalCost: number;
    totalFills: number;
    vehicleCount: number;
  };
}
export const getFuelLogsApi = (p?: Record<string, string>) =>
  apiFetch<FuelLogListResponse>(
    `/api/v1/fuel-logs${p ? "?" + new URLSearchParams(p) : ""}`,
  );
export const getFuelLogApi = (id: string) =>
  apiFetch<FuelLogItem>(`/api/v1/fuel-logs/${id}`);
export const createFuelLogApi = (d: any) =>
  apiFetch<FuelLogItem>("/api/v1/fuel-logs", {
    method: "POST",
    body: JSON.stringify(d),
  });
export const updateFuelLogApi = (id: string, d: any) =>
  apiFetch<FuelLogItem>(`/api/v1/fuel-logs/${id}`, {
    method: "PUT",
    body: JSON.stringify(d),
  });
export const deleteFuelLogApi = (id: string) =>
  apiFetch<{ message: string }>(`/api/v1/fuel-logs/${id}`, {
    method: "DELETE",
  });
export const getFuelAnalyticsApi = (p?: Record<string, string>) =>
  apiFetch<FuelAnalytics>(
    `/api/v1/fuel-logs/analytics${p ? "?" + new URLSearchParams(p) : ""}`,
  );

// ==================== SMART MAINTENANCE ====================
export const getSmartScheduleApi = (vehicleId?: string) =>
  apiFetch<any[]>(
    `/api/v1/maintenance/smart-schedule${vehicleId ? "?vehicleId=" + vehicleId : ""}`,
  );

// ==================== MAINTENANCE TICKETS ====================
export type MaintenanceTicketPriority = "LOW" | "MEDIUM" | "HIGH" | "URGENT";
/**
 * Vòng đời phiếu bảo trì (mirror enum `MaintenanceStatus` trong schema.prisma).
 * INTERNAL: CREATED → ASSIGNED → IN_PROGRESS → COMPLETED
 * OUTSOURCED: CREATED → QUOTED → QUOTE_APPROVED → AT_GARAGE → IN_PROGRESS → COMPLETED
 * (phiếu thuê ngoài ≤ ngưỡng báo giá được đi tắt thẳng sang AT_GARAGE)
 *
 * `ACCEPTED` giữ trong union vì phiếu cũ có thể còn trạng thái này, nhưng
 * không còn transition nào dẫn tới nó (bỏ 18/08/2026).
 */
export type MaintenanceTicketStatus =
  | "CREATED"
  | "SCHEDULED"
  | "ASSIGNED"
  | "OVERDUE"
  | "QUOTED"
  | "QUOTE_APPROVED"
  | "AT_GARAGE"
  | "IN_PROGRESS"
  | "ACCEPTED"
  | "COMPLETED"
  | "CANCELLED";
/** Phân loại dòng chi phí trong phiếu sửa chữa */
export type MaintenanceItemKind = "PART" | "LABOR" | "SERVICE";
export type MaintenanceExecutionType = "INTERNAL" | "OUTSOURCED";

export interface MaintenanceTicket {
  id: string;
  ticketCode?: string;
  vehicleId: string | null;
  trailerId?: string | null;
  typeId?: string | null;
  status: MaintenanceTicketStatus;
  priority: MaintenanceTicketPriority;
  description?: string;
  estimatedCost?: number;
  actualCost?: number;
  scheduledDate?: string;
  scheduledKm?: number;
  startedAt?: string;
  completedAt?: string;
  completedDate?: string;
  completedKm?: number;
  assigneeId?: string;
  branchId?: string;
  supplierId?: string;
  /** Phương án thực hiện: tự sửa nội bộ / thuê gara ngoài */
  executionType: MaintenanceExecutionType;
  garageId?: string | null;
  supervisorId?: string | null;
  invoiceNo?: string | null;
  vatRate?: number | null;
  /** Báo giá gara */
  quotedAt?: string | null;
  quotedById?: string | null;
  quotedAmount?: number | null;
  quoteNote?: string | null;
  quoteApprovedAt?: string | null;
  quoteApprovedById?: string | null;
  /** Thời gian dự kiến hoàn tất — nhập lúc bắt đầu sửa */
  expectedDays?: number | null;
  expectedCompletionAt?: string | null;
  /** Biên bản giao / nhận xe */
  deliveredToGarageAt?: string | null;
  deliveredOdo?: number | null;
  receivedFromGarageAt?: string | null;
  receivedOdo?: number | null;
  /** Nghiệm thu & bảo hành */
  acceptedAt?: string | null;
  acceptedById?: string | null;
  acceptanceNote?: string | null;
  warrantyUntil?: string | null;
  warrantyKm?: number | null;
  note?: string;
  createdBy?: string;
  createdAt: string;
  updatedAt: string;
  /** Ticket sinh từ đề xuất tài xế: category thay cho type, kèm ảnh + nguồn */
  category?: string | null;
  images?: string[] | null;
  source?: string | null;
  reportedById?: string | null;
  tripId?: string | null;
  vehicle: {
    id: string;
    plateNumber: string;
    brand?: string;
    model?: string;
    type?: string;
    odometer: number;
    status?: string;
  } | null;
  /** Rơ moóc — phiếu gắn đúng một trong hai: vehicle hoặc trailer */
  trailer?: {
    id: string;
    plateNumber: string;
    type?: string;
    size?: string;
    status?: string;
  } | null;
  /** Trạng thái đồng bộ trừ kho khi phiếu hoàn thành có dùng vật tư từ kho — optional, backend có thể chưa trả */
  stockSync?: { required: boolean; synced: boolean };
  type?: { id: string; name: string; category: string } | null;
  assignee?: { id: string; fullName: string; email?: string; phone?: string };
  supervisor?: {
    id: string;
    fullName: string;
    email?: string;
    phone?: string;
  } | null;
  branch?: { id: string; name: string; code: string };
  supplier?: { id: string; code: string; name: string };
  garage?: { id: string; code: string; name: string; phone?: string } | null;
}

export interface MaintenanceTicketStats {
  created: number;
  assigned: number;
  inProgress: number;
  completed: number;
  overdue: number;
  cancelled: number;
  total: number;
  completedThisWeek: number;
  /** Phiếu đang mở (chưa hoàn thành/hủy) theo phương án thực hiện */
  outsourcedOpen: number;
  internalOpen: number;
}

export interface TicketHistoryEntry {
  id: string;
  maintenanceId: string;
  fromStatus: string;
  toStatus: string;
  actorId?: string;
  actorName?: string;
  note?: string;
  createdAt: string;
}

export interface AvailableTechnician {
  id: string;
  fullName: string;
  email?: string;
  phone?: string;
  activeTickets: number;
  overloaded: boolean;
  userBranches: { branch: { id: string; name: string; code: string } }[];
}

export interface MaintenanceTicketListResponse {
  data: MaintenanceTicket[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}

export const getMaintenanceTicketsApi = (params?: Record<string, string>) =>
  apiFetch<MaintenanceTicketListResponse>(
    `/api/v1/maintenance/tickets${params ? "?" + new URLSearchParams(params) : ""}`,
  );
export const getMaintenanceTicketApi = (id: string) =>
  apiFetch<MaintenanceTicket>(`/api/v1/maintenance/tickets/${id}`);
export const createMaintenanceTicketApi = (data: any) =>
  apiFetch<MaintenanceTicket>("/api/v1/maintenance/tickets", {
    method: "POST",
    body: JSON.stringify(data),
  });
export const updateMaintenanceTicketApi = (id: string, data: any) =>
  apiFetch<MaintenanceTicket>(`/api/v1/maintenance/tickets/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
export const deleteMaintenanceTicketApi = (id: string) =>
  apiFetch<{ message: string }>(`/api/v1/maintenance/tickets/${id}`, {
    method: "DELETE",
  });
export const assignMaintenanceTicketApi = (id: string, assigneeId: string) =>
  apiFetch<{ ticket: MaintenanceTicket; warning?: string }>(
    `/api/v1/maintenance/tickets/${id}/assign`,
    { method: "POST", body: JSON.stringify({ assigneeId }) },
  );
export const transitionMaintenanceTicketApi = (
  id: string,
  data: {
    targetStatus: string;
    actualCost?: number;
    completedKm?: number;
    invoiceNo?: string;
    vatRate?: number;
    note?: string;
    expectedDays?: number;
    receivedOdo?: number;
    acceptanceNote?: string;
    warrantyUntil?: string;
    warrantyKm?: number;
  },
) =>
  apiFetch<MaintenanceTicket>(`/api/v1/maintenance/tickets/${id}/transition`, {
    method: "POST",
    body: JSON.stringify(data),
  });
export const assignMaintenanceSupervisorApi = (
  id: string,
  supervisorId: string,
) =>
  apiFetch<{ ticket: MaintenanceTicket }>(
    `/api/v1/maintenance/tickets/${id}/supervisor`,
    { method: "POST", body: JSON.stringify({ supervisorId }) },
  );
export const getMaintenanceTicketHistoryApi = (id: string) =>
  apiFetch<TicketHistoryEntry[]>(`/api/v1/maintenance/tickets/${id}/history`);

// ─── Hạng mục sửa chữa ───
export interface MaintenanceWorkItem {
  id: string;
  maintenanceId: string;
  seq: number;
  name: string;
  kind: MaintenanceItemKind;
  partNo?: string | null;
  unit?: string | null;
  quantity: number;
  unitPrice: number;
  amount: number;
  note?: string | null;
  /** Vật tư lấy từ danh mục kho — có giá trị thì dòng này tự xuất kho khi phiếu hoàn thành */
  partId?: string | null;
  /** Kho xuất vật tư, đi kèm partId */
  warehouseId?: string | null;
  createdAt: string;
  updatedAt: string;
}
/** Payload một dòng hạng mục khi gửi lên — `amount` do server tính. */
export interface MaintenanceWorkItemInput {
  name: string;
  kind?: MaintenanceItemKind;
  partNo?: string;
  unit?: string;
  quantity: number;
  unitPrice: number;
  note?: string;
  partId?: string;
  warehouseId?: string;
}
export const getMaintenanceItemsApi = (id: string) =>
  apiFetch<MaintenanceWorkItem[]>(`/api/v1/maintenance/tickets/${id}/items`);
export const replaceMaintenanceItemsApi = (
  id: string,
  items: MaintenanceWorkItemInput[],
) =>
  apiFetch<{ items: MaintenanceWorkItem[]; total: number }>(
    `/api/v1/maintenance/tickets/${id}/items`,
    { method: "PUT", body: JSON.stringify({ items }) },
  );

// ─── Báo giá gara (phiếu thuê ngoài) ───
export const submitMaintenanceQuoteApi = (
  id: string,
  data: { items: MaintenanceWorkItemInput[]; quoteNote?: string },
) =>
  apiFetch<{ ticket: MaintenanceTicket }>(
    `/api/v1/maintenance/tickets/${id}/quote`,
    { method: "POST", body: JSON.stringify(data) },
  );
export const approveMaintenanceQuoteApi = (id: string, note?: string) =>
  apiFetch<{ ticket: MaintenanceTicket }>(
    `/api/v1/maintenance/tickets/${id}/quote/approve`,
    { method: "POST", body: JSON.stringify({ note }) },
  );
export const rejectMaintenanceQuoteApi = (id: string, reason: string) =>
  apiFetch<{ ticket: MaintenanceTicket }>(
    `/api/v1/maintenance/tickets/${id}/quote/reject`,
    { method: "POST", body: JSON.stringify({ reason }) },
  );

// ─── Giao xe cho gara / Nghiệm thu ───
export const deliverMaintenanceToGarageApi = (
  id: string,
  data: { deliveredOdo?: number; note?: string },
) =>
  apiFetch<{ ticket: MaintenanceTicket }>(
    `/api/v1/maintenance/tickets/${id}/deliver-garage`,
    { method: "POST", body: JSON.stringify(data) },
  );
// Nghiệm thu đã gộp vào bước hoàn thành (18/08/2026): gửi kèm `receivedOdo`,
// `acceptanceNote`, `warrantyUntil`, `warrantyKm` trong transitionMaintenanceTicketApi.
/** Ngưỡng nghiệp vụ đọc từ Setting — đừng hardcode ở UI. */
export interface MaintenanceThresholds {
  /** Trên ngưỡng này, phiếu thuê ngoài phải qua bước báo giá */
  quoteThreshold: number;
  /** Trên ngưỡng này, duyệt báo giá cần Giám đốc */
  approvalThreshold: number;
}
export const getMaintenanceThresholdsApi = () =>
  apiFetch<MaintenanceThresholds>("/api/v1/maintenance/tickets/thresholds");
export const getMaintenanceTicketStatsApi = () =>
  apiFetch<MaintenanceTicketStats>("/api/v1/maintenance/tickets/stats");
export const getAvailableTechniciansApi = (branchId?: string) =>
  apiFetch<AvailableTechnician[]>(
    `/api/v1/maintenance/technicians${branchId ? "?branchId=" + branchId : ""}`,
  );
export interface AvailableSupervisor {
  id: string;
  fullName: string;
  email?: string;
  phone?: string;
}
export const getAvailableSupervisorsApi = () =>
  apiFetch<AvailableSupervisor[]>("/api/v1/maintenance/supervisors");
export const triggerOdoSyncApi = () =>
  apiFetch<{
    total: number;
    updated: number;
    failed: number;
    skipped: number;
    logId?: string;
  }>("/api/v1/maintenance/cron/sync-odo", { method: "POST" });
export const triggerOverdueCheckApi = () =>
  apiFetch<{ overdueByDate: number; overdueByKm: number; alertsSent: number }>(
    "/api/v1/maintenance/cron/check-overdue",
    { method: "POST" },
  );

// ─── Sync Logs ───
export interface OdoSyncLogItem {
  id: string;
  trigger: "MANUAL" | "CRON";
  triggeredBy?: string;
  totalVehicles: number;
  updatedCount: number;
  failedCount: number;
  skippedCount: number;
  duration: number;
  startedAt: string;
  completedAt?: string;
  note?: string;
}
export interface OdoSyncDetailItem {
  id: string;
  syncLogId: string;
  vehicleId: string;
  plateNumber: string;
  previousOdo: number;
  newOdo?: number;
  kmAdded?: number;
  syncedFrom?: string;
  syncedTo?: string;
  status: "SUCCESS" | "FAILED" | "SKIPPED";
  errorMessage?: string;
  createdAt: string;
}
export const getOdoSyncLogsApi = (params?: Record<string, string>) =>
  apiFetch<{
    data: OdoSyncLogItem[];
    meta: { total: number; page: number; limit: number; totalPages: number };
  }>(`/api/v1/odo-sync-logs${params ? "?" + new URLSearchParams(params) : ""}`);
export const getOdoSyncLogDetailsApi = (logId: string, status?: string) =>
  apiFetch<OdoSyncDetailItem[]>(
    `/api/v1/odo-sync-logs/${logId}/details${status ? "?status=" + status : ""}`,
  );

// ==================== TRAILERS ====================
export interface TrailerItem {
  id: string;
  plateNumber: string;
  /** Mã quản lý MO-0001 (có thể chưa có). */
  code?: string | null;
  fleetStatus?: FleetStatus;
  type?: string;
  size?: string;
  /** Tải trọng — dữ liệu cũ (tấn), giữ để đối chiếu. */
  payload?: number;
  registrationExp?: string;
  vin?: string | null;
  curbWeightKg?: number | null;
  overallLengthMm?: number | null;
  overallWidthMm?: number | null;
  overallHeightMm?: number | null;
  /** KL hàng chuyên chở theo thiết kế (TK) / cho phép tham gia giao thông (CP), kg. */
  cargoDesignKg?: number | null;
  cargoPermittedKg?: number | null;
  vcxDecision?: VcxDecision;
  compliance?: ComplianceSummary;
  status: string;
  note?: string;
  createdAt: string;
  branchId?: string | null;
  branch?: { id: string; name: string; code: string };
}
export interface TrailerListResponse {
  data: TrailerItem[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}
export const getTrailersApi = (p?: Record<string, string>) =>
  apiFetch<TrailerListResponse>(
    `/api/v1/trailers${p ? "?" + new URLSearchParams(p) : ""}`,
  );
export const getTrailerApi = (id: string) =>
  apiFetch<TrailerItem>(`/api/v1/trailers/${id}`);
export const createTrailerApi = (d: Partial<TrailerItem>) =>
  apiFetch<TrailerItem>("/api/v1/trailers", {
    method: "POST",
    body: JSON.stringify(d),
  });
export const updateTrailerApi = (id: string, d: Partial<TrailerItem>) =>
  apiFetch<TrailerItem>(`/api/v1/trailers/${id}`, {
    method: "PUT",
    body: JSON.stringify(d),
  });
export const deleteTrailerApi = (id: string) =>
  apiFetch<{ message: string }>(`/api/v1/trailers/${id}`, { method: "DELETE" });

// ==================== DRIVERS ====================
export interface DriverItem {
  id: string;
  code: string;
  fullName: string;
  phone?: string;
  idNumber?: string;
  licenseNumber?: string;
  licenseClass?: string;
  licenseExp?: string;
  address?: string;
  bankName?: string;
  bankAccount?: string;
  bankAccountName?: string;
  status: string;
  note?: string;
  createdAt: string;
  branchId?: string | null;
  branch?: { id: string; name: string; code: string };
}
export interface DriverListResponse {
  data: DriverItem[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}
export const getDriversApi = (p?: Record<string, string>) =>
  apiFetch<DriverListResponse>(
    `/api/v1/drivers${p ? "?" + new URLSearchParams(p) : ""}`,
  );
export const getDriverApi = (id: string) =>
  apiFetch<DriverItem>(`/api/v1/drivers/${id}`);
export const createDriverApi = (d: Partial<DriverItem>) =>
  apiFetch<DriverItem>("/api/v1/drivers", {
    method: "POST",
    body: JSON.stringify(d),
  });
export const updateDriverApi = (id: string, d: Partial<DriverItem>) =>
  apiFetch<DriverItem>(`/api/v1/drivers/${id}`, {
    method: "PUT",
    body: JSON.stringify(d),
  });
export const deleteDriverApi = (id: string) =>
  apiFetch<{ message: string }>(`/api/v1/drivers/${id}`, { method: "DELETE" });
export const getDriverNextCodeApi = () =>
  apiFetch<{ code: string }>("/api/v1/drivers/next-code");
// branchAssigned: số tài xế được gán chi nhánh lấy theo tài khoản liên kết.
export const syncDriversApi = () =>
  apiFetch<{ synced: number; branchAssigned?: number; message: string }>(
    "/api/v1/drivers/sync",
    { method: "POST" },
  );

// ==================== ROUTES ====================
export interface RouteItem {
  id: string;
  code: string;
  name: string;
  origin: string;
  destination: string;
  distance?: number;
  duration?: number;
  defaultRate?: number;
  tollFee?: number;
  note?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  branch?: { id: string; name: string; code: string };
}
export interface RouteListResponse {
  data: RouteItem[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}
export const getRoutesApi = (p?: Record<string, string>) =>
  apiFetch<RouteListResponse>(
    `/api/v1/routes${p ? "?" + new URLSearchParams(p) : ""}`,
  );
export const createRouteApi = (d: Partial<RouteItem>) =>
  apiFetch<RouteItem>("/api/v1/routes", {
    method: "POST",
    body: JSON.stringify(d),
  });
export const updateRouteApi = (id: string, d: Partial<RouteItem>) =>
  apiFetch<RouteItem>(`/api/v1/routes/${id}`, {
    method: "PUT",
    body: JSON.stringify(d),
  });
export const deleteRouteApi = (id: string) =>
  apiFetch<{ message: string }>(`/api/v1/routes/${id}`, { method: "DELETE" });
export const getRouteNextCodeApi = () =>
  apiFetch<{ code: string }>("/api/v1/routes/next-code");

// ==================== LOCATIONS ====================
export interface LocationItem {
  id: string;
  code: string;
  name: string;
  shortName?: string;
  displayName?: string | null;
  type: string;
  address?: string;
  lat?: number;
  lng?: number;
  contact?: string;
  phone?: string;
  note?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  branch?: { id: string; name: string; code: string };
}
export interface LocationListResponse {
  data: LocationItem[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}
export const getLocationsApi = (p?: Record<string, string>) =>
  apiFetch<LocationListResponse>(
    `/api/v1/locations${p ? "?" + new URLSearchParams(p) : ""}`,
  );
export const createLocationApi = (d: Partial<LocationItem>) =>
  apiFetch<LocationItem>("/api/v1/locations", {
    method: "POST",
    body: JSON.stringify(d),
  });
export const updateLocationApi = (id: string, d: Partial<LocationItem>) =>
  apiFetch<LocationItem>(`/api/v1/locations/${id}`, {
    method: "PUT",
    body: JSON.stringify(d),
  });
export const deleteLocationApi = (id: string) =>
  apiFetch<{ message: string }>(`/api/v1/locations/${id}`, {
    method: "DELETE",
  });
export const getLocationNextCodeApi = () =>
  apiFetch<{ code: string }>("/api/v1/locations/next-code");
/** Toàn bộ địa điểm đang dùng (không phân trang) — cho ô chọn địa điểm. */
export interface LocationOptionItem {
  id: string;
  code: string;
  name: string;
  shortName?: string | null;
  type?: string;
  address?: string | null;
}
export const getLocationOptionsApi = () =>
  apiFetch<LocationOptionItem[]>("/api/v1/locations/options");
/**
 * Kiểm trùng địa điểm trước khi tạo / sửa (25/09/2026). So với MỌI địa điểm,
 * kể cả chi nhánh khác và đã ẩn. `exact` = trùng tên (server sẽ chặn);
 * `similar` = gần giống (chỉ cảnh báo) kèm lý do.
 */
export type LocationDuplicateReason = "address" | "gps" | "shortName" | "name";
export interface LocationDuplicateCandidate {
  id: string;
  code: string;
  name: string;
  shortName?: string | null;
  address?: string | null;
  isActive: boolean;
  branch?: { name: string } | null;
}
export interface LocationDuplicates {
  exact: LocationDuplicateCandidate | null;
  similar: {
    location: LocationDuplicateCandidate;
    reasons: LocationDuplicateReason[];
    distanceM?: number;
  }[];
}
export const checkLocationDuplicateApi = (q: {
  name: string;
  shortName?: string;
  address?: string;
  lat?: number;
  lng?: number;
  excludeId?: string;
}) => {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(q))
    if (
      v !== undefined &&
      v !== "" &&
      !(typeof v === "number" && Number.isNaN(v))
    )
      p.set(k, String(v));
  return apiFetch<LocationDuplicates>(`/api/v1/locations/check-duplicate?${p}`);
};

// ==================== CATALOG: CARGO TYPES ====================
export interface CargoTypeItem {
  id: string;
  code: string;
  name: string;
  group: string;
  note?: string;
  isActive: boolean;
}
export interface CargoTypeListResponse {
  data: CargoTypeItem[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}
export const getCargoTypesApi = (p?: Record<string, string>) =>
  apiFetch<CargoTypeListResponse>(
    `/api/v1/cargo-types${p ? "?" + new URLSearchParams(p) : ""}`,
  );
export const createCargoTypeApi = (d: Partial<CargoTypeItem>) =>
  apiFetch<CargoTypeItem>("/api/v1/cargo-types", {
    method: "POST",
    body: JSON.stringify(d),
  });
export const updateCargoTypeApi = (id: string, d: Partial<CargoTypeItem>) =>
  apiFetch<CargoTypeItem>(`/api/v1/cargo-types/${id}`, {
    method: "PUT",
    body: JSON.stringify(d),
  });
export const deleteCargoTypeApi = (id: string) =>
  apiFetch<{ message: string }>(`/api/v1/cargo-types/${id}`, {
    method: "DELETE",
  });
export const getCargoTypeNextCodeApi = () =>
  apiFetch<{ code: string }>("/api/v1/cargo-types/next-code");

// ==================== CATALOG: COST TYPES ====================
export interface CostTypeItem {
  id: string;
  code: string;
  name: string;
  group: string;
  note?: string;
  isActive: boolean;
}
export interface CostTypeListResponse {
  data: CostTypeItem[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}
export const getCostTypesApi = (p?: Record<string, string>) =>
  apiFetch<CostTypeListResponse>(
    `/api/v1/cost-types${p ? "?" + new URLSearchParams(p) : ""}`,
  );
export const createCostTypeApi = (d: Partial<CostTypeItem>) =>
  apiFetch<CostTypeItem>("/api/v1/cost-types", {
    method: "POST",
    body: JSON.stringify(d),
  });
export const updateCostTypeApi = (id: string, d: Partial<CostTypeItem>) =>
  apiFetch<CostTypeItem>(`/api/v1/cost-types/${id}`, {
    method: "PUT",
    body: JSON.stringify(d),
  });
export const deleteCostTypeApi = (id: string) =>
  apiFetch<{ message: string }>(`/api/v1/cost-types/${id}`, {
    method: "DELETE",
  });
export const getCostTypeNextCodeApi = () =>
  apiFetch<{ code: string }>("/api/v1/cost-types/next-code");

// ==================== CATALOG: SURCHARGE TYPES ====================
export interface SurchargeTypeItem {
  id: string;
  code: string;
  name: string;
  unit: string;
  note?: string;
  isActive: boolean;
}
export interface SurchargeTypeListResponse {
  data: SurchargeTypeItem[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}
export const getSurchargeTypesApi = (p?: Record<string, string>) =>
  apiFetch<SurchargeTypeListResponse>(
    `/api/v1/surcharge-types${p ? "?" + new URLSearchParams(p) : ""}`,
  );
export const createSurchargeTypeApi = (d: Partial<SurchargeTypeItem>) =>
  apiFetch<SurchargeTypeItem>("/api/v1/surcharge-types", {
    method: "POST",
    body: JSON.stringify(d),
  });
export const updateSurchargeTypeApi = (
  id: string,
  d: Partial<SurchargeTypeItem>,
) =>
  apiFetch<SurchargeTypeItem>(`/api/v1/surcharge-types/${id}`, {
    method: "PUT",
    body: JSON.stringify(d),
  });
export const deleteSurchargeTypeApi = (id: string) =>
  apiFetch<{ message: string }>(`/api/v1/surcharge-types/${id}`, {
    method: "DELETE",
  });
export const getSurchargeTypeNextCodeApi = () =>
  apiFetch<{ code: string }>("/api/v1/surcharge-types/next-code");

// ==================== CATALOG: SHIPPING LINES ====================
export interface ShippingLineItem {
  id: string;
  code: string;
  name: string;
  country?: string;
  website?: string;
  contactName?: string;
  phone?: string;
  email?: string;
  note?: string;
  isActive: boolean;
}
export interface ShippingLineListResponse {
  data: ShippingLineItem[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}
export const getShippingLinesApi = (p?: Record<string, string>) =>
  apiFetch<ShippingLineListResponse>(
    `/api/v1/shipping-lines${p ? "?" + new URLSearchParams(p) : ""}`,
  );
export const createShippingLineApi = (d: Partial<ShippingLineItem>) =>
  apiFetch<ShippingLineItem>("/api/v1/shipping-lines", {
    method: "POST",
    body: JSON.stringify(d),
  });
export const updateShippingLineApi = (
  id: string,
  d: Partial<ShippingLineItem>,
) =>
  apiFetch<ShippingLineItem>(`/api/v1/shipping-lines/${id}`, {
    method: "PUT",
    body: JSON.stringify(d),
  });
export const deleteShippingLineApi = (id: string) =>
  apiFetch<{ message: string }>(`/api/v1/shipping-lines/${id}`, {
    method: "DELETE",
  });
export const getShippingLineNextCodeApi = () =>
  apiFetch<{ code: string }>("/api/v1/shipping-lines/next-code");

// ==================== CATALOG: CONTAINER TYPES ====================
export interface ContainerTypeItem {
  id: string;
  code: string;
  name: string;
  size?: string;
  sizeType?: string;
  maxWeight?: number;
  tare?: number;
  note?: string;
  isActive: boolean;
}
export interface ContainerTypeListResponse {
  data: ContainerTypeItem[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}
export const getContainerTypesApi = (p?: Record<string, string>) =>
  apiFetch<ContainerTypeListResponse>(
    `/api/v1/container-types${p ? "?" + new URLSearchParams(p) : ""}`,
  );
export const createContainerTypeApi = (d: Partial<ContainerTypeItem>) =>
  apiFetch<ContainerTypeItem>("/api/v1/container-types", {
    method: "POST",
    body: JSON.stringify(d),
  });
export const updateContainerTypeApi = (
  id: string,
  d: Partial<ContainerTypeItem>,
) =>
  apiFetch<ContainerTypeItem>(`/api/v1/container-types/${id}`, {
    method: "PUT",
    body: JSON.stringify(d),
  });
export const deleteContainerTypeApi = (id: string) =>
  apiFetch<{ message: string }>(`/api/v1/container-types/${id}`, {
    method: "DELETE",
  });
export const getContainerTypeNextCodeApi = () =>
  apiFetch<{ code: string }>("/api/v1/container-types/next-code");

// ==================== CATALOG: VEHICLE TYPES (GĐ4a) ====================
/** Loại xe có tải trọng — nguồn "giá xe" cho cước hàng rời. */
export interface VehicleTypeItem {
  id: string;
  code: string;
  name: string;
  /** Tải trọng chở hàng (tấn) */
  payloadTons: number;
  /** TRACTOR_TRAILER | LOWBED | TRUCK | OTHER (chuỗi tự do) */
  kind?: string | null;
  note?: string | null;
  isActive: boolean;
  sortOrder: number;
  createdAt?: string;
  updatedAt?: string;
}
export interface VehicleTypeOption {
  id: string;
  code: string;
  name: string;
  payloadTons: number;
  kind?: string | null;
}
export interface VehicleTypeListResponse {
  data: VehicleTypeItem[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}
export interface VehicleTypePayload {
  code: string;
  name: string;
  payloadTons: number;
  kind?: string | null;
  note?: string | null;
  sortOrder?: number;
  isActive?: boolean;
}
export const getVehicleTypesApi = (p?: Record<string, string>) =>
  apiFetch<VehicleTypeListResponse>(
    `/api/v1/vehicle-types${p ? "?" + new URLSearchParams(p) : ""}`,
  );
/** Chỉ loại đang dùng, xếp theo sortOrder — cho ô chọn. */
export const getVehicleTypeOptionsApi = () =>
  apiFetch<VehicleTypeOption[]>("/api/v1/vehicle-types/options");
export const createVehicleTypeApi = (d: VehicleTypePayload) =>
  apiFetch<VehicleTypeItem>("/api/v1/vehicle-types", {
    method: "POST",
    body: JSON.stringify(d),
  });
export const updateVehicleTypeApi = (
  id: string,
  d: Partial<VehicleTypePayload>,
) =>
  apiFetch<VehicleTypeItem>(`/api/v1/vehicle-types/${id}`, {
    method: "PUT",
    body: JSON.stringify(d),
  });
export const deleteVehicleTypeApi = (id: string) =>
  apiFetch<{ message: string }>(`/api/v1/vehicle-types/${id}`, {
    method: "DELETE",
  });
export const getVehicleTypeNextCodeApi = () =>
  apiFetch<{ code: string }>("/api/v1/vehicle-types/next-code");

// ==================== CATALOG: PACKING UNITS (quy cách kiện, 22/10/2026) ====================
export interface PackingUnitItem {
  id: string;
  name: string;
  isActive: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}
export interface PackingUnitOption {
  id: string;
  name: string;
}
export interface PackingUnitListResponse {
  data: PackingUnitItem[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}
export const getPackingUnitsApi = (p?: Record<string, string>) =>
  apiFetch<PackingUnitListResponse>(
    `/api/v1/packing-units${p ? "?" + new URLSearchParams(p) : ""}`,
  );
export const getPackingUnitOptionsApi = () =>
  apiFetch<PackingUnitOption[]>("/api/v1/packing-units/options");
/** Thêm quy cách; trùng mục đang ẩn / đã xoá thì server bật lại mục đó, trùng mục đang dùng → 409. */
export const createPackingUnitApi = (d: { name: string; sortOrder?: number }) =>
  apiFetch<PackingUnitItem>("/api/v1/packing-units", {
    method: "POST",
    body: JSON.stringify(d),
  });
export const updatePackingUnitApi = (
  id: string,
  d: Partial<{ name: string; sortOrder: number; isActive: boolean }>,
) =>
  apiFetch<PackingUnitItem>(`/api/v1/packing-units/${id}`, {
    method: "PUT",
    body: JSON.stringify(d),
  });
export const deletePackingUnitApi = (id: string) =>
  apiFetch<{ message: string }>(`/api/v1/packing-units/${id}`, {
    method: "DELETE",
  });

// ==================== CATALOG: DISBURSEMENT TYPES (loại chi hộ, 26/10/2026) ====================
/** Nhóm: PORT · CARRIER · CUSTOMS · WAREHOUSE · ROAD — tên nhóm ở `DISBURSEMENT_GROUPS` (@vgt/shared). */
export interface DisbursementTypeItem {
  id: string;
  code: string;
  name: string;
  group: string;
  isActive: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}
export interface DisbursementTypeOption {
  id: string;
  code: string;
  name: string;
  group: string;
}
export interface DisbursementTypeListResponse {
  data: DisbursementTypeItem[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}
export const getDisbursementTypesApi = (p?: Record<string, string>) =>
  apiFetch<DisbursementTypeListResponse>(
    `/api/v1/disbursement-types${p ? "?" + new URLSearchParams(p) : ""}`,
  );
/** Chỉ mục đang dùng — cho ô Loại chi hộ của phiếu. */
export const getDisbursementTypeOptionsApi = () =>
  apiFetch<DisbursementTypeOption[]>("/api/v1/disbursement-types/options");
/**
 * Thêm loại chi hộ. `code` bỏ trống → server tự sinh từ tên (PHI_CANG_THC). Trùng mục đang ẩn / đã xoá thì server
 * bật lại mục đó (giữ mã cũ), trùng mục đang dùng → 409.
 */
export const createDisbursementTypeApi = (d: {
  name: string;
  code?: string;
  group?: string;
  sortOrder?: number;
}) =>
  apiFetch<DisbursementTypeItem>("/api/v1/disbursement-types", {
    method: "POST",
    body: JSON.stringify(d),
  });
/** Không đổi được `code` — dòng chi hộ và bảng dịch Debit Note bám theo mã. */
export const updateDisbursementTypeApi = (
  id: string,
  d: Partial<{ name: string; group: string; sortOrder: number; isActive: boolean }>,
) =>
  apiFetch<DisbursementTypeItem>(`/api/v1/disbursement-types/${id}`, {
    method: "PUT",
    body: JSON.stringify(d),
  });
export const deleteDisbursementTypeApi = (id: string) =>
  apiFetch<{ message: string }>(`/api/v1/disbursement-types/${id}`, {
    method: "DELETE",
  });

// ==================== CATALOG: PORTS ====================
export interface PortItem {
  id: string;
  code: string;
  name: string;
  type: string;
  address?: string;
  city?: string;
  province?: string;
  lat?: number;
  lng?: number;
  contactName?: string;
  phone?: string;
  email?: string;
  note?: string;
  isActive: boolean;
}
export interface PortListResponse {
  data: PortItem[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}
export const getPortsApi = (p?: Record<string, string>) =>
  apiFetch<PortListResponse>(
    `/api/v1/ports${p ? "?" + new URLSearchParams(p) : ""}`,
  );
export const createPortApi = (d: Partial<PortItem>) =>
  apiFetch<PortItem>("/api/v1/ports", {
    method: "POST",
    body: JSON.stringify(d),
  });
export const updatePortApi = (id: string, d: Partial<PortItem>) =>
  apiFetch<PortItem>(`/api/v1/ports/${id}`, {
    method: "PUT",
    body: JSON.stringify(d),
  });
export const deletePortApi = (id: string) =>
  apiFetch<{ message: string }>(`/api/v1/ports/${id}`, { method: "DELETE" });
export const getPortNextCodeApi = () =>
  apiFetch<{ code: string }>("/api/v1/ports/next-code");

// ==================== CATALOG: SUPPLIERS ====================
export interface SupplierItem {
  id: string;
  code: string;
  name: string;
  category: string;
  taxCode?: string;
  phone?: string;
  email?: string;
  address?: string;
  contactName?: string;
  bankName?: string;
  bankAccount?: string;
  note?: string;
  isActive: boolean;
}
export interface SupplierListResponse {
  data: SupplierItem[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}
export const getSuppliersApi = (p?: Record<string, string>) =>
  apiFetch<SupplierListResponse>(
    `/api/v1/suppliers${p ? "?" + new URLSearchParams(p) : ""}`,
  );
export const createSupplierApi = (d: Partial<SupplierItem>) =>
  apiFetch<SupplierItem>("/api/v1/suppliers", {
    method: "POST",
    body: JSON.stringify(d),
  });
export const updateSupplierApi = (id: string, d: Partial<SupplierItem>) =>
  apiFetch<SupplierItem>(`/api/v1/suppliers/${id}`, {
    method: "PUT",
    body: JSON.stringify(d),
  });
/** MD-11 — dropdown NCC cho form hợp đồng (type=SUPPLIER). */
export const getSupplierOptionsApi = () =>
  apiFetch<{ id: string; code: string; name: string; category?: string }[]>(
    "/api/v1/suppliers/options",
  );
export const deleteSupplierApi = (id: string) =>
  apiFetch<{ message: string }>(`/api/v1/suppliers/${id}`, {
    method: "DELETE",
  });
export const getSupplierNextCodeApi = () =>
  apiFetch<{ code: string }>("/api/v1/suppliers/next-code");

// ==================== CATALOG: GARAGES (đối tác gara sửa xe) ====================
export interface GarageItem {
  id: string;
  code: string;
  name: string;
  taxCode?: string;
  phone?: string;
  email?: string;
  address?: string;
  contactPerson?: string;
  bankName?: string;
  bankAccount?: string;
  bankAccountName?: string;
  specialties?: string;
  note?: string;
  rating?: number;
  isActive: boolean;
}
export interface GarageListResponse {
  data: GarageItem[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}
export interface GarageOption {
  id: string;
  code: string;
  name: string;
  phone?: string;
  specialties?: string;
}
export const getGaragesApi = (p?: Record<string, string>) =>
  apiFetch<GarageListResponse>(
    `/api/v1/garages${p ? "?" + new URLSearchParams(p) : ""}`,
  );
export const getGarageOptionsApi = () =>
  apiFetch<GarageOption[]>("/api/v1/garages/options");
export const createGarageApi = (d: Partial<GarageItem>) =>
  apiFetch<GarageItem>("/api/v1/garages", {
    method: "POST",
    body: JSON.stringify(d),
  });
export const updateGarageApi = (id: string, d: Partial<GarageItem>) =>
  apiFetch<GarageItem>(`/api/v1/garages/${id}`, {
    method: "PUT",
    body: JSON.stringify(d),
  });
export const deleteGarageApi = (id: string) =>
  apiFetch<{ message: string }>(`/api/v1/garages/${id}`, { method: "DELETE" });
export interface GarageStats {
  total: number;
  active: number;
  inactive: number;
  /** Số gara đang giữ ít nhất một phiếu bảo trì chưa chốt */
  withOpenTickets: number;
}
export const getGarageStatsApi = () =>
  apiFetch<GarageStats>("/api/v1/garages/stats");
export const getGarageNextCodeApi = () =>
  apiFetch<{ code: string }>("/api/v1/garages/next-code");

// ==================== BÁO CÁO HIỆU SUẤT XE & TÀI XẾ ====================
// Xếp hạng theo SỐ CHUYẾN hoàn thành. Chỉ để xem, không dùng tính lương.

export interface VehiclePerformanceRow {
  vehicleId: string;
  plateNumber: string;
  status: string;
  branch?: { id: string; name: string } | null;
  tripCount: number;
  /** Chặng đã bàn giao (nối chuyến) — km tính, không đếm chuyến. */
  relayLegCount: number;
  totalKm: number;
  activeDays: number;
  utilizationRate: number;
  avgKmPerTrip: number;
  fuelCost: number;
  fuelLiters: number;
  partsCost: number;
  maintenanceCost: number;
  totalCost: number;
  maintenanceCount: number;
}
export interface VehiclePerformanceResponse {
  data: VehiclePerformanceRow[];
  meta: {
    from: string;
    to: string;
    periodDays: number;
    vehicleCount: number;
    activeVehicleCount: number;
    idleVehicleCount: number;
    totalTrips: number;
    totalRelayLegs: number;
    totalKm: number;
    avgTripsPerVehicle: number;
  };
}
export const getVehiclePerformanceApi = (qs?: string) =>
  apiFetch<VehiclePerformanceResponse>(
    `/api/v1/reports/vehicles/performance${qs ? "?" + qs : ""}`,
  );

export interface DriverPerformanceRow {
  driverId: string;
  code: string;
  fullName: string;
  phone?: string | null;
  status: string;
  branch?: { id: string; name: string } | null;
  tripCount: number;
  /** Chặng đã bàn giao (nối chuyến) — km tính, không đếm chuyến. */
  relayLegCount: number;
  totalKm: number;
  workDays: number;
  leaveDays: number;
  avgTripsPerWorkDay: number;
  avgKmPerTrip: number;
  incidentCount: number;
}
export interface DriverPerformanceResponse {
  data: DriverPerformanceRow[];
  meta: {
    from: string;
    to: string;
    periodDays: number;
    driverCount: number;
    activeDriverCount: number;
    idleDriverCount: number;
    totalRelayLegs: number;
    totalTrips: number;
    totalKm: number;
    avgTripsPerDriver: number;
    totalIncidents: number;
  };
}
export const getDriverPerformanceApi = (qs?: string) =>
  apiFetch<DriverPerformanceResponse>(
    `/api/v1/reports/drivers/performance${qs ? "?" + qs : ""}`,
  );

// ==================== KHO VẬT TƯ: PARTS (vật tư phụ tùng) ====================
export interface PartCategoryItem {
  id: string;
  code: string;
  name: string;
  note?: string;
  isActive: boolean;
  _count?: { parts: number };
}
export interface PartStockRow {
  id: string;
  quantity: number;
  avgCost: number;
  warehouse: {
    id: string;
    code: string;
    name: string;
    branchId: string;
    branch?: { id: string; code: string; name: string };
  };
}
export interface PartItem {
  id: string;
  code: string;
  name: string;
  unit: string;
  spec?: string;
  categoryId?: string;
  supplierId?: string;
  note?: string;
  isActive: boolean;
  minQuantity: number;
  /** Tổng tồn trên mọi kho — backend tính sẵn */
  totalStock?: number;
  isLowStock?: boolean;
  category?: { id: string; code: string; name: string };
  supplier?: { id: string; code: string; name: string };
  stocks?: PartStockRow[];
}
export interface PartListResponse {
  data: PartItem[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}
export const getPartsApi = (p?: Record<string, string>) =>
  apiFetch<PartListResponse>(
    `/api/v1/parts${p ? "?" + new URLSearchParams(p) : ""}`,
  );
export const getPartApi = (id: string) =>
  apiFetch<PartItem>(`/api/v1/parts/${id}`);
export const createPartApi = (d: Partial<PartItem>) =>
  apiFetch<PartItem>("/api/v1/parts", {
    method: "POST",
    body: JSON.stringify(d),
  });
export const updatePartApi = (id: string, d: Partial<PartItem>) =>
  apiFetch<PartItem>(`/api/v1/parts/${id}`, {
    method: "PUT",
    body: JSON.stringify(d),
  });
export const deletePartApi = (id: string) =>
  apiFetch<{ message: string }>(`/api/v1/parts/${id}`, { method: "DELETE" });

export const getPartCategoriesApi = (p?: Record<string, string>) =>
  apiFetch<{ data: PartCategoryItem[]; meta: { total: number } }>(
    `/api/v1/parts/categories${p ? "?" + new URLSearchParams(p) : ""}`,
  );
export const createPartCategoryApi = (d: Partial<PartCategoryItem>) =>
  apiFetch<PartCategoryItem>("/api/v1/parts/categories", {
    method: "POST",
    body: JSON.stringify(d),
  });
export const updatePartCategoryApi = (
  id: string,
  d: Partial<PartCategoryItem>,
) =>
  apiFetch<PartCategoryItem>(`/api/v1/parts/categories/${id}`, {
    method: "PUT",
    body: JSON.stringify(d),
  });
export const deletePartCategoryApi = (id: string) =>
  apiFetch<{ message: string }>(`/api/v1/parts/categories/${id}`, {
    method: "DELETE",
  });

// ==================== KHO VẬT TƯ: WAREHOUSES (kho) ====================
export interface WarehouseItem {
  id: string;
  code: string;
  name: string;
  branchId: string;
  address?: string;
  keeperId?: string;
  note?: string;
  isActive: boolean;
  branch?: { id: string; code: string; name: string };
  keeper?: { id: string; fullName: string; email: string };
  _count?: { stocks: number };
}
export interface WarehouseListResponse {
  data: WarehouseItem[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}
export interface WarehouseStockRow {
  id: string;
  quantity: number;
  avgCost: number;
  value: number;
  updatedAt: string;
  isLowStock: boolean;
  part: {
    id: string;
    code: string;
    name: string;
    unit: string;
    spec?: string;
    minQuantity: number;
    category?: { id: string; name: string };
  };
}
export interface WarehouseStockResponse {
  data: WarehouseStockRow[];
  meta: { total: number; totalValue: number; lowStockCount: number };
}
export const getWarehousesApi = (p?: Record<string, string>) =>
  apiFetch<WarehouseListResponse>(
    `/api/v1/warehouses${p ? "?" + new URLSearchParams(p) : ""}`,
  );
export const getWarehouseApi = (id: string) =>
  apiFetch<WarehouseItem>(`/api/v1/warehouses/${id}`);
export const getWarehouseStockApi = (id: string, p?: Record<string, string>) =>
  apiFetch<WarehouseStockResponse>(
    `/api/v1/warehouses/${id}/stock${p ? "?" + new URLSearchParams(p) : ""}`,
  );
export const createWarehouseApi = (d: Partial<WarehouseItem>) =>
  apiFetch<WarehouseItem>("/api/v1/warehouses", {
    method: "POST",
    body: JSON.stringify(d),
  });
export const updateWarehouseApi = (id: string, d: Partial<WarehouseItem>) =>
  apiFetch<WarehouseItem>(`/api/v1/warehouses/${id}`, {
    method: "PUT",
    body: JSON.stringify(d),
  });
export const deleteWarehouseApi = (id: string) =>
  apiFetch<{ message: string }>(`/api/v1/warehouses/${id}`, {
    method: "DELETE",
  });

// ==================== KHO VẬT TƯ: PHIẾU KHO ====================
export type PartTxnType = "IN" | "OUT" | "TRANSFER" | "ADJUST";

export interface PartTxnLineItem {
  id: string;
  partId: string;
  quantity: number;
  unitCost: number;
  amount: number;
  vehicleId?: string;
  note?: string;
  part?: { id: string; code: string; name: string; unit: string };
  vehicle?: { id: string; plateNumber: string };
}
export interface PartTxnItem {
  id: string;
  code: string;
  type: PartTxnType;
  warehouseId: string;
  toWarehouseId?: string;
  txnDate: string;
  totalCost: number;
  supplierId?: string;
  maintenanceId?: string;
  note?: string;
  createdBy?: string;
  createdAt: string;
  warehouse?: { id: string; code: string; name: string; branchId: string };
  toWarehouse?: { id: string; code: string; name: string } | null;
  supplier?: { id: string; code: string; name: string };
  lines: PartTxnLineItem[];
}
export interface PartTxnListResponse {
  data: PartTxnItem[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}

/** Dòng gửi lên khi tạo phiếu. Phiếu xuất/chuyển không gửi unitCost — backend chốt giá vốn. */
export interface PartTxnLineInput {
  partId: string;
  quantity: number;
  unitCost?: number;
  vehicleId?: string;
  note?: string;
}

export const getPartTransactionsApi = (p?: Record<string, string>) =>
  apiFetch<PartTxnListResponse>(
    `/api/v1/warehouses/transactions${p ? "?" + new URLSearchParams(p) : ""}`,
  );
export const getPartTransactionApi = (id: string) =>
  apiFetch<PartTxnItem>(`/api/v1/warehouses/transactions/${id}`);
export const createInboundApi = (d: {
  warehouseId: string;
  supplierId?: string;
  txnDate?: string;
  note?: string;
  lines: PartTxnLineInput[];
}) =>
  apiFetch<PartTxnItem>("/api/v1/warehouses/transactions/inbound", {
    method: "POST",
    body: JSON.stringify(d),
  });
export const createOutboundApi = (d: {
  warehouseId: string;
  maintenanceId?: string;
  txnDate?: string;
  note?: string;
  lines: PartTxnLineInput[];
}) =>
  apiFetch<PartTxnItem>("/api/v1/warehouses/transactions/outbound", {
    method: "POST",
    body: JSON.stringify(d),
  });
export const createTransferApi = (d: {
  warehouseId: string;
  toWarehouseId: string;
  txnDate?: string;
  note?: string;
  lines: PartTxnLineInput[];
}) =>
  apiFetch<PartTxnItem>("/api/v1/warehouses/transactions/transfer", {
    method: "POST",
    body: JSON.stringify(d),
  });

// ── Thẻ kho ──
export interface StockCardRow {
  id: string;
  txnId: string;
  code: string;
  type: PartTxnType;
  txnDate: string;
  direction: "IN" | "OUT";
  warehouse?: { id: string; code: string; name: string };
  quantity: number;
  unitCost: number;
  amount: number;
  vehicle?: { id: string; plateNumber: string } | null;
  balance: number;
  note?: string;
}
export interface StockCardResponse {
  part: { id: string; code: string; name: string; unit: string; spec?: string };
  openingQty: number;
  rows: StockCardRow[];
  summary: {
    inQty: number;
    outQty: number;
    inValue: number;
    outValue: number;
    closingQty: number;
  };
}
export const getStockCardApi = (p: Record<string, string>) =>
  apiFetch<StockCardResponse>(
    `/api/v1/warehouses/stock-card?${new URLSearchParams(p)}`,
  );

// ── Báo cáo chi phí phụ tùng theo xe ──
export interface VehiclePartCostRow {
  vehicleId: string;
  plateNumber: string;
  totalAmount: number;
  totalTimes: number;
  parts: {
    partId: string;
    code: string;
    name: string;
    unit: string;
    category?: string | null;
    quantity: number;
    amount: number;
    times: number;
  }[];
}
export interface VehiclePartCostResponse {
  data: VehiclePartCostRow[];
  meta: { vehicleCount: number; grandTotal: number; avgPerVehicle: number };
}
export const getPartCostByVehicleApi = (p?: Record<string, string>) =>
  apiFetch<VehiclePartCostResponse>(
    `/api/v1/warehouses/reports/cost-by-vehicle${p ? "?" + new URLSearchParams(p) : ""}`,
  );

// ── Kiểm kê ──
export interface StocktakeSheetRow {
  partId: string;
  part: {
    id: string;
    code: string;
    name: string;
    unit: string;
    spec?: string;
    category?: { id: string; name: string } | null;
  };
  systemQuantity: number;
  avgCost: number;
}
export interface StocktakeSheetResponse {
  warehouse: { id: string; code: string; name: string };
  data: StocktakeSheetRow[];
  meta: { total: number };
}
export const getStocktakeSheetApi = (warehouseId: string) =>
  apiFetch<StocktakeSheetResponse>(
    `/api/v1/warehouses/stocktake/${warehouseId}`,
  );
export const createStocktakeApi = (d: {
  warehouseId: string;
  txnDate?: string;
  note?: string;
  lines: { partId: string; actualQuantity: number; note?: string }[];
}) =>
  apiFetch<{
    message: string;
    adjusted: number;
    transaction: PartTxnItem | null;
  }>("/api/v1/warehouses/transactions/stocktake", {
    method: "POST",
    body: JSON.stringify(d),
  });

// ── Tổng quan kho ──
export interface WarehouseOverviewResponse {
  byWarehouse: {
    id: string;
    code: string;
    name: string;
    value: number;
    partCount: number;
  }[];
  byCategory: { name: string; value: number; partCount: number }[];
  lowStock: {
    part: {
      id: string;
      code: string;
      name: string;
      unit: string;
      minQuantity: number;
      category?: { id: string; name: string } | null;
    };
    warehouse: { id: string; code: string; name: string };
    quantity: number;
    shortage: number;
  }[];
  slowMoving: {
    part: {
      id: string;
      code: string;
      name: string;
      unit: string;
      category?: { id: string; name: string } | null;
    };
    warehouse: { id: string; code: string; name: string };
    quantity: number;
    value: number;
    lastChangedAt: string;
  }[];
  meta: {
    totalValue: number;
    partCount: number;
    lowStockCount: number;
    slowMovingCount?: number;
    staleDays: number;
  };
}
export const getWarehouseOverviewApi = (p?: Record<string, string>) =>
  apiFetch<WarehouseOverviewResponse>(
    `/api/v1/warehouses/reports/overview${p ? "?" + new URLSearchParams(p) : ""}`,
  );

// ==================== CONTRACTS ====================
export interface ContractItem {
  id: string;
  code: string;
  title: string;
  type: string;
  status: string;
  customerId?: string;
  partnerId?: string;
  supplierId?: string;
  customer?: {
    id: string;
    code: string;
    name: string;
    displayName?: string | null;
  };
  partner?: {
    id: string;
    code: string;
    name: string;
    displayName?: string | null;
  };
  supplier?: {
    id: string;
    code: string;
    name: string;
    displayName?: string | null;
  };
  startDate?: string;
  endDate?: string;
  value?: number;
  currency: string;
  paymentTerms?: string;
  description?: string;
  note?: string;
  isActive: boolean;
}
export interface ContractListResponse {
  data: ContractItem[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}
export const getContractsApi = (p?: Record<string, string>) =>
  apiFetch<ContractListResponse>(
    `/api/v1/contracts${p ? "?" + new URLSearchParams(p) : ""}`,
  );
export const createContractApi = (d: Partial<ContractItem>) =>
  apiFetch<ContractItem>("/api/v1/contracts", {
    method: "POST",
    body: JSON.stringify(d),
  });
export const updateContractApi = (id: string, d: Partial<ContractItem>) =>
  apiFetch<ContractItem>(`/api/v1/contracts/${id}`, {
    method: "PUT",
    body: JSON.stringify(d),
  });
export const deleteContractApi = (id: string) =>
  apiFetch<{ message: string }>(`/api/v1/contracts/${id}`, {
    method: "DELETE",
  });
export const getContractNextCodeApi = () =>
  apiFetch<{ code: string }>("/api/v1/contracts/next-code");

// ==================== VIETMAP GPS ====================
export const getVietmapConfigApi = () =>
  apiFetch<{ configured: boolean }>("/api/v1/vietmap/config");
export const syncVietmapVehiclesApi = () =>
  apiFetch<{ message: string; total: number }>(
    "/api/v1/vietmap/sync-vehicles",
    { method: "POST" },
  );
export const getVietmapStatusApi = (plate: string) =>
  apiFetch<any>(`/api/v1/vietmap/status/${encodeURIComponent(plate)}`);
export const getVietmapAllStatusApi = () =>
  apiFetch<any[]>("/api/v1/vietmap/status-all");
export const getVietmapHistoryApi = (plate: string, from: string, to: string) =>
  apiFetch<any[]>(`/api/v1/vietmap/history/${encodeURIComponent(plate)}?from=${from}&to=${to}`);
export const getVietmapDailyApi = (plate: string, from: string, to: string) =>
  apiFetch<any[]>(`/api/v1/vietmap/daily/${encodeURIComponent(plate)}?from=${from}&to=${to}`);
export const getVietmapTripsApi = (plate: string, from: string, to: string) =>
  apiFetch<any[]>(`/api/v1/vietmap/trips/${encodeURIComponent(plate)}?from=${from}&to=${to}`);
export const getVietmapTollgatesApi = (
  plate: string,
  from: string,
  to: string,
) =>
  apiFetch<any[]>(`/api/v1/vietmap/tollgates/${encodeURIComponent(plate)}?from=${from}&to=${to}`);
export const getVietmapViolationsApi = (
  driverIds: string,
  from: string,
  to: string,
) =>
  apiFetch<any[]>(
    `/api/v1/vietmap/violations?driverIds=${driverIds}&from=${from}&to=${to}`,
  );
export const refreshVehiclePositionsApi = () =>
  apiFetch<{ updated: number; total: number }>(
    "/api/v1/vietmap/refresh-positions",
    { method: "POST" },
  );

// ─── Tổng quan đội xe: GPS gộp sẵn ngữ cảnh ERP (chuyến, tài xế, cảnh báo) ───
export type FleetAlertLevel = "danger" | "warning";
export interface FleetAlert {
  kind:
    | "registrationExpired"
    | "registrationSoon"
    | "insuranceExpired"
    | "insuranceSoon"
    | "maintenanceOpen"
    | "maintenanceOverdue"
    | "gpsStale"
    | "tripAlert";
  level: FleetAlertLevel;
  message: string;
}
export interface FleetTripInfo {
  id: string;
  code: string;
  status: string;
  /** Order.transportType — chuyến xuất: lấy rỗng / đóng hàng / hạ cảng. */
  transportType?: string | null;
  orderCode: string | null;
  containerNo: string | null;
  origin: string | null;
  destination: string | null;
  customerName: string | null;
  /** Tên rút gọn để hiển thị, backend join sẵn. */
  customerDisplayName?: string | null;
  progressPercent: number;
  etaAt: string | null;
  expectedArrival: string | null;
  startedAt: string | null;
  nextWaypoint: {
    label: string;
    kind: string;
    plannedAt: string | null;
  } | null;
}
export interface FleetOverviewVehicle {
  vehicleId: string | null;
  plateNumber: string;
  brand: string | null;
  vehicleType: string | null;
  branchId: string | null;
  branchName: string | null;
  groupId: string | null;
  groupName: string | null;
  vehicleStatus: string | null;
  odometer: number | null;
  vietmapId: number | null;
  lat: number | null;
  lng: number | null;
  speed: number;
  heading: number;
  gpsStatus: number;
  gpsTime: string | null;
  address: string | null;
  gpsStale: boolean;
  minutesSinceGps: number | null;
  driver: { id: string; fullName: string; phone: string | null } | null;
  trip: FleetTripInfo | null;
  trailerPlate: string | null;
  alerts: FleetAlert[];
  registrationExp: string | null;
  insuranceExp: string | null;
}
export interface FleetOverviewResponse {
  configured: boolean;
  fetchedAt: string;
  vehicles: FleetOverviewVehicle[];
  summary: {
    total: number;
    running: number;
    parked: number;
    offline: number;
    onTrip: number;
    idle: number;
    withAlerts: number;
    avgSpeed: number;
    totalDistanceToday: number;
  };
  branches: { id: string; name: string }[];
  groups: { id: string; name: string }[];
}
export const getFleetOverviewApi = () =>
  apiFetch<FleetOverviewResponse>("/api/v1/vietmap/fleet-overview");

// ==================== FILE UPLOADS ====================
export interface FileUploadItem {
  id: string;
  fileName: string;
  fileKey: string;
  fileSize: number;
  mimeType: string;
  url: string;
  category: string;
  createdAt: string;
  uploader?: { id: string; fullName: string };
}

/**
 * POST multipart có Bearer + chi nhánh, tự làm mới token khi 401.
 *
 * Trước 06/10/2026 hai hàm upload gọi `fetch` thô KHÔNG refresh: access token
 * sống 15 phút trong memory, không có hẹn giờ làm mới — người dùng mở hồ sơ,
 * mất hơn 15 phút tìm tệp rồi bấm tải là nhận "Unauthorized" dù mọi thao tác
 * khác vẫn chạy (apiFetch tự refresh). `FormData` gửi lại được nên retry an
 * toàn: lần đầu bị 401 tức máy chủ chưa ghi gì.
 */
async function postMultipart<T>(path: string, formData: FormData): Promise<T> {
  const send = (token: string | null) =>
    fetch(`${API_URL}${path}`, {
      method: "POST",
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...branchHeader(),
      },
      body: formData, // KHÔNG đặt Content-Type — trình duyệt tự gắn boundary
    });

  let res = await send(_accessToken);
  if (res.status === 401 && typeof window !== "undefined") {
    const refresh = await refreshAccessTokenOnce();
    if (refresh.token) {
      res = await send(refresh.token);
    } else if (refresh.expired) {
      window.dispatchEvent(new CustomEvent("vgt:session-expired"));
      throw new ApiError("Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại.", 401);
    } else {
      throw new ApiError(
        "Phiên đăng nhập cần được làm mới nhưng chưa thực hiện được. Vui lòng thử lại sau giây lát.",
        401,
      );
    }
  }
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const fallback =
      res.status === 403
        ? "Bạn không có quyền tải tệp vào mục này"
        : res.status === 413
          ? "Tệp quá lớn"
          : "Tải tệp thất bại";
    throw new ApiError(readErrorMessage(err, fallback), res.status);
  }
  return res.json();
}

/** Upload files (multipart/form-data — NOT JSON) */
export async function uploadFilesApi(
  files: File[],
  module: string,
  entityId: string,
  category?: string,
): Promise<FileUploadItem[]> {
  const formData = new FormData();
  files.forEach((f) => formData.append("files", f));
  formData.append("module", module);
  formData.append("entityId", entityId);
  if (category) formData.append("category", category);
  return postMultipart<FileUploadItem[]>("/api/v1/uploads", formData);
}

/** Upload avatar (auto resize 256×256) */
export async function uploadAvatarApi(file: File): Promise<FileUploadItem> {
  const formData = new FormData();
  formData.append("file", file);
  return postMultipart<FileUploadItem>("/api/v1/uploads/avatar", formData);
}

/** List files for entity */
export const getEntityFilesApi = (module: string, entityId: string) =>
  apiFetch<FileUploadItem[]>(
    `/api/v1/uploads?module=${module}&entityId=${entityId}`,
  );

/** Gom toàn bộ tài liệu vòng đời đơn hàng (mọi nguồn) — 1 request */
export const getOrderDocumentsApi = (orderId: string) =>
  apiFetch<{
    data: import("@/types/order").OrderDocument[];
    meta: { total: number };
  }>(`/api/v1/orders/${orderId}/documents`);

/** Delete file */
export const deleteFileApi = (id: string) =>
  apiFetch<{ message: string }>(`/api/v1/uploads/${id}`, { method: "DELETE" });

// ==================== PERMISSIONS ====================
export interface PermissionItem {
  id: string;
  module: string;
  action: string;
  scope: string;
  branchId?: string | null;
}
export interface PermissionModule {
  module: string;
  label: string;
  actions: string[];
}
export interface RoleWithPermissions {
  id: string;
  name: string;
  displayName: string;
  description: string | null;
  level: string;
  department: string | null;
  isSystem: boolean;
  userCount: number;
  permissions: PermissionItem[];
}

export const getPermissionModulesApi = () =>
  apiFetch<PermissionModule[]>("/api/v1/permissions/modules");
export const getRolesPermissionsApi = () =>
  apiFetch<RoleWithPermissions[]>("/api/v1/permissions/roles");
export const getRolePermissionsApi = (id: string) =>
  apiFetch<RoleWithPermissions>(`/api/v1/permissions/roles/${id}`);
export const updateRolePermissionsApi = (
  id: string,
  permissions: {
    module: string;
    action: string;
    scope?: string;
    branchId?: string | null;
  }[],
) =>
  apiFetch<RoleWithPermissions>(`/api/v1/permissions/roles/${id}`, {
    method: "PUT",
    body: JSON.stringify({ permissions }),
  });
export const seedPermissionsApi = () =>
  apiFetch<{ message: string; total: number }>("/api/v1/permissions/seed", {
    method: "POST",
  });

// ==================== ORDERS ====================
export interface OrderItem {
  id: string;
  code: string;
  /**
   * Người đang xem có sửa được đơn này không — API tính theo `orders:update`
   * + phạm vi (đơn của mình / chi nhánh). `undefined` ở dữ liệu cũ = theo quyền chung.
   */
  canEdit?: boolean;
  /** Quyền giá trên đơn (27/10/2026). `false` = API đã gọt `grandTotal` và các field tiền. */
  canViewPrice?: boolean;
  canEditPrice?: boolean;
  customerId: string;
  status: string;
  transportType: string;
  jobId?: string | null;
  job?: { id: string; code: string; title: string; status: string } | null;
  origin?: string;
  destination?: string;
  pickupDate?: string;
  deliveryDate?: string;
  /** Điểm hạ rỗng mặc định của đơn (tên địa điểm). */
  emptyReturnLocation?: string | null;
  /** Hạn trả rỗng (đơn nhập) — GĐ3. */
  emptyReturnDeadline?: string | null;
  /** Đơn gấp — hiện chấm đỏ cạnh mã đơn (lý do trong tooltip). */
  isUrgent?: boolean;
  urgentNote?: string | null;
  /**
   * Đơn gấp ĐANG GHIM (API danh sách tính, 22/10/2026): còn dòng chưa có xe — nổi đầu
   * danh sách, vạch đỏ mép trái + mã đơn đỏ. Gán đủ xe / đã giao / hủy thì `false`.
   */
  urgentPinned?: boolean;
  /** Chi nhánh sở hữu đơn (kinh doanh ký). */
  branchId?: string | null;
  branch?: { id: string; code: string; name: string } | null;
  /** Chi nhánh thực hiện vận chuyển — null nghĩa là chi nhánh sở hữu tự chạy. */
  executingBranchId?: string | null;
  executingBranch?: { id: string; code: string; name: string } | null;
  transferredBy?: string | null;
  transferredAt?: string | null;
  transferNote?: string | null;
  pricingMethod?: string;
  /** GĐ4b: thiết lập khung hàng rời lưu theo đơn (null = mặc định settings). */
  bulkWmFactor?: number | null;
  bulkRoundStep?: number | null;
  containerFlow?: string | null;
  unitPrice?: number;
  totalAmount: number;
  surchargeAmount: number;
  vatRate: number;
  vatAmount: number;
  grandTotal: number;
  customsRequired: boolean;
  customsType?: string;
  customsNo?: string;
  customsDate?: string;
  customsStatus?: string;
  hsCode?: string;
  customsFee?: number | null;
  customsNote?: string;
  /**
   * Thông tin thương mại của lô hàng — dữ liệu người khai hải quan cần.
   * `invoiceValue` là Decimal ở DB nên API trả về CHUỖI; luôn Number() trước
   * khi tính toán.
   */
  incoterm?: string | null;
  invoiceNo?: string | null;
  invoiceDate?: string | null;
  invoiceValue?: string | number | null;
  invoiceCurrency?: string | null;
  shipperName?: string | null;
  consigneeName?: string | null;
  countryOfOrigin?: string | null;
  paidAmount?: number;
  shippingLineId?: string;
  bookingNo?: string;
  blNo?: string;
  vessel?: string;
  voyage?: string;
  /** Toàn bộ số Booking / B/L (bookingNo/blNo là mã chính = phần tử đầu mỗi loại). */
  refNos?: Array<{
    id?: string;
    type: "BOOKING" | "BL";
    value: string;
    seq?: number;
  }>;
  eta?: string;
  etd?: string;
  closingTime?: string;
  note?: string;
  internalNote?: string;
  createdBy?: string;
  confirmedBy?: string;
  confirmedAt?: string;
  approvedBy?: string;
  approvedAt?: string;
  rejectedReason?: string;
  createdAt: string;
  creator?: { id: string; fullName: string } | null;
  updatedAt?: string;
  customer?: {
    id: string;
    code: string;
    name: string;
    shortName?: string;
    displayName?: string | null;
    phone?: string;
  };
  items?: OrderLineItem[];
  costs?: OrderCostItem[];
  dispatches?: any[];
  _count?: { items: number; dispatches: number; costs: number };
  /**
   * Bộ đếm dòng hàng theo trạng thái điều phối — chỉ có ở API danh sách
   * (`GET /orders`), nuôi badge "Đợi điều phối 2/10" trên bảng.
   */
  itemDispatch?: OrderItemDispatchSummary;
  /** GĐ6 — số dòng theo khung, chỉ ở API danh sách: chip "2 cont · 3 rời" của đơn tổng hợp. */
  itemSummary?: { containerCount: number; bulkCount: number };
}
/** Cùng hình dạng với `summarizeItems` phía API. */
export interface OrderItemDispatchSummary {
  total: number;
  notTransferred: number;
  awaitingDispatch: number;
  dispatched: number;
  inTransit: number;
  delivered: number;
  /** Đã rời khỏi tay kinh doanh — mọi trạng thái trừ NOT_TRANSFERRED. */
  transferred: number;
}
export interface OrderLineItem {
  /** GĐ4b — khung, cách tính dòng, tấn cước / số chuyến server tính, cước theo xe. */
  cargoMode?: "CONTAINER" | "BULK" | null;
  pricingMethod?: string | null;
  chargeQty?: number | null;
  requiredVehicleTypeId?: string | null;
  requiredVehicleType?: { id: string; code: string; name: string; payloadTons: number } | null;
  ownVehicle?: boolean;
  tripCount?: number | null;
  tripPrice?: number | null;
  floorApplied?: boolean;
  isOversize?: boolean;
  pieceLengthCm?: number | null;
  pieceWidthCm?: number | null;
  pieceHeightCm?: number | null;
  /** Số hiệu kiện của dòng hàng rời (22/10/2026). */
  packageNo?: string | null;
  id: string;
  seq: number;
  containerTypeId?: string;
  containerNo?: string;
  sealNo?: string;
  cargoTypeId?: string;
  cargoName?: string;
  hsCode?: string | null;
  weight?: number;
  volume?: number;
  quantity?: number;
  unit?: string;
  unitPrice?: number;
  amount?: number;
  status: string;
  note?: string;
  deliveryDate?: string | null;
  /** Điểm hạ rỗng riêng của dòng; null = theo điểm hạ rỗng của đơn. */
  emptyReturnLocation?: string | null;
  /** GĐ5 — tuyến riêng của dòng (tên địa điểm); null = theo đơn. */
  pickupLocation?: string | null;
  deliveryLocation?: string | null;
  /** GĐ5 — ngày lấy của dòng; null = theo đơn. */
  pickupDate?: string | null;
  /** Chỉ có ở API chi tiết đơn (`GET /orders/:id`); danh sách không trả. */
  containerType?: {
    code: string;
    name: string;
    size?: string | null;
    sizeType?: string | null;
  } | null;
}
export interface OrderCostItem {
  id: string;
  costType: string;
  name: string;
  amount: number;
  quantity: number;
  total: number;
  paidBy: string;
  note?: string;
  /** Có giá trị = dòng do cầu nối sinh (thầu phụ…), không phải phí dịch vụ của form. */
  sourceType?: string | null;
}
export interface OrderListResponse {
  data: OrderItem[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}
export interface OrderStats {
  draft: number;
  pending: number;
  approved: number;
  awaitingDispatch: number;
  dispatched: number;
  inTransit: number;
  delivered: number;
  invoiced: number;
  completed: number;
  cancelled: number;
  rejected: number;
  total: number;
}
export interface ActivityLogItem {
  id: string;
  userId: string;
  action: string;
  module: string;
  entityId?: string;
  details?: any;
  createdAt: string;
  user?: { id: string; fullName: string; email: string };
}
export const getOrdersApi = (p?: Record<string, string>) =>
  apiFetch<OrderListResponse>(
    `/api/v1/orders${p ? "?" + new URLSearchParams(p) : ""}`,
  );
export const getOrderApi = (id: string) =>
  apiFetch<OrderItem>(`/api/v1/orders/${id}`);
export const createOrderApi = (d: any, init?: { signal?: AbortSignal }) =>
  apiFetch<OrderItem>("/api/v1/orders", {
    method: "POST",
    body: JSON.stringify(d),
    signal: init?.signal,
  });
export const updateOrderApi = (
  id: string,
  d: any,
  init?: { signal?: AbortSignal },
) =>
  apiFetch<OrderItem>(`/api/v1/orders/${id}`, {
    method: "PUT",
    body: JSON.stringify(d),
    signal: init?.signal,
  });
export const deleteOrderApi = (id: string) =>
  apiFetch<{ message: string }>(`/api/v1/orders/${id}`, { method: "DELETE" });
export const getOrderStatsApi = () =>
  apiFetch<OrderStats>("/api/v1/orders/stats");
/** Mặc định cước hàng rời (GĐ2/GĐ4b): hệ số m³→tấn, bước làm tròn, ngưỡng cảnh báo giá. */
export interface OrderPricingDefaults {
  bulkWmFactor: number;
  bulkRoundStep: number;
  priceWarnRatio: number;
}
export const getOrderPricingDefaultsApi = () =>
  apiFetch<OrderPricingDefaults>("/api/v1/orders/pricing-defaults");

// ─── AI nhận cấu trúc cho "Nhập danh sách hàng hóa" (Đợt 4) ───────────────
/** AI dùng được không: khoá AI trên máy chủ + Cài đặt `business.aiColumnMapping`. */
export interface AiSheetStatus {
  enabled: boolean;
  /** Lý do tắt (hiện ở tooltip), null khi bật. */
  reason: string | null;
  provider: string | null;
  model: string | null;
  /** Thứ tự gọi, gồm cả nhà cung cấp dự phòng. */
  chain: AiChainLink[];
}
export interface AiChainLink {
  provider: string;
  model: string;
}
export interface AiSheetColumnInput {
  index: number;
  header: string;
  /** Tối đa 5 ô mẫu, đã che giá. */
  samples: string[];
  /** Trường từ điển đã gán chắc — gửi làm bối cảnh. */
  mapped?: string;
}
export interface AiSheetColumnSuggestion {
  index: number;
  field: string;
  unit: "" | "kg" | "t" | "mm" | "cm" | "m";
  confidence: number;
  reason: string;
}
/** Mỗi trường là đoạn chữ NGUYÊN VĂN trong dòng (server đã lọc đoạn bịa). */
export interface AiSheetTextLine {
  line: number;
  kind: "goods" | "container" | "other";
  description: string;
  quantity: string;
  unit: string;
  weight: string;
  weightBasis: "total" | "per_unit" | "";
  volume: string;
  dims: string;
  containerNo: string;
  containerType: string;
  seal: string;
  deliveryDate: string;
}
export const getAiSheetStatusApi = () =>
  apiFetch<AiSheetStatus>("/api/v1/ai/analyze-sheet/status");
export const analyzeSheetColumnsAiApi = (
  body: { frame: string; columns: AiSheetColumnInput[] },
  signal?: AbortSignal,
) =>
  apiFetch<{ columns: AiSheetColumnSuggestion[] }>("/api/v1/ai/analyze-sheet", {
    method: "POST",
    body: JSON.stringify({ mode: "columns", ...body }),
    signal,
  });
export const analyzeSheetTextAiApi = (
  body: { frame: string; lines: string[] },
  signal?: AbortSignal,
) =>
  apiFetch<{ lines: AiSheetTextLine[] }>("/api/v1/ai/analyze-sheet", {
    method: "POST",
    body: JSON.stringify({ mode: "text", ...body }),
    signal,
  });
export const getOrderNextCodeApi = () =>
  apiFetch<{ code: string }>("/api/v1/orders/next-code");
/**
 * Loại thu phí dịch vụ của đơn (25/09/2026: danh mục "Loại thu phí dịch vụ",
 * thay 7 mã cố định). Gồm cả loại đã tắt — form cần tên loại cũ trên đơn cũ,
 * chỉ loại `isActive` mới được chọn mới.
 */
export interface ServiceFeeTypeOption {
  code: string;
  name: string;
  isActive: boolean;
}
export const getServiceFeeTypesApi = () =>
  apiFetch<ServiceFeeTypeOption[]>("/api/v1/orders/service-fee-types");
/** Tạo nhanh loại mới từ form đơn — trùng tên thì server trả lại loại đã có. */
export const quickCreateServiceFeeTypeApi = (name: string) =>
  apiFetch<ServiceFeeTypeOption>("/api/v1/orders/service-fee-types", {
    method: "POST",
    body: JSON.stringify({ name }),
  });

/** Kiểm tra số Booking / B-L đã được đơn khác dùng chưa (form tạo đơn). */
export const checkOrderRefApi = (params: {
  field: "booking" | "bl";
  value: string;
  excludeId?: string;
}) => {
  const qs = new URLSearchParams({ field: params.field, value: params.value });
  if (params.excludeId) qs.set("excludeId", params.excludeId);
  return apiFetch<{ exists: boolean; code: string | null }>(
    `/api/v1/orders/check-ref?${qs.toString()}`,
  );
};
export const addOrderItemApi = (orderId: string, d: any) =>
  apiFetch<OrderLineItem>(`/api/v1/orders/${orderId}/items`, {
    method: "POST",
    body: JSON.stringify(d),
  });
export const removeOrderItemApi = (orderId: string, itemId: string) =>
  apiFetch<{ message: string }>(`/api/v1/orders/${orderId}/items/${itemId}`, {
    method: "DELETE",
  });
export const addOrderCostApi = (orderId: string, d: any) =>
  apiFetch<OrderCostItem>(`/api/v1/orders/${orderId}/costs`, {
    method: "POST",
    body: JSON.stringify(d),
  });
export const removeOrderCostApi = (orderId: string, costId: string) =>
  apiFetch<{ message: string }>(`/api/v1/orders/${orderId}/costs/${costId}`, {
    method: "DELETE",
  });
// submitOrderApi/approveOrderApi đã gỡ (ORD-14, 29/09/2026): backend đã gỡ
// `PUT :id/submit` và `PUT :id/approve` khỏi controller — luồng duyệt
// PENDING/APPROVED không còn trong vòng đời thực tế.
/**
 * Chuyển một tập con dòng hàng sang điều phối — mỗi lần gọi sinh MỘT lệnh điều
 * xe ôm đúng tập dòng đã tích. Gọi được nhiều lần cho cùng một đơn.
 */
export interface TransferDispatchBody {
  orderItemIds: string[];
  note?: string;
  pickupTime?: string;
  deliveryTime?: string;
}
/**
 * Plan 4 Task 16 (L7): xem trước "Thay đổi này ảnh hưởng lệnh điều xe" khi lưu đơn — API không
 * ghi gì; lưu đơn mới cập nhật lệnh / báo tài xế (`DispatchRouteSyncService`).
 */
export interface DispatchImpactRow {
  orderItemId: string;
  containerNo: string | null;
  field: 'pickupLocation' | 'deliveryLocation' | 'emptyReturnLocation' | 'pickupDate' | 'deliveryDate';
  /** Điểm: tên; ngày: `YYYY-MM-DD`; null = theo đơn / chưa có. */
  from: string | null;
  to: string | null;
  dispatchCode: string;
  tripCode: string | null;
  tripStatus: string | null;
  driverName: string | null;
  result: 'UPDATE' | 'UPDATE_NOTIFY_DRIVER' | 'LOCKED';
  /** true = đổi điểm / ngày của ĐƠN kéo theo (dòng theo đơn); false = tự sửa trên dòng. */
  viaOrder: boolean;
}
export interface DispatchImpactResult {
  rows: DispatchImpactRow[];
  dispatchesWithNewRoute: { code: string; routeCount: number }[];
}
export const getDispatchImpactApi = (orderId: string, body: unknown) =>
  apiFetch<DispatchImpactResult>(`/api/v1/orders/${orderId}/dispatch-impact`, {
    method: 'POST',
    body: JSON.stringify(body),
  });

export const transferToDispatchApi = (id: string, body: TransferDispatchBody) =>
  apiFetch<OrderItem>(`/api/v1/orders/${id}/transfer-dispatch`, {
    method: "PUT",
    body: JSON.stringify(body),
  });

// ── Điều chỉnh giá theo dòng hàng ──
export interface CreatePriceAdjustmentBody {
  reason: string;
  note?: string;
  mode: "PERCENT" | "AMOUNT";
  split?: "PER_LINE" | "PRORATE";
  /** Có dấu: dương phụ thu, âm chiết khấu. */
  value: number;
  itemIds: string[];
}
export interface PriceAdjustmentResult {
  adjustment: { id: string; seq: number };
  /** Tổng tác động của thao tác (âm khi huỷ), để hiện toast. */
  delta: number;
  order: OrderItem;
}
export const createOrderPriceAdjustmentApi = (
  id: string,
  body: CreatePriceAdjustmentBody,
) =>
  apiFetch<PriceAdjustmentResult>(`/api/v1/orders/${id}/price-adjustments`, {
    method: "POST",
    body: JSON.stringify(body),
  });
export const cancelOrderPriceAdjustmentApi = (
  id: string,
  adjustmentId: string,
  reason: string,
) =>
  apiFetch<PriceAdjustmentResult>(
    `/api/v1/orders/${id}/price-adjustments/${adjustmentId}/cancel`,
    { method: "PUT", body: JSON.stringify({ reason }) },
  );
/** Tình hình điều phối của đơn: dòng hàng, lệnh, chuyến trong một lượt gọi. */
export const getOrderDispatchOverviewApi = (id: string) =>
  apiFetch<import("@/types/order").OrderDispatchOverview>(
    `/api/v1/orders/${id}/dispatch-overview`,
  );
/**
 * Chuyển việc vận chuyển của đơn sang chi nhánh khác. Chi nhánh SỞ HỮU đơn
 * không đổi — chỉ đổi chi nhánh thực hiện. `branchId: null` = thu hồi về
 * chi nhánh sở hữu.
 */
export const transferOrderBranchApi = (
  id: string,
  branchId: string | null,
  note?: string,
) =>
  apiFetch<OrderItem>(`/api/v1/orders/${id}/transfer-branch`, {
    method: "PUT",
    body: JSON.stringify({ branchId, note }),
  });
// Chuyển yêu cầu tạo hóa đơn (DELIVERED → INVOICED). Bridge R1 sinh hóa đơn AR
// nháp rồi đẩy chứng từ sang Fast BO — xem OrdersService.requestInvoice.
export const requestOrderInvoiceApi = (id: string) =>
  apiFetch<OrderItem>(`/api/v1/orders/${id}/request-invoice`, {
    method: "PUT",
  });
/**
 * `blockers` chặn xuất hóa đơn; `warnings` chỉ nhắc (lệnh thiếu D/O · EIR ·
 * biên bản giao hàng) — quyết định nghiệp vụ 17/09/2026 là không chặn.
 */
export type OrderInvoiceReadiness = {
  ready: boolean;
  applicable: boolean;
  blockers: string[];
  warnings?: string[];
};
export const getOrderInvoiceReadinessApi = (id: string) =>
  apiFetch<OrderInvoiceReadiness>(`/api/v1/orders/${id}/invoice-readiness`);
// rejectOrderApi đã gỡ (ORD-14, 29/09/2026): backend đã gỡ `PUT :id/reject`.
export const getOrderActivitiesApi = (id: string) =>
  apiFetch<ActivityLogItem[]>(`/api/v1/orders/${id}/activities`);
export const getOrderTimelineApi = (id: string) =>
  apiFetch<import("@/types/order").OrderTimeline>(
    `/api/v1/orders/${id}/timeline`,
  );
// updateOrderPaymentApi đã gỡ (ORD-23, 29/09/2026): backend đã gỡ
// `PUT :id/payment` — nguồn sự thật công nợ là hoá đơn AR/Fast BO.

// ── Bulk operations (skip-with-report; see orders-list-refactor spec Req 12) ──
export interface BulkOrdersResult {
  success: string[];
  skipped: { id: string; reason: string }[];
}
// bulkApproveOrdersApi/bulkRejectOrdersApi đã gỡ (ORD-14, 29/09/2026): backend
// đã gỡ `POST /orders/bulk-approve` / `bulk-reject`.
export const bulkDeleteOrdersApi = (ids: string[]) =>
  apiFetch<BulkOrdersResult>("/api/v1/orders/bulk-delete", {
    method: "POST",
    body: JSON.stringify({ ids }),
  });

// ==================== UPLOADS — convenience wrappers for order documents ====================
// Reuses existing FileUploadItem + endpoints. We add a single-file POST helper here
// because the existing `uploadFilesApi` only supports the bulk endpoint.
export const uploadSingleFileApi = (
  module: string,
  entityId: string,
  file: File,
  category?: string,
): Promise<FileUploadItem> => {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("module", module);
  fd.append("entityId", entityId);
  if (category) fd.append("category", category);
  const headers: Record<string, string> = {
    Authorization: `Bearer ${_accessToken ?? ""}`,
    ...branchHeader(),
  };
  return fetch(`${API_URL}/api/v1/uploads/single`, {
    method: "POST",
    headers,
    body: fd,
  }).then(async (res) => {
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || "Upload thất bại");
    }
    return res.json() as Promise<FileUploadItem>;
  });
};

// ==================== CONTAINERS ====================
export interface ContainerTrackingEntry {
  id: string;
  status: string;
  location?: string;
  timestamp: string;
  note?: string;
  updatedBy?: string;
}
export interface ContainerListItem {
  id: string;
  orderId: string;
  containerNo?: string;
  sealNo?: string;
  cargoName?: string;
  weight?: number;
  status: string;
  order?: {
    id: string;
    code: string;
    status: string;
    origin?: string;
    destination?: string;
    customer?: {
      id: string;
      name: string;
      code: string;
      displayName?: string | null;
    };
  };
  containerTracking?: ContainerTrackingEntry[];
}
export interface ContainerListResponse {
  data: ContainerListItem[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}
export const getContainersApi = (p?: Record<string, string>) =>
  apiFetch<ContainerListResponse>(
    `/api/v1/containers${p ? "?" + new URLSearchParams(p) : ""}`,
  );
export const getContainerApi = (id: string) =>
  apiFetch<ContainerListItem>(`/api/v1/containers/${id}`);
export const updateContainerStatusApi = (
  id: string,
  d: { status: string; location?: string; note?: string; timestamp?: string },
) =>
  apiFetch<ContainerListItem>(`/api/v1/containers/${id}/status`, {
    method: "PUT",
    body: JSON.stringify(d),
  });
export const getContainerStatsApi = () =>
  apiFetch<Record<string, number>>("/api/v1/containers/stats");

// ==================== DISPATCH ====================
/** Trip con nhúng trong DispatchItem — chỉ các field mà `dispatch.service.ts` (sel.trips) trả về. */
export interface DispatchEmbeddedTrip {
  id: string;
  code: string;
  status: string;
  /** Dòng hàng chuyến này chở — dùng để loại container đã có chuyến khi gán xe. */
  orderItemId?: string | null;
  containerNo?: string | null;
  /** T2: nhóm chuyến ghép 2×20' — board gộp thành một thẻ xe. */
  comboId?: string | null;
  comboLead?: boolean;
  /** Nối chuyến: chặng trước trong cùng lệnh — dòng xe gộp "50E ⇢ 51C". */
  relayFromTripId?: string | null;
  isOutsourced?: boolean;
  /** Tuyến + hạn giao theo container chuyến chở — chỉ chi tiết lệnh trả (Plan 3, L4). */
  route?: TripRoute | null;
  subVehiclePlate?: string | null;
  subDriverName?: string | null;
  subcontractorId?: string | null;
  subcontractor?: {
    id: string;
    name: string;
    displayName?: string | null;
  } | null;
  vehicle?: { plateNumber: string } | null;
  driver?: { fullName: string } | null;
}
export interface DispatchItem {
  id: string;
  code: string;
  orderId: string;
  orderItemId?: string;
  status: string;
  vehicleId?: string;
  driverId?: string;
  trailerId?: string;
  routeId?: string;
  pickupLocation?: string;
  deliveryLocation?: string;
  pickupTime?: string;
  deliveryTime?: string;
  /** Điểm trả container rỗng — chặng thứ ba, nhân bản từ đơn lúc tạo lệnh. */
  emptyReturnLocation?: string | null;
  /** Đơn gấp — nhân bản từ đơn để điều phối/tài xế thấy ngay trên lệnh. */
  isUrgent?: boolean;
  /** Người trả phí hạ rỗng — null = chưa chốt (tài xế chốt ở bước Hoàn tất). */
  liftOffPayer?: LiftOffPayer | null;
  liftOffNote?: string | null;
  startedAt?: string;
  completedAt?: string;
  startKm?: number;
  endKm?: number;
  distance?: number;
  fuelCost?: number;
  tollCost?: number;
  otherCost?: number;
  driverAllowance?: number;
  note?: string;
  createdBy?: string;
  createdAt: string;
  /** Ngày giao hàng theo đơn: ngày giao sớm nhất của các dòng hàng lệnh ôm — điều phối không được đổi. */
  deliveryDeadline?: string | null;
  /** Tuyến theo từng container + khoảng ngày giao (L3, 29/10/2026) — chip trên danh sách / board. */
  routeSummary?: DispatchRouteSummary | null;
  /**
   * GĐ5 — luồng của LỆNH (đơn tổng hợp tách lệnh container / hàng rời). Đọc qua
   * `dispatchTransportType(d)`; null ở lệnh cũ → theo đơn.
   */
  transportType?: string | null;
  order?: {
    id: string;
    code: string;
    status: string;
    transportType: string;
    origin?: string;
    destination?: string;
    emptyReturnLocation?: string | null;
    isUrgent?: boolean;
    /** Ngày lấy hàng theo đơn — mốc điều phối cần nhìn trước khi gán xe. */
    pickupDate?: string | null;
    bookingNo?: string | null;
    blNo?: string | null;
    customer?: {
      id: string;
      code: string;
      name: string;
      shortName?: string | null;
      displayName?: string | null;
    };
  };
  vehicle?: { id: string; plateNumber: string; type?: string };
  driver?: { id: string; code: string; fullName: string; phone?: string };
  trailer?: { id: string; plateNumber: string };
  trips?: DispatchEmbeddedTrip[];
  /** Khung "Lịch sử đổi tuyến / ngày" — chỉ `GET /dispatches/:id` trả, mới nhất trước (Plan 3, L4). */
  routeHistory?: DispatchRouteHistoryEntry[];
  /**
   * Tập dòng hàng thuộc lệnh này. Vắng mặt ở lệnh cũ tạo trước bảng nối
   * `dispatch_items` — khi đó coi như lệnh ôm cả đơn.
   */
  items?: Array<{
    orderItemId: string;
    orderItem: {
      id: string;
      seq: number;
      containerTypeId?: string | null;
      containerNo?: string | null;
      sealNo?: string | null;
      /** GĐ7 — khung + tải trọng xe yêu cầu để form gán xe tách chuyến hàng rời. */
      cargoMode?: 'CONTAINER' | 'BULK' | null;
      requiredVehicleType?: { payloadTons?: number | null } | null;
      cargoName?: string | null;
      weight?: number | null;
      quantity?: number | null;
      unit?: string | null;
      deliveryDate?: string | null;
      emptyReturnLocation?: string | null;
      /** Tuyến + ngày lấy riêng của dòng (L3 / L4, 29/10/2026). */
      pickupLocation?: string | null;
      deliveryLocation?: string | null;
      pickupDate?: string | null;
      dispatchStatus?: string | null;
      containerType?: {
        code: string;
        name: string;
        size?: string | null;
        sizeType?: string | null;
      } | null;
      cargoType?: { code: string; name: string } | null;
    };
  }>;
}

export interface DispatchRouteHistoryEntry {
  id: string;
  /** DISPATCH_CREATED (dòng cuối, từ ngày tạo lệnh) · DISPATCH_ITEM_SPLIT · DISPATCH_ITEM_RELEASED · ROUTE_CHANGED · DATE_CHANGE_*. */
  action: string;
  details: Record<string, unknown> | null;
  at: string;
  by: string | null;
}
export interface DispatchListResponse {
  data: DispatchItem[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}
export interface DispatchStats {
  pending: number;
  assigned: number;
  inTransit: number;
  completed: number;
  cancelled: number;
  total: number;
}
export const getDispatchesApi = (p?: Record<string, string>) =>
  apiFetch<DispatchListResponse>(
    `/api/v1/dispatches${p ? "?" + new URLSearchParams(p) : ""}`,
  );
export const getDispatchApi = (id: string) =>
  apiFetch<DispatchItem>(`/api/v1/dispatches/${id}`);
export const createDispatchApi = (d: any) =>
  apiFetch<DispatchItem>("/api/v1/dispatches", {
    method: "POST",
    body: JSON.stringify(d),
  });
export const createDispatchBulkApi = (d: {
  orderId: string;
  assignments: any[];
  pickupLocation?: string;
  deliveryLocation?: string;
  emptyReturnLocation?: string;
  pickupTime?: string;
  deliveryTime?: string;
  note?: string;
}) =>
  apiFetch<{ count: number; dispatches: DispatchItem[] }>(
    "/api/v1/dispatches/bulk",
    { method: "POST", body: JSON.stringify(d) },
  );
export const updateDispatchApi = (id: string, d: any) =>
  apiFetch<DispatchItem>(`/api/v1/dispatches/${id}`, {
    method: "PUT",
    body: JSON.stringify(d),
  });
export const deleteDispatchApi = (id: string) =>
  apiFetch<{ message: string }>(`/api/v1/dispatches/${id}`, {
    method: "DELETE",
  });
/**
 * Lệnh gán dần (26/10/2026) — thao tác trên MỘT container chưa có xe của lệnh.
 * Tách sang LỆNH MỚI (Chờ điều phối) — trả về lệnh mới; trả về kinh doanh
 * (dòng về "chưa chuyển", có lý do) — trả về lệnh gốc đã cập nhật.
 */
export const splitDispatchItemApi = (dispatchId: string, orderItemId: string) =>
  apiFetch<DispatchItem>(
    `/api/v1/dispatches/${dispatchId}/items/${orderItemId}/split`,
    { method: "PUT" },
  );
export const releaseDispatchItemApi = (
  dispatchId: string,
  orderItemId: string,
  reason: string,
) =>
  apiFetch<DispatchItem>(
    `/api/v1/dispatches/${dispatchId}/items/${orderItemId}/release`,
    { method: "PUT", body: JSON.stringify({ reason }) },
  );
// ─── Plan 5 (L8 / L9, 30/10/2026): yêu cầu đổi ngày của điều độ ───
export type DateChangeRequestStatus =
  | "PENDING"
  | "APPROVED"
  | "REJECTED"
  | "EXPIRED";

export interface DateChangeRequest {
  id: string;
  /** `YC-2026-00001`. */
  code: string;
  orderId: string;
  dispatchId: string;
  /** `YYYY-MM-DD` hoặc null = không đổi. Một bộ ngày chung cho mọi container của yêu cầu. */
  newPickupDate: string | null;
  newDeliveryDate: string | null;
  reason: string;
  isUrgent: boolean;
  driverNote: string | null;
  status: DateChangeRequestStatus;
  requestedById: string;
  decidedById: string | null;
  decidedAt: string | null;
  /** Lý do từ chối, hoặc vì sao hết hiệu lực. */
  decisionNote: string | null;
  createdAt: string;
  updatedAt: string;
  order: {
    id: string;
    code: string;
    branchId: string | null;
    executingBranchId: string | null;
    pickupDate: string | null;
    deliveryDate: string | null;
  };
  dispatch: { id: string; code: string };
  requestedBy: { id: string; fullName: string } | null;
  decidedBy: { id: string; fullName: string } | null;
  items: Array<{
    id: string;
    orderItemId: string;
    /** Ngày của dòng lúc gửi yêu cầu — cột "hiện tại" trên khung xác nhận. */
    oldPickupDate: string | null;
    oldDeliveryDate: string | null;
    orderItem: {
      id: string;
      containerNo: string | null;
      cargoName: string | null;
      pickupDate: string | null;
      deliveryDate: string | null;
      dispatchStatus: string | null;
    };
  }>;
}

export const createDateChangeRequestApi = (body: {
  dispatchId: string;
  orderItemIds: string[];
  newPickupDate?: string | null;
  newDeliveryDate?: string | null;
  reason: string;
  isUrgent?: boolean;
  driverNote?: string | null;
}) =>
  apiFetch<DateChangeRequest>("/api/v1/date-change-requests", {
    method: "POST",
    body: JSON.stringify(body),
  });
export const getDateChangeRequestsByDispatchApi = (dispatchId: string) =>
  apiFetch<DateChangeRequest[]>(
    `/api/v1/date-change-requests/by-dispatch/${dispatchId}`,
  );
/** Mặc định chỉ đang chờ; `status: 'all'` lấy cả lịch sử. */
export const getDateChangeRequestsByOrderApi = (
  orderId: string,
  status?: DateChangeRequestStatus | "all",
) =>
  apiFetch<DateChangeRequest[]>(
    `/api/v1/date-change-requests/by-order/${orderId}${status ? `?status=${status}` : ""}`,
  );
export const approveDateChangeRequestApi = (id: string) =>
  apiFetch<DateChangeRequest>(`/api/v1/date-change-requests/${id}/approve`, {
    method: "PUT",
  });
export const rejectDateChangeRequestApi = (id: string, reason: string) =>
  apiFetch<DateChangeRequest>(`/api/v1/date-change-requests/${id}/reject`, {
    method: "PUT",
    body: JSON.stringify({ reason }),
  });

export const getDispatchStatsApi = () =>
  apiFetch<DispatchStats>("/api/v1/dispatches/stats");
export const getDispatchNextCodeApi = () =>
  apiFetch<{ code: string }>("/api/v1/dispatches/next-code");
// busyRef: lệnh đang dùng xe (nếu busy) — để cảnh báo "xe đang chạy cho lệnh ABC",
// KHÔNG dùng để chặn chọn.
export const getAvailableVehiclesApi = () =>
  apiFetch<
    {
      id: string;
      plateNumber: string;
      type?: string;
      status?: string;
      available: boolean;
      busy: boolean;
      busyRef?: {
        dispatchId: string;
        dispatchCode: string;
        containerNo?: string | null;
      } | null;
      lastGpsAddress?: string | null;
      lastGpsTime?: string | null;
      branch?: { id: string; name: string; code: string } | null;
    }[]
  >("/api/v1/dispatches/available-vehicles");
// offDuty/workStatus: backend tính từ user.workStatus (R7.3) — tài xế nghỉ phép
// vẫn trả về nhưng không được chọn để điều phối.
export const getAvailableDriversApi = () =>
  apiFetch<
    {
      id: string;
      code: string;
      fullName: string;
      phone?: string;
      available: boolean;
      busy: boolean;
      busyRef?: {
        dispatchId: string;
        dispatchCode: string;
        containerNo?: string | null;
      } | null;
      offDuty?: boolean;
      workStatus?: string | null;
      branch?: { id: string; name: string; code: string } | null;
    }[]
  >("/api/v1/dispatches/available-drivers");
export const getAvailableTrailersApi = () =>
  apiFetch<
    {
      id: string;
      plateNumber: string;
      type?: string;
      available: boolean;
      busy: boolean;
    }[]
  >("/api/v1/dispatches/available-trailers");
// `reason` bắt buộc khi status === 'CANCELLED' (backend từ chối 400 nếu thiếu/rỗng — ORD-02).
export const updateOrderStatusApi = (
  id: string,
  status: string,
  reason?: string,
) =>
  apiFetch<OrderItem>(`/api/v1/orders/${id}/status`, {
    method: "PUT",
    body: JSON.stringify({ status, reason }),
  });

// ==================== DISPATCH TRIPS ====================
export interface TripCostItem {
  id: string;
  type: string;
  name: string;
  /** Số đã chốt (điều độ có thể sửa trước khi xác nhận — số tài xế ghi ở `originalAmount`). */
  amount: number;
  receiptNo?: string;
  receiptUrl?: string;
  note?: string;
  createdAt: string;
  // Xác nhận chi phí tài xế ghi (04/11/2026). Thiếu (API cũ) → coi là CONFIRMED.
  status?: "PENDING" | "CONFIRMED" | "REJECTED";
  source?: "DRIVER" | "OFFICE" | "AUTO" | null;
  createdById?: string | null;
  /** Chỉ GET chi tiết chuyến trả tên; danh sách chỉ có id. */
  createdByName?: string | null;
  confirmedById?: string | null;
  confirmedByName?: string | null;
  confirmedAt?: string | null;
  /** Lý do từ chối, hoặc ghi chú khi điều độ sửa số tiền. */
  decisionNote?: string | null;
  originalAmount?: number | null;
}

// ─── Checklist giấy tờ chuyến xe ───
export interface TripDocRequirementItem {
  id: string;
  tripId?: string;
  kind: string;
  name: string;
  phase: "BEFORE_START" | "AFTER_COMPLETE";
  required: boolean;
  status: "PENDING" | "RECEIVED" | "WAIVED";
  matchedPhotoId?: string | null;
  matchedPhoto?: { id: string; url: string; kind: string } | null;
  receivedAt?: string | null;
  /** 'system' = văn phòng đồng bộ tự động; userId = tick tay; null = match ảnh */
  receivedBy?: string | null;
  note?: string | null;
  sortOrder: number;
  /** File nguồn văn phòng (eDO, mã vạch/tờ khai) — tài xế bấm Xem trên app */
  sourceFile?: { url: string; name: string } | null;
  /** Mọi tệp của ô (D/O nhiều bản…) — `sourceFile` là tệp đầu. */
  sourceFiles?: { url: string; name: string }[];
}
export interface TripDocPhaseProgress {
  total: number;
  requiredTotal: number;
  received: number;
  waived: number;
  percent: number;
  /** Tên các giấy tờ bắt buộc còn thiếu — dựng câu cảnh báo trực tiếp */
  missing: string[];
}
export interface TripDocProgress {
  beforeStart: TripDocPhaseProgress;
  afterComplete: TripDocPhaseProgress;
}

export const getTripDocumentsApi = (tripId: string) =>
  apiFetch<{ data: TripDocRequirementItem[]; progress: TripDocProgress }>(
    `/api/v1/dispatch-trips/${tripId}/documents`,
  );
export const addTripDocumentApi = (
  tripId: string,
  d: {
    kind: string;
    name: string;
    phase: string;
    required?: boolean;
    note?: string;
  },
) =>
  apiFetch<TripDocRequirementItem>(
    `/api/v1/dispatch-trips/${tripId}/documents`,
    { method: "POST", body: JSON.stringify(d) },
  );
export const updateTripDocumentStatusApi = (
  docId: string,
  status: "PENDING" | "RECEIVED" | "WAIVED",
) =>
  apiFetch<TripDocRequirementItem>(
    `/api/v1/dispatch-trips/documents/${docId}/status`,
    { method: "PUT", body: JSON.stringify({ status }) },
  );
export const updateTripDocumentApi = (
  docId: string,
  d: { name?: string; required?: boolean; note?: string },
) =>
  apiFetch<TripDocRequirementItem>(
    `/api/v1/dispatch-trips/documents/${docId}`,
    { method: "PUT", body: JSON.stringify(d) },
  );
export const deleteTripDocumentApi = (docId: string) =>
  apiFetch<{ message: string }>(`/api/v1/dispatch-trips/documents/${docId}`, {
    method: "DELETE",
  });
/** T2: thành viên nhóm chuyến ghép (GET dispatch-trips/:id). */
export interface TripComboMember {
  id: string;
  code: string;
  status: string;
  comboLead: boolean;
  containerNo?: string | null;
  sealNo?: string | null;
  pickupCondition?: "OK" | "DAMAGED" | null;
  orderItem?: {
    sealNo?: string | null;
    containerType?: { code: string; name: string } | null;
  } | null;
}

// ==================== NỐI CHUYẾN (26/10/2026) ====================
// docs/VGT_ERP_Noi_Chuyen_Plan.md — chuỗi chặng CX-A ⇢ CX-B: chặng cũ kết thúc
// `HANDED_OVER`, chặng kế mang `relayFromTripId` và nhận bàn giao rồi đi tiếp.

/** Chặng TRƯỚC của một chuyến (chi tiết / danh sách / app tài xế). */
export interface TripRelayFrom {
  id: string;
  code: string;
  status: string;
  containerNo?: string | null;
  sealNo?: string | null;
  handoverAt?: string | null;
  handoverBy?: "giver" | "receiver" | "office" | null;
  handoverLocation?: string | null;
  handoverLat?: number | null;
  handoverLng?: number | null;
  handoverNote?: string | null;
  distance?: number | null;
  isOutsourced?: boolean;
  subVehiclePlate?: string | null;
  subDriverName?: string | null;
  subDriverPhone?: string | null;
  vehicle?: { id: string; plateNumber: string } | null;
  driver?: { id: string; fullName: string; phone?: string | null } | null;
}
/** Chặng KẾ còn hiệu lực (chưa hủy) của một chuyến. */
export interface TripRelayTo {
  id: string;
  code: string;
  status: string;
  handoverReceivedAt?: string | null;
  relayFromStatus?: string | null;
  isOutsourced?: boolean;
  subVehiclePlate?: string | null;
  subDriverName?: string | null;
  subDriverPhone?: string | null;
  vehicle?: { id: string; plateNumber: string } | null;
  driver?: { id: string; fullName: string; phone?: string | null } | null;
}
/** Một mắt xích trong chuỗi chặng của container (gốc → mới nhất). */
export interface TripRelayLeg {
  id: string;
  code: string;
  status: string;
  relayFromTripId?: string | null;
  relayFromStatus?: string | null;
  handoverAt?: string | null;
  handoverLocation?: string | null;
  handoverReceivedAt?: string | null;
  startedAt?: string | null;
  completedAt?: string | null;
  distance?: number | null;
  isOutsourced?: boolean;
  subVehiclePlate?: string | null;
  subDriverName?: string | null;
  vehicle?: { id: string; plateNumber: string } | null;
  driver?: { id: string; fullName: string; phone?: string | null } | null;
}
/** Khối `relay` của GET chi tiết chuyến — null khi chuyến không nối với ai. */
export interface TripRelayInfo {
  from: TripRelayFrom | null;
  to: TripRelayTo | null;
  /** Chặng cũ đang chờ bàn giao (có chặng kế, mình chưa kết thúc). */
  pending: boolean;
  receivedAt?: string | null;
  chain: TripRelayLeg[];
}
export type RelayReasonValue = "BREAKDOWN" | "ACCIDENT" | "DRIVER" | "OTHER";
/** `POST dispatch-trips/:id/relay` — khớp `CreateRelayDto` (API). */
export interface CreateRelayPayload {
  reason: RelayReasonValue;
  note?: string;
  vehicleId?: string;
  driverId?: string;
  trailerId?: string;
  takeOverTrailer?: boolean;
  isOutsourced?: boolean;
  subcontractorId?: string;
  subVehiclePlate?: string;
  subDriverName?: string;
  subDriverPhone?: string;
  agreedRate?: number;
  rateCardId?: string;
  handoverLocation?: string;
  handoverLat?: number;
  handoverLng?: number;
  createIncident?: boolean;
}
export interface CreateRelayResult {
  /** Chặng cũ (chuyến dẫn) sau khi nối — đã kèm khối `relay`. */
  trip: DispatchTripItem;
  created: { id: string; code: string; relayFromTripId: string | null }[];
}
export const createRelayApi = (tripId: string, d: CreateRelayPayload) =>
  apiFetch<CreateRelayResult>(`/api/v1/dispatch-trips/${tripId}/relay`, {
    method: "POST",
    body: JSON.stringify(d),
  });
/** Chặng CŨ chốt "Đã bàn giao xong" (gọi lại chỉ điền ODO / ghi chú còn trống). */
export const handoverTripApi = (
  tripId: string,
  d: { endKm?: number; note?: string },
) =>
  apiFetch<DispatchTripItem>(`/api/v1/dispatch-trips/${tripId}/handover`, {
    method: "PUT",
    body: JSON.stringify(d),
  });
/** Chặng KẾ chốt "Đã nhận bàn giao" — nhảy hợp lệ sang bước chặng trước đã tới. */
export const receiveHandoverApi = (
  tripId: string,
  d: { startKm?: number; sealNo?: string; note?: string },
) =>
  apiFetch<DispatchTripItem>(
    `/api/v1/dispatch-trips/${tripId}/receive-handover`,
    { method: "PUT", body: JSON.stringify(d) },
  );

/**
 * Tuyến theo từng container + khoảng ngày giao của một lệnh (L3, 29/10/2026) — API tính ở
 * `dispatch/dispatch-route-summary.ts`; chip "+N tuyến" / "Giao 29 → 31/10" trên danh sách.
 */
export interface DispatchRouteSummary {
  /** Tuyến (lấy, trả) — nhiều container nhất đứng đầu, hoà thì theo số thứ tự dòng. */
  routes: { pickup: string | null; delivery: string | null; count: number }[];
  pickupFrom: string | null;
  deliveryFrom: string | null;
  deliveryTo: string | null;
  deliveryDays: number;
  /** Plan 4 (L3): đơn đổi tuyến / ngày của lệnh trong 24 giờ qua — chip "Đổi điểm trả 14:05". */
  recentChange?: { field: string; at: string } | null;
  /** Plan 5 (L8): số container của lệnh đang có yêu cầu đổi ngày chờ — chip "Chờ KD xác nhận đổi ngày". */
  pendingDateChanges?: number;
}

/**
 * Tuyến của một chuyến theo CONTAINER chở (L2, 29/10/2026) — API tính ở
 * `dispatch/trip-route.util.ts`; app tài xế đọc ở đây thay vì điểm / giờ của lệnh.
 */
export interface TripRoute {
  pickup: string | null;
  delivery: string | null;
  emptyReturn: string | null;
  pickupAt: string | null;
  /** `pickupAt` là giờ hẹn điều phối đặt; false = chỉ là ngày lấy (không hiện "07:00"). */
  pickupAtHasTime: boolean;
  deliverBy: string | null;
  /** `deliverBy` mang giờ hẹn của lệnh; false = chỉ là ngày giao của container. */
  deliverByHasTime: boolean;
  pickupAddress?: string | null;
  deliveryAddress?: string | null;
}

export interface DispatchTripItem {
  id: string;
  code: string;
  dispatchId: string;
  status: string;
  /** Tuyến theo container — endpoint của tài xế (my-trips, chi tiết chuyến) trả; chi tiết lệnh đọc `DispatchItem.trips[].route`. */
  route?: TripRoute | null;
  vehicleId?: string;
  driverId?: string;
  trailerId?: string;
  containerNo?: string;
  orderItemId?: string;
  /** GĐ7 — phần hàng rời chuyến này chở (dòng rời nhiều chuyến); null = chở cả dòng. */
  cargoWeight?: number | null;
  cargoQuantity?: number | null;
  /**
   * T2 (26/10/2026) — chuyến ghép 2×20': cùng `comboId` = một xe chạy một
   * vòng; `comboLead` giữ km / chi phí / KPI. `comboMembers` chỉ trả ở GET chi
   * tiết (gồm cả chính chuyến này, chuyến dẫn đứng trước).
   */
  comboId?: string | null;
  comboLead?: boolean;
  comboMembers?: TripComboMember[];
  // ─── Nối chuyến (26/10/2026) — 10 cột + khối quan hệ ───
  relayFromTripId?: string | null;
  relayFromStatus?: string | null;
  relayReason?: RelayReasonValue | null;
  /** Chặng kế: mốc "Đã nhận bàn giao". */
  handoverReceivedAt?: string | null;
  /** Chặng cũ: mốc chốt bàn giao (= completedAt). */
  handoverAt?: string | null;
  handoverBy?: "giver" | "receiver" | "office" | null;
  handoverLocation?: string | null;
  handoverLat?: number | null;
  handoverLng?: number | null;
  handoverNote?: string | null;
  relayFrom?: TripRelayFrom | null;
  relayTo?: TripRelayTo[];
  /** Khối nối chuyến — chỉ GET chi tiết; null khi chuyến không nối với ai. */
  relay?: TripRelayInfo | null;
  /** Loại cont của dòng hàng chuyến này chở — API lấy từ orderItem. */
  containerType?: {
    code: string;
    name: string;
    size?: string | null;
    sizeType?: string | null;
  } | null;
  // Thuê ngoài / thầu phụ
  isOutsourced?: boolean;
  subcontractorId?: string | null;
  subVehiclePlate?: string | null;
  subDriverName?: string | null;
  subDriverPhone?: string | null;
  agreedRate?: number | null;
  rateCardId?: string | null;
  serviceConfirmed?: boolean;
  serviceConfirmedAt?: string | null;
  /** Vòng đời thầu phụ — nguồn sự thật; `serviceConfirmed` là trường dẫn xuất */
  subcontractStatus?: SubcontractStatus;
  confirmedEarly?: boolean;
  confirmNote?: string | null;
  subVatRate?: number | null;
  serviceReversedAt?: string | null;
  reverseReason?: string | null;
  apBridgeError?: string | null;
  apBridgeFailedAt?: string | null;
  /** Chuyến có cảnh báo trễ giờ đang mở (chỉ trả trong danh sách khi lọc isOutsourced=true) */
  lateWarning?: boolean;
  /** Cảnh báo chứng chỉ thầu phụ sắp/đã hết hạn (chỉ trả trong danh sách khi lọc isOutsourced=true) */
  certWarning?: {
    type: "expiring" | "expired";
    certType: string;
    expiryDate: string;
  } | null;
  subcontractor?: {
    id: string;
    code: string;
    name: string;
    displayName?: string | null;
  } | null;
  vehicle?: {
    id: string;
    plateNumber: string;
    type?: string;
    vietmapId?: string | null;
  } | null;
  driver?: {
    id: string;
    code: string;
    fullName: string;
    phone?: string;
  } | null;
  trailer?: { id: string; plateNumber: string } | null;
  startedAt?: string | null;
  completedAt?: string | null;
  startKm?: number | null;
  endKm?: number | null;
  distance?: number | null;
  startLat?: number | null;
  startLng?: number | null;
  startAddress?: string | null;
  endLat?: number | null;
  endLng?: number | null;
  endAddress?: string | null;
  currentKm?: number | null;
  currentLat?: number | null;
  currentLng?: number | null;
  currentAddress?: string | null;
  lastOdoSyncAt?: string | null;
  /** Log trả rỗng — hình thức hạ + vị trí VietMap lúc tài xế báo "Đã hạ rỗng". */
  emptyReturnMode?: "YARD" | "TEMP" | "REEXPORT" | null;
  emptyReturnedAt?: string | null;
  emptyReturnLat?: number | null;
  emptyReturnLng?: number | null;
  emptyReturnAddress?: string | null;
  advanceAmount?: number | null;
  costs: TripCostItem[];
  photos?: TripPhotoItem[];
  /** Lịch sử chuyển trạng thái (ActivityLog) — chỉ trả ở GET chi tiết */
  statusHistory?: {
    at: string;
    from?: string | null;
    to?: string | null;
    reason?: string | null;
    by?: string | null;
    /** Nối chuyến — dòng log của lần nhận bàn giao: ai chốt (`receiver` / `office`). */
    handoverReceived?: boolean | null;
    handoverBy?: string | null;
  }[];
  /** Checklist giấy tờ chuyến — chỉ trả ở GET chi tiết */
  docRequirements?: TripDocRequirementItem[];
  /** Tiến độ giấy tờ 2 pha — trả ở GET chi tiết và by-dispatch */
  docProgress?: TripDocProgress | null;
  note?: string;
  createdAt: string;
  /** Tài xế khai tại hiện trường (quy trình v2). */
  sealNo?: string | null;
  pickupCondition?: "OK" | "DAMAGED" | null;
  deliveryCondition?: "OK" | "ISSUE" | null;
  deliveryNote?: string | null;
  pickedQuantity?: number | null;
  /** Mốc tài xế mở chuyến lần đầu (ActivityLog TRIP_VIEWED) — null = chưa xem. */
  viewedAt?: string | null;
  /** Sự cố tài xế đã báo ở chuyến + hồ sơ xử lý (chỉ sự cố nghiêm trọng có) — chỉ GET chi tiết. */
  incidents?: {
    id: string;
    type: string;
    description?: string | null;
    createdAt: string;
    /** Tài xế trả lời "xe còn chạy tiếp được không" — null ở bản ghi cũ. */
    vehicleDrivable?: boolean | null;
    handling: {
      code: string;
      status:
        | "REPORTED"
        | "ACKNOWLEDGED"
        | "IN_PROGRESS"
        | "RESOLVED"
        | "CLOSED";
      resolvedAt?: string | null;
    } | null;
  }[];
  /** Đề xuất bảo trì tài xế đã gửi + phiếu bảo trì sinh ra — chỉ GET chi tiết. */
  maintenanceProposals?: {
    id: string;
    category: string;
    description: string;
    createdAt: string;
    severity?: "WATCH" | "SOON" | "STOP" | null;
    ticket: { code: string | null; status: string } | null;
  }[];
  /** Dòng hàng chuyến chở — my-trips + chi tiết (24/09/2026): seal để đối chiếu tại cổng, loại cont, hàng. */
  orderItem?: DriverOrderItemInfo | null;
  dispatch?: {
    id: string;
    code: string;
    pickupLocation?: string;
    deliveryLocation?: string;
    pickupAddress?: string | null;
    deliveryAddress?: string | null;
    emptyReturnLocation?: string | null;
    isUrgent?: boolean;
    liftOffPayer?: LiftOffPayer | null;
    dispatcher?: { fullName: string; phone?: string | null } | null;
    /** Giờ hẹn lấy / giao và ghi chú lệnh — my-trips + chi tiết. */
    pickupTime?: string | null;
    deliveryTime?: string | null;
    note?: string | null;
    /** GĐ5: luồng của LỆNH — app tài xế phân nhánh quy trình theo đây (lệnh ?? đơn). */
    transportType?: string | null;
    order?: {
      id: string;
      code: string;
      transportType?: string;
      customsType?: string | null;
      emptyReturnLocation?: string | null;
      customer?: { name: string; displayName?: string | null };
    } & DriverOrderInfo;
  };
}
/** Thông tin đơn tài xế cần khi chạy — không có trị giá hàng, giá cước. */
export interface DriverOrderInfo {
  bookingNo?: string | null;
  blNo?: string | null;
  vessel?: string | null;
  voyage?: string | null;
  closingTime?: string | null;
  eta?: string | null;
  shippingLine?: { code: string; name: string } | null;
}
export interface DriverOrderItemInfo {
  sealNo?: string | null;
  cargoName?: string | null;
  weight?: number | null;
  volume?: number | null;
  quantity?: number | null;
  unit?: string | null;
  hsCode?: string | null;
  /** Depot riêng của dòng — ưu tiên trước depot của lệnh/đơn (L3). */
  emptyReturnLocation?: string | null;
  containerType?: { code: string; name: string } | null;
}
export const assignTripsApi = (dispatchId: string, assignments: any[]) =>
  apiFetch<{ count: number; trips: DispatchTripItem[] }>(
    `/api/v1/dispatch-trips/assign/${dispatchId}`,
    { method: "POST", body: JSON.stringify({ assignments }) },
  );
export const addTripApi = (dispatchId: string, d: any) =>
  apiFetch<DispatchTripItem>(`/api/v1/dispatch-trips/add/${dispatchId}`, {
    method: "POST",
    body: JSON.stringify(d),
  });
export const getTripsByDispatchApi = (dispatchId: string) =>
  apiFetch<DispatchTripItem[]>(
    `/api/v1/dispatch-trips/by-dispatch/${dispatchId}`,
  );
export const getMyTripsApi = (status?: string) =>
  apiFetch<DispatchTripItem[]>(
    `/api/v1/dispatch-trips/my-trips${status ? "?status=" + status : ""}`,
  );
export const getTripApi = (id: string) =>
  apiFetch<DispatchTripItem>(`/api/v1/dispatch-trips/${id}`);
/** Tài xế mở chuyến → ghi mốc "đã xem" (idempotent, thay nút "Nhận chuyến"). */
export const markTripViewedApi = (id: string) =>
  apiFetch<{ viewedAt: string | null }>(`/api/v1/dispatch-trips/${id}/viewed`, {
    method: "PUT",
  });
export const syncTripTollsApi = (id: string) =>
  apiFetch<{
    synced: boolean;
    created: number;
    totalCrossings: number;
    totalAmount: number;
    message: string;
  }>(`/api/v1/dispatch-trips/${id}/sync-tolls`, { method: "POST" });
/** Thông tin tài xế khai kèm khi đổi trạng thái (quy trình v2, 24/09/2026). */
export interface TripStatusExtras {
  startKm?: number;
  endKm?: number;
  emptyReturnMode?: string;
  /** Số container cấp ở depot (xuất) — backend kiểm ISO 6346. */
  containerNo?: string;
  /** Seal niêm khi lệnh chưa có số (xuất). */
  sealNo?: string;
  pickupCondition?: "OK" | "DAMAGED";
  deliveryCondition?: "OK" | "ISSUE";
  /** Bắt buộc khi deliveryCondition = ISSUE. */
  deliveryNote?: string;
  pickedQuantity?: number;
  /** Lý do khi nhảy bước (văn phòng nhập thay) — tài xế không được nhảy. */
  reason?: string;
  /** T2: số khai riêng cho các chuyến ghép cùng nhóm (trạng thái tự lan). */
  members?: {
    tripId: string;
    containerNo?: string;
    sealNo?: string;
    pickupCondition?: "OK" | "DAMAGED";
  }[];
}
export const updateTripStatusApi = (
  id: string,
  status: string,
  extras?: TripStatusExtras,
) =>
  apiFetch<DispatchTripItem>(`/api/v1/dispatch-trips/${id}/status`, {
    method: "PUT",
    body: JSON.stringify({ status, ...extras }),
  });

/**
 * Tài xế khai hóa đơn hạ rỗng ở bước "Đã hạ rỗng" (chốt 24/09/2026) —
 * `Idempotency-Key` bắt buộc. `receiptUrl` bắt buộc với MỌI người trả;
 * `amount` chỉ bắt buộc khi DRIVER (tạo chi phí hoàn ứng).
 */
export interface DeclareLiftOffPayload {
  payer: LiftOffPayer;
  amount?: number;
  receiptNo?: string;
  receiptUrl: string;
  note?: string;
}
export const declareLiftOffApi = (
  tripId: string,
  d: DeclareLiftOffPayload,
  idempotencyKey: string,
) =>
  apiFetch<{ payer: LiftOffPayer; photoId: string; costId: string | null }>(
    `/api/v1/dispatch-trips/${tripId}/lift-off`,
    {
      method: "POST",
      headers: { "Idempotency-Key": idempotencyKey },
      body: JSON.stringify(d),
    },
  );
export interface TripPhotoItem {
  id: string;
  kind: string;
  url: string;
  caption?: string | null;
  createdAt: string;
  /** Người tải — so với trip.driver.userId để biết tài xế chụp hay văn phòng tải. */ uploadedById?:
    | string
    | null;
}
/**
 * Điều độ xác nhận / từ chối / hoàn tác khoản chi tài xế ghi (04/11/2026,
 * quyền `dispatch:confirm-cost`). `amount` chỉ gửi khi sửa số tiền — server giữ
 * số tài xế ghi ở `originalAmount`.
 */
export const confirmTripCostApi = (
  costId: string,
  d: { amount?: number; note?: string } = {},
) =>
  apiFetch<TripCostItem>(`/api/v1/dispatch-trips/costs/${costId}/confirm`, {
    method: "PUT",
    body: JSON.stringify(d),
  });
export const rejectTripCostApi = (costId: string, reason: string) =>
  apiFetch<TripCostItem>(`/api/v1/dispatch-trips/costs/${costId}/reject`, {
    method: "PUT",
    body: JSON.stringify({ reason }),
  });
export const unconfirmTripCostApi = (costId: string) =>
  apiFetch<TripCostItem>(`/api/v1/dispatch-trips/costs/${costId}/unconfirm`, {
    method: "PUT",
  });
/** Ghi nhận ảnh bằng chứng cho chuyến (file phải upload qua uploadSingleFileApi trước để lấy url) */
export const addTripPhotoApi = (
  tripId: string,
  d: { kind: string; url: string; caption?: string },
  idempotencyKey: string,
) =>
  apiFetch<TripPhotoItem>(`/api/v1/dispatch-trips/${tripId}/photos`, {
    method: "POST",
    headers: { "Idempotency-Key": idempotencyKey },
    body: JSON.stringify(d),
  });
/** Tài xế báo sự cố trên chuyến. Idempotency-Key chống nhân đôi khi replay offline. */
export const reportTripIncidentApi = (
  tripId: string,
  d: { type: string; description?: string; vehicleDrivable?: boolean },
  idempotencyKey: string,
) =>
  apiFetch<{ id: string }>(`/api/v1/dispatch-trips/${tripId}/incidents`, {
    method: "POST",
    headers: { "Idempotency-Key": idempotencyKey },
    body: JSON.stringify(d),
  });
/** Tài xế đề xuất bảo trì xe trên chuyến — bridge tự tạo ticket bảo trì cho kỹ thuật. */
export const proposeTripMaintenanceApi = (
  tripId: string,
  d: {
    category: string;
    description: string;
    severity?: "WATCH" | "SOON" | "STOP";
    images?: string[];
  },
  idempotencyKey: string,
) =>
  apiFetch<{ id: string }>(
    `/api/v1/dispatch-trips/${tripId}/maintenance-proposals`,
    {
      method: "POST",
      headers: { "Idempotency-Key": idempotencyKey },
      body: JSON.stringify(d),
    },
  );
export const updateTripAdvanceApi = (id: string, advanceAmount: number) =>
  apiFetch<DispatchTripItem>(`/api/v1/dispatch-trips/${id}/advance`, {
    method: "PUT",
    body: JSON.stringify({ advanceAmount }),
  });
export const updateTripApi = (
  id: string,
  data: {
    vehicleId?: string;
    driverId?: string;
    trailerId?: string;
    containerNo?: string;
    note?: string;
    agreedRate?: number;
  },
) =>
  apiFetch<DispatchTripItem>(`/api/v1/dispatch-trips/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
/**
 * DSP-22 (audit 02-dispatch.md) — mất mạng: `m-sw.js` chặn mutation
 * `dispatch-trips*`, trả `202 {queued:true}` ngay thay vì để request thất
 * bại, để tài xế không bị mất input đã gõ. Response đó KHÔNG có nghĩa server
 * đã ghi chi phí — UI phải phân biệt được "đã lưu thật" với "mới xếp hàng
 * đợi mạng" (trước đây cả hai đều rơi vào `onSuccess` và toast "Đã lưu").
 *
 * `Idempotency-Key`: BE hiện CHƯA đọc header này ở `POST :id/costs` (đã grep
 * `apps/api/src/modules/dispatch`, không có xử lý) — client vẫn gửi để khi
 * BE bổ sung, phát lại từ hàng đợi/SW không tạo chi phí trùng; không thay
 * đổi gì ở phía client cho tới lúc đó.
 */
export interface QueuedApiResponse {
  queued: true;
  message?: string;
}
export function isQueuedResponse(v: unknown): v is QueuedApiResponse {
  return (
    !!v && typeof v === "object" && (v as { queued?: unknown }).queued === true
  );
}
export const addTripCostApi = (
  tripId: string,
  d: any,
  idempotencyKey?: string,
) =>
  apiFetch<TripCostItem | QueuedApiResponse>(
    `/api/v1/dispatch-trips/${tripId}/costs`,
    {
      method: "POST",
      headers: idempotencyKey
        ? { "Idempotency-Key": idempotencyKey }
        : undefined,
      body: JSON.stringify(d),
    },
  );
/** Tài xế chụp bổ sung ảnh hoá đơn cho khoản chi đã lưu (url đã upload trước). */
export const setTripCostReceiptApi = (costId: string, receiptUrl: string) =>
  apiFetch<TripCostItem>(`/api/v1/dispatch-trips/costs/${costId}/receipt`, {
    method: "PUT",
    body: JSON.stringify({ receiptUrl }),
  });
export const removeTripCostApi = (costId: string) =>
  apiFetch<{ message: string }>(`/api/v1/dispatch-trips/costs/${costId}`, {
    method: "DELETE",
  });
/**
 * Xác nhận dịch vụ thầu phụ — sinh công nợ AP thật.
 * `confirmNote` BẮT BUỘC khi chuyến chưa nghiệm thu (xác nhận sớm, PUR-010).
 */
export const confirmTripServiceApi = (
  id: string,
  data: {
    agreedRate?: number;
    note?: string;
    confirmNote?: string;
    subVatRate?: number;
  },
) =>
  apiFetch<DispatchTripItem>(`/api/v1/dispatch-trips/${id}/confirm-service`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
/** Hoàn tác xác nhận dịch vụ — đảo AP nháp, cần quyền `dispatch.approve`. */
export const reverseTripServiceApi = (id: string, reason: string) =>
  apiFetch<DispatchTripItem>(`/api/v1/dispatch-trips/${id}/reverse-service`, {
    method: "PUT",
    body: JSON.stringify({ reason }),
  });
/** Hủy chuyến — lý do bắt buộc, cần quyền riêng `dispatch.cancel-trip`. */
/** T2: `cancelGroup` = hủy cả nhóm chuyến ghép; mặc định chuyến còn lại tách thành chuyến đơn. */
export const cancelTripApi = (
  id: string,
  reason: string,
  opts?: { cancelGroup?: boolean },
) =>
  apiFetch<DispatchTripItem>(`/api/v1/dispatch-trips/${id}/cancel`, {
    method: "PUT",
    body: JSON.stringify({ reason, ...(opts?.cancelGroup ? { cancelGroup: true } : {}) }),
  });
/** Thống kê chuyến thuê ngoài — tính ở DB, không cộng client-side. */
export const getOutsourcedTripsStatsApi = () =>
  apiFetch<OutsourcedTripsStats>("/api/v1/dispatch-trips/outsourced-stats");

// ==================== ROUTE PRICES ====================
export interface RoutePriceItem {
  id: string;
  routeId?: string;
  origin: string;
  destination: string;
  containerSize?: string;
  transportType: string;
  pricingMethod: string;
  price: number;
  currency: string;
  validFrom?: string;
  validTo?: string;
  isActive: boolean;
  note?: string;
  createdAt: string;
  /** GĐ4a: giá chuyến theo loại xe — null = không phân biệt loại xe */
  vehicleTypeId?: string | null;
  vehicleType?: {
    id: string;
    code: string;
    name: string;
    payloadTons: number;
  } | null;
}
/** Tham số tra giá — hàng rời gửi `pricingMethod: 'PER_TRIP'` + `vehicleTypeId`. */
export interface RoutePriceLookupParams {
  origin: string;
  destination: string;
  containerSize?: string;
  transportType?: string;
  pricingMethod?: string;
  vehicleTypeId?: string;
  /** Ngày chạy đơn (ISO) — bỏ trống = hôm nay */
  atDate?: string;
}
export interface RoutePriceListResponse {
  data: RoutePriceItem[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}
export const getRoutePricesApi = (p?: Record<string, string>) =>
  apiFetch<RoutePriceListResponse>(
    `/api/v1/route-prices${p ? "?" + new URLSearchParams(p) : ""}`,
  );
export const getRoutePriceApi = (id: string) =>
  apiFetch<RoutePriceItem>(`/api/v1/route-prices/${id}`);
export const lookupRoutePriceApi = (p: RoutePriceLookupParams) => {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(p)) if (v) qs.set(k, v);
  return apiFetch<RoutePriceItem[]>(`/api/v1/route-prices/lookup?${qs}`);
};
export const createRoutePriceApi = (d: any) =>
  apiFetch<RoutePriceItem>("/api/v1/route-prices", {
    method: "POST",
    body: JSON.stringify(d),
  });
export const updateRoutePriceApi = (id: string, d: any) =>
  apiFetch<RoutePriceItem>(`/api/v1/route-prices/${id}`, {
    method: "PUT",
    body: JSON.stringify(d),
  });
export const deleteRoutePriceApi = (id: string) =>
  apiFetch<{ message: string }>(`/api/v1/route-prices/${id}`, {
    method: "DELETE",
  });

// ==================== IMPORT EXCEL ====================
export interface ImportResult {
  total: number;
  success: number;
  failed: number;
  errors: { row: number; message: string }[];
}
export async function importExcelApi(
  module: string,
  file: File,
): Promise<ImportResult> {
  const token = _accessToken;
  const fd = new FormData();
  fd.append("file", file);
  const res = await fetch(`${API_URL}/api/v1/import/${module}`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, ...branchHeader() },
    body: fd,
  });
  if (!res.ok) {
    const e = await res.json().catch(() => ({}));
    throw new Error(e.message || "Import thất bại");
  }
  return res.json();
}
export function downloadTemplateUrl(module: string) {
  return `${API_URL}/api/v1/import/${module}/template`;
}

// ==================== FINANCE ====================
export interface PaymentItem {
  id: string;
  code: string;
  invoiceId: string;
  amount: number;
  method: string;
  paymentDate: string;
  bankAccount?: string;
  referenceNo?: string;
  note?: string;
  createdBy?: string;
  createdAt: string;
}
export interface InvoiceItem {
  id: string;
  code: string;
  type: string;
  status: string;
  customerId?: string;
  supplierId?: string;
  partnerId?: string;
  contactName?: string;
  orderId?: string;
  orderCode?: string;
  subtotal: number;
  vatRate: number;
  vatAmount: number;
  totalAmount: number;
  paidAmount: number;
  remainAmount: number;
  issueDate?: string;
  dueDate?: string;
  vatInvoiceNo?: string;
  description?: string;
  note?: string;
  createdBy?: string;
  createdAt: string;
  /** Có giá trị khi hóa đơn đã đồng bộ sang Fast — khóa sửa tiền/xóa/đảo phiếu thu. */
  fastVoucherNo?: string | null;
  payments?: PaymentItem[];
  _count?: { payments: number };
}
export interface InvoiceListResponse {
  data: InvoiceItem[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}
export interface FinanceStats {
  receivable: { total: number; paid: number; remain: number; overdue: number };
  payable: { total: number; paid: number; remain: number; overdue: number };
}
export const getInvoicesApi = (p?: Record<string, string>) =>
  apiFetch<InvoiceListResponse>(
    `/api/v1/invoices${p ? "?" + new URLSearchParams(p) : ""}`,
  );
export const getInvoiceApi = (id: string) =>
  apiFetch<InvoiceItem>(`/api/v1/invoices/${id}`);
export const createInvoiceApi = (d: any) =>
  apiFetch<InvoiceItem>("/api/v1/invoices", {
    method: "POST",
    body: JSON.stringify(d),
  });
// PUT thường KHÔNG nhận `status`/`orderId` — backend loại bỏ nếu client gửi
// (xem docstring `FinanceService.update`). Đổi trạng thái đi qua
// `updateInvoiceStatusApi` bên dưới, cần quyền `finance:approve`.
export const updateInvoiceApi = (id: string, d: any) =>
  apiFetch<InvoiceItem>(`/api/v1/invoices/${id}`, {
    method: "PUT",
    body: JSON.stringify(d),
  });
// FIN-05: đổi trạng thái hóa đơn — quyền `finance:approve`. Chuyển hợp lệ:
// DRAFT→ISSUED|CANCELLED, ISSUED→CANCELLED. Không đặt PAID tay.
export const updateInvoiceStatusApi = (id: string, status: string) =>
  apiFetch<InvoiceItem>(`/api/v1/invoices/${id}/status`, {
    method: "PUT",
    body: JSON.stringify({ status }),
  });
export const deleteInvoiceApi = (id: string) =>
  apiFetch<{ message: string }>(`/api/v1/invoices/${id}`, { method: "DELETE" });
export const addPaymentApi = (invoiceId: string, d: any) =>
  apiFetch<InvoiceItem>(`/api/v1/invoices/${invoiceId}/payments`, {
    method: "POST",
    body: JSON.stringify(d),
  });
// FIN-12: "đảo" phiếu thu nhập nhầm — quyền `finance:approve`.
export const reversePaymentApi = (invoiceId: string, paymentId: string) =>
  apiFetch<InvoiceItem>(
    `/api/v1/invoices/${invoiceId}/payments/${paymentId}/reverse`,
    { method: "PUT" },
  );
export const getFinanceStatsApi = () =>
  apiFetch<FinanceStats>("/api/v1/invoices/stats");
export const getOverdueInvoicesApi = (p?: Record<string, string>) =>
  apiFetch<InvoiceListResponse>(
    `/api/v1/invoices?type=RECEIVABLE&status=OVERDUE${p ? "&" + new URLSearchParams(p) : ""}`,
  );

// ─── Aging Report ───
export interface AgingBucket {
  range: string;
  label: string;
  count: number;
  amount: number;
  invoices: {
    id: string;
    code: string;
    type: string;
    remainAmount: number;
    totalAmount: number;
    dueDate?: string;
    issueDate?: string;
    contactName?: string;
    orderCode?: string;
    ageDays: number;
  }[];
}
export interface AgingReport {
  buckets: AgingBucket[];
  total: number;
  totalAmount: number;
}
export const getAgingReportApi = (type?: string) =>
  apiFetch<AgingReport>(`/api/v1/invoices/aging${type ? "?type=" + type : ""}`);

// ─── Reconciliation ───
export interface ReconciliationItem {
  entityId: string;
  entityName: string;
  entityType: "customer" | "supplier" | "partner";
  totalAmount: number;
  paidAmount: number;
  remainAmount: number;
  invoiceCount: number;
  overdueCount: number;
  invoices: {
    id: string;
    code: string;
    type: string;
    status: string;
    totalAmount: number;
    paidAmount: number;
    remainAmount: number;
    dueDate?: string;
    orderCode?: string;
  }[];
}
export interface ReconciliationResponse {
  items: ReconciliationItem[];
  summary: {
    totalEntities: number;
    totalAmount: number;
    totalRemain: number;
    totalOverdue: number;
  };
}
export const getReconciliationApi = (params?: Record<string, string>) =>
  apiFetch<ReconciliationResponse>(
    `/api/v1/invoices/reconciliation${params ? "?" + new URLSearchParams(params) : ""}`,
  );

// ─── Advances (Tạm ứng) ───
export interface AdvanceItem {
  id: string;
  code: string;
  requesterId: string;
  requesterName: string;
  orderId?: string;
  orderCode?: string;
  dispatchId?: string;
  dispatchCode?: string;
  amount: number;
  settledAmount?: number;
  returnAmount?: number;
  purpose: string;
  status: "PENDING" | "APPROVED" | "SETTLED" | "REJECTED";
  approvedBy?: string;
  approvedAt?: string;
  rejectionReason?: string;
  settledBy?: string;
  settledAt?: string;
  settleNote?: string;
  settleFileUrl?: string;
  note?: string;
  createdBy?: string;
  createdAt: string;
  updatedAt: string;
}
export interface AdvanceListResponse {
  data: AdvanceItem[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}
export interface AdvanceStats {
  pending: number;
  approved: number;
  settled: number;
  rejected: number;
  totalAdvanced: number;
  totalSettled: number;
  unsettled: number;
}

export const getAdvancesApi = (p?: Record<string, string>) =>
  apiFetch<AdvanceListResponse>(
    `/api/v1/advances${p ? "?" + new URLSearchParams(p) : ""}`,
  );
export const getAdvanceStatsApi = () =>
  apiFetch<AdvanceStats>("/api/v1/advances/stats");
export const getAdvanceApi = (id: string) =>
  apiFetch<AdvanceItem>(`/api/v1/advances/${id}`);
export const createAdvanceApi = (d: any) =>
  apiFetch<AdvanceItem>("/api/v1/advances", {
    method: "POST",
    body: JSON.stringify(d),
  });
export const approveAdvanceApi = (id: string) =>
  apiFetch<AdvanceItem>(`/api/v1/advances/${id}/approve`, { method: "PUT" });
export const rejectAdvanceApi = (id: string, reason: string) =>
  apiFetch<AdvanceItem>(`/api/v1/advances/${id}/reject`, {
    method: "PUT",
    body: JSON.stringify({ reason }),
  });
export const settleAdvanceApi = (
  id: string,
  d: { settledAmount: number; settleNote?: string; settleFileUrl?: string },
) =>
  apiFetch<AdvanceItem>(`/api/v1/advances/${id}/settle`, {
    method: "PUT",
    body: JSON.stringify(d),
  });

// ─── Tải file (PDF / Excel) ───
/**
 * Tải một file nhị phân từ API rồi kích hoạt lưu về máy.
 *
 * KHÔNG dùng `apiFetch` được vì hàm đó `res.json()` — ở đây là blob. Nhưng
 * vẫn phải lặp lại hai thứ của nó, nếu không nút tải hỏng ở những lúc mọi
 * thứ khác vẫn chạy:
 *   • Tự làm mới access token khi 401. Token nằm trong BỘ NHỚ, nên sau khi
 *     F5 hoặc sau ~15 phút là hết; trước đây nút tải chết ngay tại đó trong
 *     khi cả trang vẫn tải dữ liệu bình thường (các API khác đi qua apiFetch
 *     và tự refresh).
 *   • Đọc thông điệp lỗi thật từ body. Trước đây mọi lỗi đều hiện "Không thể
 *     tải PDF" — hết phiên, thiếu quyền hay lỗi sinh file đều một câu như
 *     nhau, không lần ra được nguyên nhân.
 */
export const downloadPdfApi = async (path: string, filename: string) => {
  // Tên lịch sử: hàm này còn tải cả Excel (nhân viên, Debit Note) — KHÔNG
  // kiểm content-type ở đây.
  const res = await fetchFileApi(path);
  saveBlobAs(await res.blob(), filename);
};

/**
 * Lấy PDF về dạng Blob (KHÔNG tự tải) — dùng khi cần xem trước rồi tải đúng
 * bản đó (PDF tổng quan đơn hàng). `asOf` đọc từ header `X-Data-As-Of`.
 */
export const fetchPdfBlobApi = async (
  path: string,
  signal?: AbortSignal,
): Promise<{ blob: Blob; asOf: string | null }> => {
  const res = await fetchFileApi(path, signal);
  // 200 nhưng không phải PDF (proxy trả trang HTML lỗi...) → báo lỗi, không
  // đưa nội dung đó vào khung xem trước / lưu thành file .pdf hỏng.
  const type = res.headers.get("content-type") ?? "";
  if (!type.includes("application/pdf")) {
    throw new ApiError("Máy chủ không trả về file PDF", res.status);
  }
  return { blob: await res.blob(), asOf: res.headers.get("x-data-as-of") };
};

/** Fetch thô một file: Bearer + chi nhánh, refresh khi 401, ném ApiError khi lỗi. */
const fetchFileApi = async (
  path: string,
  signal?: AbortSignal,
): Promise<Response> => {
  const send = (token: string | null) =>
    fetch(`${API_URL}${path}`, {
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...branchHeader(),
      },
      signal,
    });

  let res = await send(getApiAccessToken());

  if (res.status === 401 && typeof window !== "undefined") {
    const refresh = await refreshAccessTokenOnce();
    if (refresh.token) {
      res = await send(refresh.token);
    } else if (refresh.expired) {
      window.dispatchEvent(new CustomEvent("vgt:session-expired"));
      throw new ApiError(
        "Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại.",
        401,
      );
    } else {
      throw new ApiError(
        "Phiên đăng nhập cần được làm mới nhưng chưa thực hiện được. Vui lòng thử lại sau giây lát.",
        401,
      );
    }
  }

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const fallback =
      res.status === 403
        ? "Bạn không có quyền tải chứng từ này"
        : res.status === 404
          ? "Không tìm thấy chứng từ"
          : "Không tạo được file để tải";
    throw new ApiError(readErrorMessage(err, fallback), res.status);
  }

  return res;
};

/** Lưu Blob xuống máy qua thẻ <a download>. */
export const saveBlobAs = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  // Thẻ phải NẰM TRONG DOM và blob URL chỉ được thu hồi SAU khi trình duyệt
  // đọc xong: trước đây click trên thẻ rời rồi revoke ngay dòng sau, Chrome
  // đôi khi thu hồi trước khi kịp đọc — HTTP 200 mà file không hề xuống máy.
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
};
export const downloadDispatchPdfApi = (id: string) =>
  downloadPdfApi(
    `/api/v1/export/dispatch/${id}/pdf`,
    `LenhDieuXe_${id.slice(0, 8)}.pdf`,
  );
export const downloadInvoicePdfApi = (id: string) =>
  downloadPdfApi(
    `/api/v1/export/invoice/${id}/pdf`,
    `HoaDon_${id.slice(0, 8)}.pdf`,
  );
/** PDF tổng quan một đơn hàng — trả Blob để hộp thoại xem trước và tải CÙNG một bản. */
export const fetchOrderOverviewPdfApi = (orderId: string, signal?: AbortSignal) =>
  fetchPdfBlobApi(
    `/api/v1/export/order/${encodeURIComponent(orderId)}/overview-pdf`,
    signal,
  );

// ==================== REPORTS ====================
export interface KpiData {
  ordersThisMonth: number;
  ordersLastMonth: number;
  orderGrowth: number;
  pendingApproval: number;
  awaitingDispatch: number;
  activeDispatches: number;
  // Năm số tiền dưới là `null` khi người xem thiếu quyền Xem giá đơn (28/10/2026).
  revenueThisMonth: number | null;
  revenuePrevMonth: number | null;
  revenueGrowth: number | null;
  arRemain: number | null;
  arOverdue: number | null;
  apRemain: number;
  vehicleCount: number; // tổng xe đang vận hành (đã loại xe thanh lý)
  vehicleActive: number; // xe khả dụng (không nằm xưởng)
  vehicleOnDuty: number; // xe ĐANG chạy chuyến — tử số của tỷ lệ sử dụng
  driverCount: number;
}
export interface RevenueMonth {
  label: string;
  /** `null` khi người xem thiếu quyền Xem giá đơn. */
  revenue: number | null;
  orders: number;
}
export interface OrderByType {
  type: string;
  count: number;
}
export interface TopCustomer {
  customerId: string;
  name: string;
  code: string;
  /** `null` khi người xem thiếu quyền Xem giá đơn. */
  revenue: number | null;
  orders: number;
}
export interface OrderStatusCount {
  status: string;
  count: number;
}
export const getKpisApi = () => apiFetch<KpiData>("/api/v1/reports/kpis");
export const getRevenueChartApi = () =>
  apiFetch<RevenueMonth[]>("/api/v1/reports/revenue-chart");
export const getOrdersByTypeApi = () =>
  apiFetch<OrderByType[]>("/api/v1/reports/orders-by-type");
export const getTopCustomersApi = () =>
  apiFetch<TopCustomer[]>("/api/v1/reports/top-customers");
export const getOrderStatusBreakdownApi = () =>
  apiFetch<OrderStatusCount[]>("/api/v1/reports/order-status-breakdown");

// ==================== FLEET GPS (VietMap) ====================
export interface GpsVehicle {
  Id: number;
  GpsTime: string;
  SysTime: string;
  X: number;
  Y: number; // X=lng, Y=lat
  N?: string; // plate number raw field from VietMap
  Status: number;
  Speed: number;
  Heading: number;
  Distance?: number;
  Address?: string;
  Driver?: string;
  vehicleId?: string;
  plateNumber?: string;
  brand?: string;
  vehicleType?: string;
}
export interface DbVehicleGps {
  id: string;
  plateNumber: string;
  brand?: string;
  type?: string;
  status?: string;
  lastGpsLat?: number;
  lastGpsLng?: number;
  lastGpsSpeed?: number;
  lastGpsTime?: string;
  lastGpsAddress?: string;
  driver?: { fullName: string; phone: string };
}
// Live GPS from VietMap (requires API key configured)
export const getFleetGpsApi = () =>
  apiFetch<GpsVehicle[]>("/api/v1/vietmap/status-all");
// DB GPS cache (from vehicles table lastGps* fields)
export const getDbFleetGpsApi = () =>
  apiFetch<DbVehicleGps[]>("/api/v1/vehicles?limit=200&hasGps=true");

// ==================== BRANCHES API ====================

export interface BranchItem {
  id: string;
  code: string;
  name: string;
  address?: string;
  phone?: string;
  region?: string;
  isActive: boolean;
  deletedAt?: string;
  createdAt: string;
  updatedAt: string;
  _count?: {
    userBranches: number;
    vehicles: number;
    drivers: number;
    customers: number;
    orders: number;
  };
}
export interface BranchListResponse {
  data: BranchItem[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}

export const getBranchesApi = (p?: Record<string, string>) =>
  apiFetch<BranchListResponse>(
    `/api/v1/branches${p ? "?" + new URLSearchParams(p) : ""}`,
  );

export const getBranchApi = (id: string) =>
  apiFetch<BranchItem>(`/api/v1/branches/${id}`);

export const createBranchApi = (d: Partial<BranchItem>) =>
  apiFetch<BranchItem>("/api/v1/branches", {
    method: "POST",
    body: JSON.stringify(d),
  });

export const updateBranchApi = (id: string, d: Partial<BranchItem>) =>
  apiFetch<BranchItem>(`/api/v1/branches/${id}`, {
    method: "PUT",
    body: JSON.stringify(d),
  });

export const deleteBranchApi = (id: string) =>
  apiFetch<{ message: string }>(`/api/v1/branches/${id}`, { method: "DELETE" });

export const getBranchActivitiesApi = (id: string, limit = 50) =>
  apiFetch<any[]>(`/api/v1/branches/${id}/activities?limit=${limit}`);

// Members
export interface BranchMemberItem {
  userId: string;
  fullName: string;
  email: string;
  position?: string;
  isPrimary: boolean;
  isActive: boolean;
  roles: string[];
}
export interface BranchMemberListResponse {
  data: BranchMemberItem[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}

export const getBranchMembersApi = (
  branchId: string,
  p?: Record<string, string>,
) =>
  apiFetch<BranchMemberListResponse>(
    `/api/v1/branches/${branchId}/members${p ? "?" + new URLSearchParams(p) : ""}`,
  );

export const addBranchMemberApi = (
  branchId: string,
  d: { userId: string; isPrimary?: boolean },
) =>
  apiFetch<{ message: string }>(`/api/v1/branches/${branchId}/members`, {
    method: "POST",
    body: JSON.stringify(d),
  });

export const removeBranchMemberApi = (branchId: string, userId: string) =>
  apiFetch<{ message: string }>(
    `/api/v1/branches/${branchId}/members/${userId}`,
    { method: "DELETE" },
  );

export const setBranchMemberPrimaryApi = (branchId: string, userId: string) =>
  apiFetch<{ message: string }>(
    `/api/v1/branches/${branchId}/members/${userId}/primary`,
    { method: "PUT" },
  );

// Branch Statistics
export interface BranchStatistics {
  orders: {
    total: number;
    completed: number;
    cancelled: number;
    inProgress: number;
  };
  revenue: { total: number; completedOnly: number; currency: string };
  fleet: { vehicles: number; drivers: number; trailers: number };
  topCustomers: {
    customerId: string;
    customerName: string;
    customerDisplayName?: string | null;
    orderCount: number;
    totalRevenue: number;
  }[];
  completionRate: number;
  monthlyRevenue: { month: string; revenue: number }[];
}

export const getBranchStatisticsApi = (
  id: string,
  from?: string,
  to?: string,
) => {
  const p = new URLSearchParams();
  if (from) p.set("from", from);
  if (to) p.set("to", to);
  return apiFetch<BranchStatistics>(
    `/api/v1/branches/${id}/statistics${p.toString() ? "?" + p : ""}`,
  );
};

// Branch Transfers
export interface BranchTransferItem {
  id: string;
  entityType: string;
  entityId: string;
  /** Biển số / mã + tên tài xế / mã đơn — null nếu không tra được. */
  entityLabel?: string | null;
  fromBranch?: { id: string; code: string; name: string };
  toBranch: { id: string; code: string; name: string };
  user: { id: string; fullName: string };
  reason?: string;
  createdAt: string;
}
export interface BranchTransferListResponse {
  data: BranchTransferItem[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}

export const getBranchTransfersApi = (p?: Record<string, string>) =>
  apiFetch<BranchTransferListResponse>(
    `/api/v1/branches/transfers${p ? "?" + new URLSearchParams(p) : ""}`,
  );

export const createBranchTransferApi = (d: {
  entityType: string;
  entityId: string;
  fromBranchId: string;
  toBranchId: string;
  reason?: string;
}) =>
  apiFetch<{ message: string }>("/api/v1/branches/transfers", {
    method: "POST",
    body: JSON.stringify(d),
  });

// Regions
export interface RegionItem {
  region: string;
  branches: BranchItem[];
  totals: {
    branches: number;
    members: number;
    vehicles: number;
    drivers: number;
  };
}

export const getBranchRegionsApi = () =>
  apiFetch<RegionItem[]>("/api/v1/branches/regions/list");

// ==================== DISPATCH TRIPS API ====================

export async function getTripsListApi(query: Record<string, any>) {
  const params = new URLSearchParams();
  Object.entries(query).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") {
      params.set(k, Array.isArray(v) ? v.join(",") : String(v));
    }
  });
  return apiFetch<any>(`/api/v1/dispatch-trips?${params.toString()}`);
}

export async function getTripsChipCountsApi() {
  return apiFetch<{
    lateEta: number;
    nearDelivery: number;
    departedToday: number;
    needReconciliation: number;
    pendingCosts: number;
  }>("/api/v1/dispatch-trips/chip-counts");
}

export async function getMyTripsKpiApi() {
  return apiFetch<import("@/types/trip-list").TripMyKpi>(
    "/api/v1/dispatch-trips/my-kpi",
  );
}

export interface TripsStats {
  waitingDeparture: number;
  running: number;
  completedThisMonth: number;
  /** Chặng đã bàn giao (nối chuyến) trong tháng — hiện riêng, không cộng vào hoàn thành. */
  handedOverThisMonth: number;
  /** Badge tab "Đã hoàn thành" — nội bộ, COMPLETED+CANCELLED, mọi thời gian (khớp bảng) */
  completedInternal: number;
  totalKmThisMonth: number;
}

export async function getTripsStatsApi() {
  return apiFetch<TripsStats>("/api/v1/dispatch-trips/stats");
}

export async function getSavedViewsApi() {
  return apiFetch<any[]>("/api/v1/dispatch-trips/saved-views");
}

export async function createSavedViewApi(data: {
  name: string;
  filters: Record<string, any>;
}) {
  return apiFetch<any>("/api/v1/dispatch-trips/saved-views", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function deleteSavedViewApi(id: string) {
  return apiFetch<{ message: string }>(
    `/api/v1/dispatch-trips/saved-views/${id}`,
    { method: "DELETE" },
  );
}

export async function bulkExportTripsApi(ids: string[]) {
  return apiFetch<{
    success: { id: string }[];
    skipped: { id: string; reason: string }[];
  }>("/api/v1/dispatch-trips/bulk-export", {
    method: "POST",
    body: JSON.stringify({ ids }),
  });
}

export async function bulkReassignTripsApi(ids: string[], driverId: string) {
  return apiFetch<{
    success: { id: string }[];
    skipped: { id: string; reason: string }[];
  }>("/api/v1/dispatch-trips/bulk-reassign", {
    method: "POST",
    body: JSON.stringify({ ids, driverId }),
  });
}

export async function bulkReconcileTripsApi(ids: string[]) {
  return apiFetch<{
    success: { id: string }[];
    skipped: { id: string; reason: string }[];
  }>("/api/v1/dispatch-trips/bulk-reconcile", {
    method: "POST",
    body: JSON.stringify({ ids }),
  });
}

/**
 * Cập nhật trạng thái nhiều chuyến — dùng cho chuyến thuê ngoài, nơi tài xế thầu
 * phụ không có tài khoản nên điều phối phải nhập tiến độ thay.
 */
export async function bulkUpdateTripStatusApi(
  ids: string[],
  status: "IN_TRANSIT" | "COMPLETED" | "CANCELLED",
) {
  return apiFetch<{
    success: { id: string; code: string }[];
    skipped: { id: string; code?: string; reason: string }[];
  }>("/api/v1/dispatch-trips/bulk-status", {
    method: "POST",
    body: JSON.stringify({ ids, status }),
  });
}

// ==================== NOTIFICATIONS ====================

/** Mức độ của sự kiện thông báo (catalog phía API). */
export type NotificationSeverity = "INFO" | "WARNING" | "CRITICAL";

export interface NotificationItem {
  id: string;
  userId: string;
  /** Loại cũ (enum) — app /m, /kt lọc theo trường này. */
  type: string;
  /** Mã sự kiện trong catalog của API (vd 'trip.assigned'). */
  event?: string | null;
  /** Nhóm catalog ('operations', 'maintenance', 'finance'…) — server tính sẵn. */
  group?: string | null;
  severity?: NotificationSeverity;
  title: string;
  message: string;
  link?: string | null;
  meta?: Record<string, any> | null;
  isRead: boolean;
  readAt?: string | null;
  createdAt: string;
}

export interface NotificationListResponse {
  data: NotificationItem[];
  total: number;
  page: number;
  limit: number;
}

/** Danh sách loại gửi lên server dạng `A,B` (rỗng/không có = mọi loại). */
function appendTypes(qs: URLSearchParams, types?: readonly string[]) {
  if (types && types.length > 0) qs.set("types", types.join(","));
}

export const getNotificationsApi = (params?: {
  page?: number;
  limit?: number;
  isRead?: boolean;
  /** Chỉ lấy các loại này (lọc phía server). */
  types?: readonly string[];
  /** Chỉ lấy nhóm này của catalog (lọc phía server theo mã sự kiện). */
  group?: string;
}) => {
  const qs = new URLSearchParams();
  if (params?.page) qs.set("page", String(params.page));
  if (params?.limit) qs.set("limit", String(params.limit));
  if (params?.isRead !== undefined) qs.set("isRead", String(params.isRead));
  appendTypes(qs, params?.types);
  if (params?.group) qs.set("group", params.group);
  const suffix = qs.toString() ? `?${qs}` : "";
  return apiFetch<NotificationListResponse>(`/api/v1/notifications${suffix}`);
};

export const getUnreadCountApi = (
  types?: readonly string[],
  group?: string,
) => {
  const qs = new URLSearchParams();
  appendTypes(qs, types);
  if (group) qs.set("group", group);
  const suffix = qs.toString() ? `?${qs}` : "";
  return apiFetch<{ count: number }>(
    `/api/v1/notifications/unread-count${suffix}`,
  );
};

export const markNotificationReadApi = (id: string) =>
  apiFetch<NotificationItem>(`/api/v1/notifications/${id}/read`, {
    method: "POST",
  });

/** `types` rỗng = đánh dấu mọi loại; có `types` = chỉ các loại đó. */
export const markAllNotificationsReadApi = (
  types?: readonly string[],
  group?: string,
) => {
  const qs = new URLSearchParams();
  appendTypes(qs, types);
  if (group) qs.set("group", group);
  const suffix = qs.toString() ? `?${qs}` : "";
  return apiFetch<{ updated: number }>(
    `/api/v1/notifications/mark-all-read${suffix}`,
    { method: "POST" },
  );
};

export const deleteNotificationApi = (id: string) =>
  apiFetch<void>(`/api/v1/notifications/${id}`, { method: "DELETE" });

/** Xoá mọi thông báo ĐÃ ĐỌC của chính mình. */
export const deleteReadNotificationsApi = () =>
  apiFetch<{ deleted: number }>("/api/v1/notifications/read", {
    method: "DELETE",
  });

// ---- Cài đặt thông báo của chính người đăng nhập (`/api/v1/notification-settings`) ----

/** Một loại thông báo mà người dùng này thực sự có thể nhận. */
export interface NotificationCatalogEvent {
  /** Mã sự kiện, vd 'trip.assigned'. */
  event: string;
  label: string;
  /** "Khi nào gửi". */
  description: string;
  severity: NotificationSeverity;
  /** Việc trực tiếp của chính người nhận — không tắt được trong ứng dụng. */
  mandatory: boolean;
  /** `none` = không có kênh email; `digest` = chỉ vào bản tin gộp. */
  emailMode: "immediate" | "digest" | "none";
  inApp: boolean;
  email: boolean;
  defaults: { inApp: boolean; email: boolean };
  /** Người dùng đã có tuỳ chỉnh được lưu cho loại này. */
  customized: boolean;
}

export interface NotificationCatalogGroup {
  key: "operations" | "documents" | "finance" | "maintenance" | "compliance" | "system";
  label: string;
  events: NotificationCatalogEvent[];
}

/** `null` = về mặc định; bỏ field = giữ nguyên. Tối đa 100 phần tử mỗi lần gọi. */
export interface NotificationPreferenceChange {
  event: string;
  inApp?: boolean | null;
  email?: boolean | null;
}

export interface NotificationUserSettings {
  /** Email nhận thông báo (lấy từ hồ sơ). */
  email: string;
  emailEnabled: boolean;
  /** Bản tin hằng ngày — Đợt 3, chưa hiện trên giao diện. */
  digestEnabled: boolean;
  mailConfigured: boolean;
  systemEmailEnabled: boolean;
  /** `null` = email tới được; chuỗi = lý do hiện không gửi được. */
  emailIssue: string | null;
  emailUndeliverableReason: string | null;
}

export interface NotificationTestEmailResult {
  status: "PENDING" | "SENDING" | "SENT" | "FAILED" | "SKIPPED";
  reason: string | null;
}

export const getNotificationCatalogApi = () =>
  apiFetch<NotificationCatalogGroup[]>("/api/v1/notification-settings/catalog");

/** Trả lại catalog mới sau khi lưu. 400 nếu tắt sự kiện bắt buộc hoặc bật email cho loại không có email. */
export const updateNotificationPreferencesApi = (
  items: NotificationPreferenceChange[],
) =>
  apiFetch<NotificationCatalogGroup[]>(
    "/api/v1/notification-settings/preferences",
    { method: "PUT", body: JSON.stringify({ items }) },
  );

/** Khôi phục mặc định cho mọi loại thông báo; trả lại catalog mới. */
export const resetNotificationPreferencesApi = () =>
  apiFetch<NotificationCatalogGroup[]>(
    "/api/v1/notification-settings/preferences",
    { method: "DELETE" },
  );

export const getNotificationUserSettingsApi = () =>
  apiFetch<NotificationUserSettings>("/api/v1/notification-settings");

export const updateNotificationUserSettingsApi = (data: {
  emailEnabled?: boolean;
  digestEnabled?: boolean;
}) =>
  apiFetch<NotificationUserSettings>("/api/v1/notification-settings", {
    method: "PUT",
    body: JSON.stringify(data),
  });

/** Gửi email thử tới CHÍNH địa chỉ của mình. 429 khi quá 3 lần / 10 phút. */
export const sendNotificationTestEmailApi = () =>
  apiFetch<NotificationTestEmailResult>(
    "/api/v1/notification-settings/test-email",
    { method: "POST" },
  );

// ==================== DOCUMENTS (Chứng từ XNK) ====================

export interface ShippingDocumentItem {
  id: string;
  documentSetId: string;
  category: string;
  name: string;
  documentNo?: string;
  issueDate?: string;
  expiryDate?: string;
  issuer?: string;
  fileUrl?: string;
  fileName?: string;
  fileSize?: number;
  note?: string;
  uploadedBy?: string;
  createdAt: string;
  updatedAt: string;
  /** Chứng từ thuộc riêng một lệnh điều xe; null = chung cho cả đơn. */
  dispatchId?: string | null;
  dispatch?: { id: string; code: string } | null;
  /** Có giá trị = tự sinh từ ảnh tài xế chụp (EIR / biên bản giao hàng). */
  tripPhotoId?: string | null;
  /**
   * Tài xế xem được chứng từ này trên app. Chỉ có nghĩa với loại NGOÀI khung
   * bốn chứng từ vận chuyển — B/L, D/O, EIR, biên bản luôn tới tay tài xế qua
   * checklist chuyến và luôn mang `false`.
   */
  shareWithDriver?: boolean;
  /** 'IN' nhận · 'OUT' trả rỗng · null không áp dụng (chỉ có nghĩa với category EIR). */
  eirDirection?: "IN" | "OUT" | null;
  /** 'TEMPLATE' bản mềm · 'SIGNED' đã ký · null không áp dụng (chỉ có nghĩa với DELIVERY_RECORD). */
  recordStage?: "TEMPLATE" | "SIGNED" | null;
  /** true = đang bị đánh dấu cần nộp lại (ảnh mờ, thiếu trang…). */
  needsReupload?: boolean;
  reuploadNote?: string | null;
  reuploadAt?: string | null;
  currentVersionId?: string | null;
}

export interface DocumentSetItem {
  id: string;
  code: string;
  title: string;
  description?: string;
  note?: string;
  status: "DRAFT" | "IN_PROGRESS" | "COMPLETE";
  orderId?: string;
  order?: {
    id: string;
    code: string;
    bookingNo?: string | null;
    blNo?: string | null;
    vessel?: string | null;
    voyage?: string | null;
    customer?: {
      id: string;
      name: string;
      shortName?: string;
      displayName?: string | null;
    };
  };
  /** Có giá trị = bộ RIÊNG của lệnh điều xe; null/thiếu = bộ chung của đơn. */
  dispatchId?: string | null;
  dispatch?: { id: string; code: string; status: string } | null;
  bookingNo?: string;
  blNo?: string;
  vessel?: string;
  voyage?: string;
  documents?: ShippingDocumentItem[];
  _count?: { documents: number };
  /** Tiến độ checklist chứng từ của đơn gắn bộ này; null nếu bộ mồ côi. */
  docProgress?: DocRequirementProgress | null;
  createdBy?: string;
  createdAt: string;
  updatedAt: string;
}

export interface DocumentSetListResponse {
  data: DocumentSetItem[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}

export interface DocumentSetStats {
  total: number;
  draft: number;
  inProgress: number;
  complete: number;
  /** Số bộ mà đơn còn dòng checklist bắt buộc chưa nhận — khớp filter missing=true. */
  missingDocs: number;
}

export const getDocumentSetsApi = (params?: Record<string, string>) =>
  apiFetch<DocumentSetListResponse>(
    `/api/v1/documents${params ? "?" + new URLSearchParams(params) : ""}`,
  );

export const getDocumentSetStatsApi = () =>
  apiFetch<DocumentSetStats>("/api/v1/documents/stats");

export const getDocumentSetApi = (id: string) =>
  apiFetch<DocumentSetItem>(`/api/v1/documents/${id}`);

export const createDocumentSetApi = (data: Partial<DocumentSetItem>) =>
  apiFetch<DocumentSetItem>("/api/v1/documents", {
    method: "POST",
    body: JSON.stringify(data),
  });

export const updateDocumentSetApi = (
  id: string,
  data: Partial<DocumentSetItem>,
) =>
  apiFetch<DocumentSetItem>(`/api/v1/documents/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });

export const updateDocumentSetStatusApi = (id: string, status: string) =>
  apiFetch<DocumentSetItem>(`/api/v1/documents/${id}/status`, {
    method: "PUT",
    body: JSON.stringify({ status }),
  });

export const deleteDocumentSetApi = (id: string) =>
  apiFetch<{ message: string }>(`/api/v1/documents/${id}`, {
    method: "DELETE",
  });

// Shipping Document (file in set) CRUD
/** Metadata gửi kèm file trong endpoint upload gộp (multipart). */
export interface UploadShippingDocumentMeta {
  category?: string;
  name?: string;
  documentNo?: string;
  issueDate?: string;
  issuer?: string;
  note?: string;
  /**
   * Cho tài xế xem chứng từ này trên app. Chỉ có tác dụng với loại NGOÀI khung
   * bốn chứng từ vận chuyển — backend ép về false cho B/L, D/O, EIR, biên bản.
   */
  shareWithDriver?: boolean;
  /**
   * Chiều phiếu EIR: 'IN' nhận · 'OUT' trả rỗng. Chỉ có nghĩa khi category =
   * EIR; bỏ trống → backend mặc định 'IN'.
   */
  eirDirection?: "IN" | "OUT";
  /**
   * Giai đoạn biên bản giao hàng: 'TEMPLATE' bản mềm · 'SIGNED' đã ký. Chỉ có
   * nghĩa khi category = DELIVERY_RECORD; bỏ trống → backend mặc định 'TEMPLATE'.
   */
  recordStage?: "TEMPLATE" | "SIGNED";
}

async function postShippingDocumentForm(
  path: string,
  file: File,
  meta: UploadShippingDocumentMeta & { scope?: string },
): Promise<ShippingDocumentItem> {
  const token = _accessToken;
  const formData = new FormData();
  formData.append("file", file);
  // `v == null` chứ KHÔNG phải `if (v)`: `false` là giá trị hợp lệ của
  // shareWithDriver, dùng truthy check sẽ nuốt nó. Boolean đổi sang 'true'/
  // 'false' vì multipart chỉ truyền được chuỗi — DTO bên API có @Transform để
  // đọc ngược lại.
  Object.entries(meta).forEach(([k, v]) => {
    if (v == null || v === "") return;
    formData.append(k, typeof v === "boolean" ? String(v) : v);
  });
  const res = await fetch(`${API_URL}${path}`, {
    method: "POST",
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...branchHeader(),
    },
    body: formData, // KHÔNG set Content-Type — browser tự thêm boundary
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ message: "Upload thất bại" }));
    throw new Error(err.message || "Upload thất bại");
  }
  return res.json();
}

/** Upload gộp 1 call vào bộ chứng từ: file + metadata → R2 + bản ghi + auto-tick checklist */
export const uploadShippingDocumentFileApi = (
  setId: string,
  file: File,
  meta: UploadShippingDocumentMeta = {},
) =>
  postShippingDocumentForm(
    `/api/v1/documents/${setId}/files/upload`,
    file,
    meta,
  );

/** Upload theo đơn hàng — backend tự tìm/tạo bộ chứng từ của đơn */
export const uploadOrderShippingDocumentApi = (
  orderId: string,
  file: File,
  meta: UploadShippingDocumentMeta = {},
) =>
  postShippingDocumentForm(
    `/api/v1/documents/orders/${orderId}/files/upload`,
    file,
    meta,
  );

export const addShippingDocumentApi = (
  setId: string,
  data: Partial<ShippingDocumentItem>,
) =>
  apiFetch<ShippingDocumentItem>(`/api/v1/documents/${setId}/files`, {
    method: "POST",
    body: JSON.stringify(data),
  });

export const updateShippingDocumentApi = (
  setId: string,
  docId: string,
  data: Partial<ShippingDocumentItem>,
) =>
  apiFetch<ShippingDocumentItem>(`/api/v1/documents/${setId}/files/${docId}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });

/** Xoá mềm chứng từ — bắt buộc nêu lý do (≤500 ký tự), lưu vào nhật ký. */
export const deleteShippingDocumentApi = (
  setId: string,
  docId: string,
  reason: string,
) =>
  apiFetch<{ message: string }>(`/api/v1/documents/${setId}/files/${docId}`, {
    method: "DELETE",
    body: JSON.stringify({ reason }),
  });

// ── Vòng đời FILE của chứng từ: thay · nộp lại · lịch sử · khôi phục · cờ ──

/** Nguồn gốc của một phiên bản file. */
export type ShippingDocVersionSource =
  | "UPLOAD"
  | "TRIP_PHOTO"
  | "COPIED_FROM_SHARED"
  | "MERGED_FROM_DISPATCH"
  | "RESTORED";

export interface ShippingDocVersionItem {
  id: string;
  version: number;
  fileUrl: string;
  fileName: string | null;
  fileSize: number | null;
  source: ShippingDocVersionSource;
  sourceNote: string | null;
  uploadedByName: string | null;
  createdAt: string;
  isCurrent: boolean;
}

export interface ShippingDocVersionsResponse {
  documentId: string;
  name: string;
  versions: ShippingDocVersionItem[];
}

async function putFileFormReplace(
  path: string,
  file: File,
  note?: string,
): Promise<ShippingDocumentItem> {
  const token = getApiAccessToken();
  const formData = new FormData();
  formData.append("file", file);
  if (note?.trim()) formData.append("note", note.trim());
  const res = await fetch(`${API_URL}${path}`, {
    method: "POST",
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...branchHeader(),
    },
    body: formData, // KHÔNG set Content-Type — browser tự thêm boundary
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new ApiError(readErrorMessage(err, "Thay file thất bại"), res.status);
  }
  return res.json();
}

/** Thay file của chứng từ — bản cũ tự thành phiên bản, không bị ghi đè. Quyền documents.update. */
export const replaceShippingDocumentFileApi = (
  setId: string,
  docId: string,
  file: File,
  note?: string,
) =>
  putFileFormReplace(
    `/api/v1/documents/${setId}/files/${docId}/replace`,
    file,
    note,
  );

/**
 * Nộp bản mới cho chứng từ đang bị đánh dấu "cần nộp lại" — chỉ cần quyền
 * documents.create, nhưng backend từ chối (403) nếu chứng từ không đang bị cờ.
 */
export const reuploadShippingDocumentFileApi = (
  setId: string,
  docId: string,
  file: File,
  note?: string,
) =>
  putFileFormReplace(
    `/api/v1/documents/${setId}/files/${docId}/reupload`,
    file,
    note,
  );

export const listShippingDocumentVersionsApi = (setId: string, docId: string) =>
  apiFetch<ShippingDocVersionsResponse>(
    `/api/v1/documents/${setId}/files/${docId}/versions`,
  );

/** Đưa một phiên bản cũ về làm bản hiện hành (tạo phiên bản mới). Quyền update. */
export const restoreShippingDocumentVersionApi = (
  setId: string,
  docId: string,
  versionId: string,
) =>
  apiFetch<ShippingDocumentItem>(
    `/api/v1/documents/${setId}/files/${docId}/versions/${versionId}/restore`,
    { method: "POST" },
  );

/** Đánh dấu / gỡ dấu "cần nộp lại". Quyền documents.create. */
export const flagShippingDocumentReuploadApi = (
  setId: string,
  docId: string,
  flagged: boolean,
  note?: string,
) =>
  apiFetch<ShippingDocumentItem>(
    `/api/v1/documents/${setId}/files/${docId}/reupload-flag`,
    {
      method: "PUT",
      body: JSON.stringify({ flagged, note: note?.trim() || undefined }),
    },
  );

/** Nhật ký thao tác trên bộ chứng từ — 100 dòng mới nhất. */
export interface DocumentSetActivityItem {
  id: string;
  action: string;
  details: Record<string, unknown> | null;
  createdAt: string;
  user: { id: string; fullName: string } | null;
}

export const getDocumentSetActivityApi = (setId: string) =>
  apiFetch<DocumentSetActivityItem[]>(`/api/v1/documents/${setId}/activity`);

/**
 * Một lượt XEM/TẢI file. Khác `DocumentSetActivityItem` (thao tác ghi) ở chỗ có
 * `ipAddress` và `details.reason` — lý do người đó được phép đọc.
 */
export interface FileReadLogItem {
  id: string;
  action: string;
  details: {
    fileName?: string;
    fileModule?: string;
    reason?: string;
    download?: boolean;
  } | null;
  ipAddress: string | null;
  createdAt: string;
  user: { id: string; fullName: string } | null;
}

export const getDocumentSetFileReadsApi = (setId: string) =>
  apiFetch<FileReadLogItem[]>(`/api/v1/documents/${setId}/file-reads`);

// ============ CHỨNG TỪ THEO LỆNH ĐIỀU XE (D/O · EIR · biên bản) ============

/** Ba chứng từ bắt buộc của một lệnh. Nhãn tiếng Việt lấy từ DOC_CATEGORY_LABEL. */
/** Thứ tự = trình tự nghiệp vụ: nhận B/L → đổi D/O → lấy cont → giao hàng. B/L không bao giờ bắt buộc. */
/**
 * Khung chứng từ vận chuyển. Giữ khớp tay với `DISPATCH_DOC_SLOT_KINDS` bên
 * `apps/api/src/modules/documents/dispatch-doc-slots.util.ts` (chốt 23/09/2026:
 * tách EIR/biên bản theo chiều — không còn slot 'EIR' gộp chung).
 */
export type DispatchDocSlotKind =
  | "BOOKING"
  | "BL"
  | "DO"
  | "EIR_IN"
  | "DELIVERY_TEMPLATE"
  | "DELIVERY_RECORD"
  | "EIR_OUT"
  | "LIFT_OFF_RECEIPT"
  | "PORT_LIFT_RECEIPT";

/**
 * Nhãn tiếng Việt — khớp `DISPATCH_DOC_SLOT_LABEL` bên API. Chỉ dùng làm tài
 * liệu đối chiếu tay (không import ở đâu khác trong frontend) — UI luôn hiện
 * `slot.label` do server trả, đã đổi theo chiều hàng (`slotPresentationFor`).
 * EIR_OUT chiều nhập/nội địa: "EIR trả rỗng (phiếu hạ rỗng)"; chiều xuất:
 * "EIR hạ cảng".
 */
export const DISPATCH_DOC_SLOT_LABEL: Record<DispatchDocSlotKind, string> = {
  BOOKING: "Booking / lệnh cấp rỗng",
  BL: "Vận đơn (B/L)",
  DO: "Lệnh giao hàng",
  EIR_IN: "EIR nhận",
  DELIVERY_TEMPLATE: "Biên bản giao hàng",
  DELIVERY_RECORD: "Biên bản giao hàng ký nhận",
  EIR_OUT: "EIR trả rỗng (phiếu hạ rỗng)",
  LIFT_OFF_RECEIPT: "Hóa đơn hạ rỗng",
  PORT_LIFT_RECEIPT: "Phiếu nâng hạ cảng",
};

/** Người trả phí hạ rỗng — `Dispatch.liftOffPayer`. */
export type LiftOffPayer = "DRIVER" | "OFFICE" | "NONE";

/** 'own' = bộ riêng của lệnh · 'shared' = bộ chung của đơn · 'none' = chưa có. */
export type DispatchDocSource = "own" | "shared" | "none";

export interface DispatchDocFile {
  id: string;
  documentSetId: string;
  name: string;
  fileName: string | null;
  fileUrl: string;
  fileSize: number | null;
  documentNo: string | null;
  issueDate: string | null;
  note: string | null;
  uploadedBy: string | null;
  createdAt: string;
  /**
   * null = tờ dùng chung cho cả lô hàng (mọi lệnh thấy); có giá trị = người nộp
   * đã chọn gán riêng lệnh đó.
   */
  dispatchId?: string | null;
  /** Có giá trị = chứng từ tự sinh từ ảnh tài xế chụp trên chuyến `tripCode`. */
  tripPhotoId: string | null;
  tripCode: string | null;
  /**
   * Chiều của phiếu EIR: 'IN' nhận vỏ · 'OUT' trả vỏ · null chưa rõ.
   * Một chuyến sinh hai phiếu; tranh chấp phí sửa vỏ cần phân biệt được.
   */
  eirDirection?: "IN" | "OUT" | null;
  /** 'TEMPLATE' bản mềm · 'SIGNED' đã ký · null không áp dụng (không phải biên bản giao hàng). */
  recordStage?: "TEMPLATE" | "SIGNED" | null;
  /** true = đang bị đánh dấu cần nộp lại (ảnh mờ, thiếu trang…). */
  needsReupload: boolean;
  reuploadNote: string | null;
  reuploadAt: string | null;
}

/** Trạng thái dòng checklist tương ứng ở một chuyến của lệnh. */
export interface DispatchDocSlotTrip {
  tripId: string;
  tripCode: string;
  tripStatus: string;
  containerNo: string | null;
  /** Tên tài xế của chuyến (null = chuyến thuê ngoài / chưa gán). */
  driverName?: string | null;
  requirementId: string | null;
  status: DocumentRequirementStatus | null;
  required: boolean | null;
  /** 'system' = hệ thống tick theo chứng từ văn phòng · userId = tick tay. */
  receivedBy: string | null;
  matchedPhotoId: string | null;
}

export interface DispatchDocSlot {
  kind: DispatchDocSlotKind;
  /** Nhãn theo loại hàng — chiều xuất: "EIR lấy rỗng" / "EIR hạ cảng". */
  label: string;
  required: boolean;
  /** Ô có thuộc quy trình của loại hàng không (`slotPresentationFor` bên API). Thiếu = coi như có. */
  applicable?: boolean;
  /** Ai xử lý: văn phòng nộp hay tài xế chụp trong chuyến. */
  owner?: "OFFICE" | "DRIVER";
  source: DispatchDocSource;
  satisfied: boolean;
  /**
   * true = có bản nhưng TẤT CẢ đều đang bị đánh dấu "cần nộp lại" — quyết
   * định nghiệp vụ 25/09/2026: trường hợp này `satisfied` đã là `false` (cờ =
   * thiếu), field này chỉ để UI phân biệt "chưa có gì" với "có nhưng phải nộp
   * lại". Optional — client cũ (chưa deploy backend mới) không có field vẫn
   * chạy đúng nhờ `slotFlagged()` soi `documents[].needsReupload`.
   */
  flagged?: boolean;
  documents: DispatchDocFile[];
  trips: DispatchDocSlotTrip[];
}

export interface DispatchDocSetRef {
  id: string;
  code: string;
  title: string | null;
  status: string | null;
}

/**
 * Khung chứng từ hạt CHUYẾN (`trip-doc-slots.util.ts` bên API) — 9 phần tử,
 * cùng tập phần tử với `DispatchDocSlotKind` (hạt LỆNH) nhưng luật bắt buộc khác.
 */
export type TripDocSlotKind =
  | "BOOKING"
  | "BL"
  | "DO"
  | "EIR_IN"
  | "DELIVERY_TEMPLATE"
  | "DELIVERY_RECORD"
  | "EIR_OUT"
  | "LIFT_OFF_RECEIPT"
  | "PORT_LIFT_RECEIPT";

export type TripDocCellStatus =
  | "ok"
  | "missing"
  | "flagged"
  | "classify"
  | "pending"
  | "na";

export type TripSlotLevel = "trip" | "dispatch" | "order" | "none";

export interface TripDocWaivedInfo {
  reason: string | null;
  waivedBy: string | null;
  waivedAt: string | null;
}

/** Hình dạng `DocFile` trong `trips[].cells[].effectiveDoc` — xem `toDocFile` bên API. */
export interface TripDocFile {
  id: string;
  documentSetId: string;
  dispatchId: string | null;
  name: string;
  fileName: string | null;
  fileUrl: string;
  fileSize: number | null;
  documentNo: string | null;
  issueDate: string | null;
  note: string | null;
  uploadedBy: string | null;
  needsReupload: boolean;
  reuploadNote: string | null;
  reuploadAt: string | null;
  createdAt: string;
  tripPhotoId: string | null;
  tripCode: string | null;
  eirDirection?: "IN" | "OUT" | null;
  recordStage?: "TEMPLATE" | "SIGNED" | null;
}

export interface TripDocCell {
  kind: TripDocSlotKind;
  label: string;
  applicable: boolean;
  required: boolean;
  status: TripDocCellStatus;
  level: TripSlotLevel;
  late: boolean;
  waived: TripDocWaivedInfo | null;
  effectiveDoc: TripDocFile | null;
  overridden: TripDocFile[];
  suggestion?: { direction: "IN" | "OUT"; reason: string } | null;
}

/** Một chuyến trong `trips[]` — hạt CHUYẾN đúng luật đè §3 (chung-tu.md 13.1). */
/** Khoá `tripId` giả của hàng chờ (dòng hàng chưa có chuyến) — khớp API. */
export const PENDING_ITEM_ROW_PREFIX = "item:";

export interface DispatchDocTripRow {
  /** Hàng chờ: `item:<orderItemId>` — không gọi được route theo chuyến. */
  tripId: string;
  tripCode: string;
  tripStatus: string;
  containerNo: string | null;
  driverName: string | null;
  cells: TripDocCell[];
  orderItemId?: string | null;
  /** Dòng hàng của lệnh CHƯA có chuyến — chỉ nộp/xem chứng từ được. */
  pendingItem?: boolean;
}

export interface DispatchDocumentsView {
  dispatch: {
    id: string;
    code: string;
    status: string;
    orderId: string;
    orderCode: string | null;
    liftOffPayer?: LiftOffPayer | null;
  };
  ownSet: DispatchDocSetRef | null;
  sharedSet: DispatchDocSetRef | null;
  effectiveSetId: string | null;
  mode: DispatchDocSource;
  slots: DispatchDocSlot[];
  missing: DispatchDocSlotKind[];
  missingLabel: string;
  requiredCount: number;
  satisfiedCount: number;
  complete: boolean;
  progress: { required: number; satisfied: number; percent: number };
  /** Hạt CHUYẾN đúng luật đè đầy đủ (26/09/2026) — dùng cho UI mới, xem chung-tu.md 13.1. */
  columns: Array<{ kind: TripDocSlotKind; label: string; applicable: boolean }>;
  trips: DispatchDocTripRow[];
}

/** Một lệnh trong toàn cảnh chứng từ của đơn. */
/** Chứng từ chỉ-chuyến nằm nhầm ở cấp lệnh (dữ liệu cũ) — hộp thoại "Gắn container". */
export interface OrderDocsUnattachedDoc {
  id: string;
  kind: TripDocSlotKind;
  label: string;
  fileName: string | null;
  fileUrl: string;
  createdAt: string;
}

export interface OrderDocsDispatchEntry extends Omit<
  DispatchDocumentsView,
  "dispatch"
> {
  dispatch: {
    id: string;
    code: string;
    status: string;
    tripCount: number;
    /** Ngày giao sớm nhất trong các dòng hàng lệnh này gom. */
    deliveryDate: string | null;
    containers: string[];
    liftOffPayer?: LiftOffPayer | null;
    liftOffNote?: string | null;
  };
  unattachedTripOnlyDocsCount?: number;
  unattachedTripOnlyDocs?: OrderDocsUnattachedDoc[];
}

export interface OrderDocumentsOverview {
  order: {
    id: string;
    code: string;
    status: string;
    customsRequired: boolean;
    customsType: string | null;
  };
  /**
   * Chứng từ cấp LÔ HÀNG (BOOKING, B/L, D/O) tính cho cả đơn: gộp bản ở bộ
   * chung và bản gán riêng từng lệnh. Có cả khi đơn chưa có lệnh nào.
   */
  orderSlots: DispatchDocSlot[] | null;
  /** Bộ chung của đơn — luôn tồn tại (chứng từ cấp đơn nằm ở đây). */
  sharedSet: DispatchDocSetRef | null;
  dispatches: OrderDocsDispatchEntry[];
  summary: {
    /** Số BỘ chứng từ vận chuyển — đếm theo định danh, KHÔNG theo số lệnh dùng. */
    setCount: number;
    setsComplete: number;
    setsIncomplete: number;
    /** Mẫu số: lệnh chưa hủy. */
    dispatchTotal: number;
    dispatchCovered: number;
    /** Luôn hiển thị riêng — không gộp vào tỷ lệ phần trăm. */
    dispatchNoSet: number;
    dispatchCount: number;
    dispatchesComplete: number;
    missingTotal: number;
    requiredTotal: number;
    satisfiedTotal: number;
  };
  otherDocuments: import("@/types/order").OrderDocument[];
}

export const getOrderDocumentsOverviewApi = (orderId: string) =>
  apiFetch<OrderDocumentsOverview>(
    `/api/v1/orders/${orderId}/documents-overview`,
  );

export const getDispatchDocumentsApi = (dispatchId: string) =>
  apiFetch<DispatchDocumentsView>(`/api/v1/documents/dispatches/${dispatchId}`);

/** Tách bộ chứng từ riêng cho lệnh — chứng từ đã gắn lệnh chuyển sang bộ mới. */
export const createDispatchDocumentSetApi = (dispatchId: string) =>
  apiFetch<{ id: string; code: string; movedCount: number }>(
    `/api/v1/documents/dispatches/${dispatchId}/set`,
    { method: "POST" },
  );

// ── Đổi chế độ chứng từ của đơn ──
export interface DocModeSetSummary {
  id: string;
  code: string;
  dispatchId: string | null;
  dispatchCode: string | null;
  /** Bộ sẽ được tạo khi đổi — chưa có mã thật. */
  isNew: boolean;
  fileCount: number;
  counts: Record<"BL" | "DO" | "EIR" | "DELIVERY_RECORD" | "OTHER", number>;
}

export interface DocModeConflict {
  category: "BL" | "DO";
  candidates: Array<{
    documentId: string;
    fileUrl: string;
    fileName: string | null;
    fileSize: number | null;
    fromDispatchCode: string | null;
    fromSetCode: string | null;
    uploadedByName: string | null;
    createdAt: string;
  }>;
}

/** Nộp chứng từ cho một lệnh. */
export const uploadDispatchDocumentApi = (
  dispatchId: string,
  file: File,
  meta: UploadShippingDocumentMeta = {},
) =>
  postShippingDocumentForm(
    `/api/v1/documents/dispatches/${dispatchId}/files/upload`,
    file,
    meta,
  );

/**
 * Nộp chứng từ cho một hoặc nhiều chuyến của CÙNG một lệnh — B7
 * (`POST /api/v1/documents/trips/upload`, chung-tu.md 13.1). Một file, nhiều
 * chuyến = một lần tải lên, nhiều bản ghi trỏ chung file. Header
 * `Idempotency-Key` bắt buộc — gọi lại cùng key trả nguyên kết quả cũ, không
 * tạo object/bản ghi thứ hai (chống bấm hai lần / mạng chập chờn).
 */
export const uploadTripsDocumentApi = (
  dispatchId: string,
  tripIds: string[],
  file: File,
  meta: UploadShippingDocumentMeta,
  idempotencyKey: string,
): Promise<ShippingDocumentItem[]> => {
  const token = _accessToken;
  const formData = new FormData();
  formData.append("file", file);
  formData.append("dispatchId", dispatchId);
  // Hàng chờ của dòng hàng chưa có chuyến mang khoá `item:<orderItemId>`.
  const realTripIds = tripIds.filter((id) => !id.startsWith(PENDING_ITEM_ROW_PREFIX));
  const orderItemIds = tripIds
    .filter((id) => id.startsWith(PENDING_ITEM_ROW_PREFIX))
    .map((id) => id.slice(PENDING_ITEM_ROW_PREFIX.length));
  if (realTripIds.length) formData.append("tripIds", JSON.stringify(realTripIds));
  if (orderItemIds.length) formData.append("orderItemIds", JSON.stringify(orderItemIds));
  Object.entries(meta).forEach(([k, v]) => {
    if (v == null || v === "") return;
    formData.append(k, typeof v === "boolean" ? String(v) : v);
  });
  return fetch(`${API_URL}/api/v1/documents/trips/upload`, {
    method: "POST",
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...branchHeader(),
      "Idempotency-Key": idempotencyKey,
    },
    body: formData,
  }).then(async (res) => {
    if (!res.ok) {
      const err = await res
        .json()
        .catch(() => ({ message: "Upload thất bại" }));
      throw new Error(err.message || "Upload thất bại");
    }
    return res.json();
  });
};

/** Một bộ chứng từ của đơn (chung hoặc riêng theo lệnh) — dòng gọn cho trang theo đơn. */
export interface OrderDocSetSummary {
  id: string;
  code: string;
  title: string | null;
  status: string;
  dispatchId: string | null;
  bookingNo: string | null;
  blNo: string | null;
  vessel: string | null;
  voyage: string | null;
  note: string | null;
  updatedAt: string;
}

/**
 * Toàn bộ chứng từ của MỘT đơn — nguồn cho `/dashboard/documents/order/:orderId`.
 * Khớp `DispatchDocumentsService.orderDocumentsPage` (apps/api).
 */
export interface OrderDocumentsPage {
  order: {
    id: string;
    code: string;
    status: string;
    customsRequired: boolean;
    customsType: string | null;
    transportType: string | null;
    customer: {
      id: string;
      name: string;
      shortName?: string | null;
      displayName?: string | null;
    } | null;
    creator?: { id: string; fullName: string } | null;
    bookingNo: string | null;
    blNo: string | null;
    vessel: string | null;
    voyage: string | null;
  };
  sharedSet: DispatchDocSetRef | null;
  orderSlots: DispatchDocSlot[] | null;
  dispatches: OrderDocsDispatchEntry[];
  summary: OrderDocumentsOverview["summary"];
  sets: OrderDocSetSummary[];
  /** Tệp của bộ chung KHÔNG thuộc 4 ô vận chuyển (hồ sơ hải quan, tài liệu khác). */
  sharedDocuments: ShippingDocumentItem[];
  /** Ảnh tài xế CHƯA thành chứng từ — ảnh đã thành chứng từ nằm trong các ô. */
  otherDocuments: OrderOtherDocument[];
  /** Dòng container/hàng của đơn — hiện khi đơn chưa có lệnh. */
  orderItems: OrderDocsPageItem[];
  /**
   * Tờ khai hải quan của đơn + tệp của tờ khai (15/10/2026): file tờ khai
   * thông quan (bản chính + file nối thêm) và biên lai nộp thuế. Nguồn là
   * FileUpload module `customs`, không phải ShippingDocument — chỉ xem/tải ở
   * trang này, nộp ở trang tờ khai.
   */
  customsDeclarations?: OrderDocsDeclaration[];
}

export interface OrderDocsDeclarationFile {
  id: string;
  fileName: string;
  url: string;
  mimeType: string | null;
  fileSize: number | null;
  createdAt: string;
}

export interface OrderDocsDeclaration {
  id: string;
  code: string | null;
  declNo: string | null;
  declType: string | null;
  status: string;
  /** Bản chính của file tờ khai thông quan (điều kiện CLEARED). */
  fileId: string | null;
  clearedDocuments: OrderDocsDeclarationFile[];
  taxReceipts: OrderDocsDeclarationFile[];
}

export interface OrderDocsPageItem {
  id: string;
  seq: number;
  containerNo: string | null;
  containerTypeCode: string | null;
  cargoName: string | null;
  dispatchStatus: string;
}

export interface OrderOtherDocument {
  id: string;
  source: "trip-photo";
  name: string;
  category: string;
  fileUrl: string;
  fileName: string | null;
  fileSize: number | null;
  createdAt: string;
  tripId: string;
  tripCode: string;
}

export const getOrderDocumentsPageApi = (orderId: string) =>
  apiFetch<OrderDocumentsPage>(`/api/v1/documents/orders/${orderId}/page`);

// ==================== DOCUMENT REQUIREMENTS (checklist chứng từ theo đơn) ====================

export type DocumentRequirementStatus = "PENDING" | "RECEIVED" | "WAIVED";

/** Tiến độ checklist chứng từ của một đơn — backend tính, FE chỉ hiển thị. */
export interface DocRequirementProgress {
  total: number;
  requiredTotal: number;
  received: number;
  waived: number;
  /** received / (required - waived), 0..100 */
  percent: number;
  /** category các dòng bắt buộc còn thiếu */
  missing: string[];
}

export interface DocumentRequirementItem {
  id: string;
  category: string;
  name: string;
  required: boolean;
  status: DocumentRequirementStatus;
  matchedDocumentId?: string | null;
  matchedDocument?: {
    id: string;
    name: string;
    fileUrl?: string | null;
    fileName?: string | null;
  } | null;
  receivedAt?: string | null;
  receivedBy?: string | null;
  dueDate?: string | null;
  assigneeId?: string | null;
  assignee?: { id: string; fullName: string } | null;
  note?: string | null;
  sortOrder: number;
}

export interface OrderRequirementsResponse {
  data: DocumentRequirementItem[];
  progress: DocRequirementProgress;
  /** Bộ chứng từ đầu tiên của đơn — nơi upload file checklist. */
  documentSet?: { id: string; code: string; title: string } | null;
}

export const getOrderRequirementsApi = (orderId: string) =>
  apiFetch<OrderRequirementsResponse>(
    `/api/v1/documents/orders/${orderId}/requirements`,
  );

export const addOrderRequirementApi = (
  orderId: string,
  data: {
    category: string;
    name: string;
    required?: boolean;
    dueDate?: string;
    assigneeId?: string;
    note?: string;
  },
) =>
  apiFetch<DocumentRequirementItem>(
    `/api/v1/documents/orders/${orderId}/requirements`,
    {
      method: "POST",
      body: JSON.stringify(data),
    },
  );

export const updateRequirementApi = (
  id: string,
  data: Partial<
    Pick<
      DocumentRequirementItem,
      "name" | "required" | "dueDate" | "assigneeId" | "note" | "sortOrder"
    >
  >,
) =>
  apiFetch<DocumentRequirementItem>(`/api/v1/documents/requirements/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });

export const updateRequirementStatusApi = (
  id: string,
  status: DocumentRequirementStatus,
) =>
  apiFetch<DocumentRequirementItem>(
    `/api/v1/documents/requirements/${id}/status`,
    {
      method: "PUT",
      body: JSON.stringify({ status }),
    },
  );

export const deleteRequirementApi = (id: string) =>
  apiFetch<{ message: string }>(`/api/v1/documents/requirements/${id}`, {
    method: "DELETE",
  });

// ==================== PROJECTS (Jobs — hàng dự án) ====================

export type JobType = "PRJ" | "OOG" | "HL" | "MM" | "PLI" | "INS";

export interface JobMilestoneItem {
  id: string;
  seq: number;
  name: string;
  plannedDate?: string | null;
  actualDate?: string | null;
  evidenceKey?: string | null;
  isBillingTrigger: boolean;
  status: string;
  statusTrigger?: string | null;
}
export interface JobHistoryItem {
  id: string;
  fromStatus: string;
  toStatus: string;
  actorId?: string | null;
  actorName?: string | null;
  note?: string | null;
  createdAt: string;
}
export interface JobDocItem {
  id: string;
  docKind: string;
  name: string;
  fileKey?: string | null;
  fileName?: string | null;
  createdAt: string;
}
export interface JobCostItem {
  id: string;
  category: string;
  name: string;
  amount: number;
  supplierId?: string | null;
  fileKey?: string | null;
  fileName?: string | null;
  note?: string | null;
  createdAt: string;
}
export interface JobItem {
  id: string;
  code: string;
  type: JobType;
  status: string;
  title: string;
  description?: string | null;
  customerId: string;
  branchId: string;
  pmId: string;
  contractId?: string | null;
  createdAt: string;
  updatedAt: string;
  customer?: {
    id: string;
    code: string;
    name: string;
    displayName?: string | null;
  };
  branch?: { id: string; code: string; name: string };
  _count?: { orders: number; milestones: number };
  pmName?: string | null;
  milestonesDone?: number;
  milestones?: JobMilestoneItem[];
  history?: JobHistoryItem[];
  orders?: JobOrderItem[];
  costs?: JobCostItem[];
  nextStatuses?: string[];
  gateRequirements?: JobGateRequirement[];
  steps?: JobJourneyStep[];
  /** -1 khi status nằm ngoài đường chính (ngoại lệ / đã hủy). */
  currentStepIndex?: number;
  totalSteps?: number;
  settlePmConfirmedAt?: string | null;
  settleAccountantConfirmedAt?: string | null;
  settleLockedAt?: string | null;
  plRevenue?: number | null;
  plCost?: number | null;
  plProfit?: number | null;
  plClosedAt?: string | null;
}
export interface JobGateRequirement {
  status: string;
  requiredDocs: { kind: string; has: boolean }[];
  missing: string[];
  /** Vai trò được phép chuyển sang trạng thái này ([] = không giới hạn). */
  allowedRoles: string[];
  /** Người đang đăng nhập có vai trò hợp lệ cho bước này không. */
  canAct: boolean;
}
/** Một bước trên quy trình của loại đơn (metadata tĩnh, không gắn Job cụ thể). */
export interface JobWorkflowStep {
  status: string;
  requiredDocs: string[];
  allowedRoles: string[];
}
/** Bước trên quy trình của MỘT Job — kèm tiến độ thực tế. */
export interface JobJourneyStep extends JobWorkflowStep {
  reached: boolean;
  current: boolean;
  missingDocs: string[];
}
export interface JobWorkflowDefinition {
  type: JobType;
  steps: JobWorkflowStep[];
}
export interface JobChangeRequestItem {
  id: string;
  jobId: string;
  kind: "CHANGE_ORDER" | "INCIDENT";
  status: "PENDING" | "APPROVED" | "REJECTED";
  title: string;
  description?: string | null;
  costImpact?: number | null;
  requestedBy?: string | null;
  requestedByName?: string | null;
  approvedBy?: string | null;
  approvedAt?: string | null;
  rejectReason?: string | null;
  createdAt: string;
}
export interface JobStats {
  total: number;
  byStatus: Record<string, number>;
  byType: Record<string, number>;
}
export interface JobOrderItem {
  id: string;
  code: string;
  status: string;
  grandTotal: number;
  paidAmount: number;
  origin?: string | null;
  destination?: string | null;
  createdAt: string;
}
export interface JobListResponse {
  data: JobItem[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}

export const getJobsApi = (p?: Record<string, string>) =>
  apiFetch<JobListResponse>(
    `/api/v1/projects/jobs${p ? "?" + new URLSearchParams(p) : ""}`,
  );
export const getJobStatsApi = (p?: Record<string, string>) =>
  apiFetch<JobStats>(
    `/api/v1/projects/jobs/stats${p ? "?" + new URLSearchParams(p) : ""}`,
  );
export const getJobApi = (id: string) =>
  apiFetch<JobItem>(`/api/v1/projects/jobs/${id}`);
/** Quy trình của một loại đơn — FE dựng stepper từ đây, không hardcode chuỗi bước. */
export const getJobWorkflowApi = (type: JobType) =>
  apiFetch<JobWorkflowDefinition>(`/api/v1/projects/jobs/workflows/${type}`);
export const createJobApi = (d: {
  type: JobType;
  title: string;
  customerId: string;
  branchId: string;
  pmId: string;
  contractId?: string;
  description?: string;
}) =>
  apiFetch<JobItem>("/api/v1/projects/jobs", {
    method: "POST",
    body: JSON.stringify(d),
  });
export const updateJobApi = (
  id: string,
  d: Partial<{
    title: string;
    pmId: string;
    contractId: string;
    description: string;
  }>,
) =>
  apiFetch<JobItem>(`/api/v1/projects/jobs/${id}`, {
    method: "PUT",
    body: JSON.stringify(d),
  });
export const deleteJobApi = (id: string) =>
  apiFetch<{ message: string }>(`/api/v1/projects/jobs/${id}`, {
    method: "DELETE",
  });
export const transitionJobApi = (
  id: string,
  targetStatus: string,
  note?: string,
  /** Chỉ dùng khi sang EXCEPTION_CONTROLLED — tạo sự cố cùng transaction. */
  incident?: { title: string; description?: string; costImpact?: number },
) =>
  apiFetch<JobItem>(`/api/v1/projects/jobs/${id}/transition`, {
    method: "POST",
    body: JSON.stringify({ targetStatus, note, incident }),
  });
export const updateJobMilestoneApi = (
  id: string,
  milestoneId: string,
  d: Partial<{
    plannedDate: string;
    actualDate: string;
    evidenceKey: string;
    status: string;
  }>,
) =>
  apiFetch<JobMilestoneItem>(
    `/api/v1/projects/jobs/${id}/milestones/${milestoneId}`,
    { method: "PUT", body: JSON.stringify(d) },
  );
export const listJobDocsApi = (id: string) =>
  apiFetch<JobDocItem[]>(`/api/v1/projects/jobs/${id}/documents`);
export const attachJobDocApi = (
  id: string,
  d: { kind: string; fileKey?: string; fileName?: string; name?: string },
) =>
  apiFetch<JobDocItem>(`/api/v1/projects/jobs/${id}/documents`, {
    method: "POST",
    body: JSON.stringify(d),
  });
export const createJobChangeRequestApi = (
  id: string,
  d: {
    kind?: string;
    title: string;
    description?: string;
    costImpact?: number;
  },
) =>
  apiFetch<JobChangeRequestItem>(
    `/api/v1/projects/jobs/${id}/change-requests`,
    { method: "POST", body: JSON.stringify(d) },
  );
export const listJobChangeRequestsApi = (id: string) =>
  apiFetch<JobChangeRequestItem[]>(
    `/api/v1/projects/jobs/${id}/change-requests`,
  );
export const approveJobChangeRequestApi = (crId: string) =>
  apiFetch<JobChangeRequestItem>(
    `/api/v1/projects/jobs/change-requests/${crId}/approve`,
    { method: "POST" },
  );
export const rejectJobChangeRequestApi = (crId: string, reason: string) =>
  apiFetch<JobChangeRequestItem>(
    `/api/v1/projects/jobs/change-requests/${crId}/reject`,
    { method: "POST", body: JSON.stringify({ reason }) },
  );
export const confirmJobSettlementApi = (id: string, by: "PM" | "ACCOUNTANT") =>
  apiFetch<any>(`/api/v1/projects/jobs/${id}/settlement/confirm`, {
    method: "POST",
    body: JSON.stringify({ by }),
  });
export const closeJobApi = (id: string) =>
  apiFetch<any>(`/api/v1/projects/jobs/${id}/settlement/close`, {
    method: "POST",
  });

// Chi phí cấp dự án (JobCost)
export const listJobCostsApi = (id: string) =>
  apiFetch<JobCostItem[]>(`/api/v1/projects/jobs/${id}/costs`);
export const addJobCostApi = (
  id: string,
  d: {
    category?: string;
    name: string;
    amount: number;
    supplierId?: string;
    fileKey?: string;
    fileName?: string;
    note?: string;
  },
) =>
  apiFetch<JobCostItem>(`/api/v1/projects/jobs/${id}/costs`, {
    method: "POST",
    body: JSON.stringify(d),
  });
export const removeJobCostApi = (id: string, costId: string) =>
  apiFetch<{ message: string }>(`/api/v1/projects/jobs/${id}/costs/${costId}`, {
    method: "DELETE",
  });

// Tạo hóa đơn AR nháp từ milestone billing-trigger đã DONE
export const createMilestoneInvoiceApi = (jobId: string, milestoneId: string) =>
  apiFetch<{ id: string; code: string }>(
    `/api/v1/projects/jobs/${jobId}/milestones/${milestoneId}/invoice`,
    { method: "POST" },
  );

// Gắn/gỡ Order vào Job (dùng chung updateOrderApi với field jobId)
export const attachOrderToJobApi = (orderId: string, jobId: string) =>
  apiFetch<OrderItem>(`/api/v1/orders/${orderId}`, {
    method: "PUT",
    body: JSON.stringify({ jobId }),
  });
export const detachOrderFromJobApi = (orderId: string) =>
  apiFetch<OrderItem>(`/api/v1/orders/${orderId}`, {
    method: "PUT",
    body: JSON.stringify({ jobId: null }),
  });

// Options: Job đang mở của 1 khách hàng (cho dropdown gắn Job ở form đơn hàng)
export const getJobOptionsApi = (customerId: string) =>
  apiFetch<JobListResponse>(
    `/api/v1/projects/jobs?customerId=${customerId}&limit=100`,
  );

// ==================== HR (Hợp đồng LĐ / Bảo hiểm / Trạng thái làm việc) ====================

export interface LaborContractItem {
  id: string;
  code: string;
  userId: string;
  branchId: string;
  type: string;
  effectiveDate: string;
  expiryDate?: string | null;
  status: string;
  note?: string | null;
  createdAt: string;
  user?: {
    id: string;
    fullName: string;
    email?: string | null;
    employeeCode?: string | null;
  };
  branch?: { id: string; code: string; name: string };
}
export interface InsuranceItem {
  id: string;
  userId: string;
  branchId?: string | null;
  kind: string;
  policyNo?: string | null;
  status: string;
  validFrom?: string | null;
  validTo?: string | null;
  note?: string | null;
  createdAt: string;
  user?: {
    id: string;
    fullName: string;
    email?: string | null;
    employeeCode?: string | null;
  };
  branch?: { id: string; code: string; name: string } | null;
}
export interface HrListResponse<T> {
  data: T[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}

// ─── Labor contracts ───
export const getLaborContractsApi = (p?: Record<string, string>) =>
  apiFetch<HrListResponse<LaborContractItem>>(
    `/api/v1/hr/contracts${p ? "?" + new URLSearchParams(p) : ""}`,
  );
export const getLaborContractAlertsApi = (days = 30) =>
  apiFetch<LaborContractItem[]>(`/api/v1/hr/contracts/alerts?days=${days}`);
export const createLaborContractApi = (d: {
  userId: string;
  branchId: string;
  type: string;
  effectiveDate: string;
  expiryDate?: string;
  note?: string;
}) =>
  apiFetch<LaborContractItem>("/api/v1/hr/contracts", {
    method: "POST",
    body: JSON.stringify(d),
  });
export const updateLaborContractApi = (
  id: string,
  d: Partial<{
    type: string;
    effectiveDate: string;
    expiryDate: string;
    status: string;
    note: string;
  }>,
) =>
  apiFetch<LaborContractItem>(`/api/v1/hr/contracts/${id}`, {
    method: "PUT",
    body: JSON.stringify(d),
  });
export const deleteLaborContractApi = (id: string) =>
  apiFetch<{ message: string }>(`/api/v1/hr/contracts/${id}`, {
    method: "DELETE",
  });

// ─── Insurance ───
export const getInsurancesApi = (p?: Record<string, string>) =>
  apiFetch<HrListResponse<InsuranceItem>>(
    `/api/v1/hr/insurances${p ? "?" + new URLSearchParams(p) : ""}`,
  );
export const getInsuranceAlertsApi = (days = 30) =>
  apiFetch<InsuranceItem[]>(`/api/v1/hr/insurances/alerts?days=${days}`);
export const createInsuranceApi = (d: {
  userId: string;
  branchId?: string;
  kind: string;
  policyNo?: string;
  status?: string;
  validFrom?: string;
  validTo?: string;
  note?: string;
}) =>
  apiFetch<InsuranceItem>("/api/v1/hr/insurances", {
    method: "POST",
    body: JSON.stringify(d),
  });
export const updateInsuranceApi = (
  id: string,
  d: Partial<{
    kind: string;
    policyNo: string;
    status: string;
    branchId: string;
    validFrom: string;
    validTo: string;
    note: string;
  }>,
) =>
  apiFetch<InsuranceItem>(`/api/v1/hr/insurances/${id}`, {
    method: "PUT",
    body: JSON.stringify(d),
  });
export const deleteInsuranceApi = (id: string) =>
  apiFetch<{ message: string }>(`/api/v1/hr/insurances/${id}`, {
    method: "DELETE",
  });

// ─── Work status ───
// FE-13 (audit 07-frontend.md): `getWorkStatusApi` gọi `GET /hr/work-status/:userId`
// — backend chỉ có `PUT` (đổi trạng thái) và `POST reconcile`, không có GET.
// Hàm này chưa từng được gọi ở đâu trong repo (code chết) — đã gỡ.
// setWorkStatusApi: xem HR PRESENCE section (signature object arg đầy đủ hơn).

// ==================== YARDS (Kho bãi) ====================

export interface YardItem {
  id: string;
  code: string;
  name: string;
  branchId: string;
  capacity: number;
  lat?: number | null;
  lng?: number | null;
  allowedVehicleType?: string | null;
  status: string;
  note?: string | null;
  createdAt: string;
  branch?: { id: string; code: string; name: string };
  _count?: { occupancy: number };
  occupancy?: {
    id: string;
    entityType: string;
    entityId: string;
    entityCode?: string | null;
    inAt: string;
    note?: string | null;
  }[];
}
export interface YardListResponse {
  data: YardItem[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}
export interface YardInventoryRow {
  yard: {
    id: string;
    code: string;
    name: string;
    capacity: number;
    status: string;
  };
  current: number;
  available: number;
  utilizationPct: number;
  staleCount: number;
  staleVehicles: {
    id: string;
    entityType: string;
    entityCode?: string | null;
    inAt: string;
  }[];
}

export const getYardsApi = (p?: Record<string, string>) =>
  apiFetch<YardListResponse>(
    `/api/v1/yards${p ? "?" + new URLSearchParams(p) : ""}`,
  );
export const getYardApi = (id: string) =>
  apiFetch<YardItem>(`/api/v1/yards/${id}`);
export const getYardInventoryApi = (staleDays = 7) =>
  apiFetch<YardInventoryRow[]>(
    `/api/v1/yards/inventory?staleDays=${staleDays}`,
  );
export const createYardApi = (d: {
  name: string;
  branchId: string;
  capacity: number;
  lat?: number;
  lng?: number;
  allowedVehicleType?: string;
  status?: string;
  note?: string;
}) =>
  apiFetch<YardItem>("/api/v1/yards", {
    method: "POST",
    body: JSON.stringify(d),
  });
export const updateYardApi = (
  id: string,
  d: Partial<{
    name: string;
    capacity: number;
    lat: number;
    lng: number;
    allowedVehicleType: string;
    status: string;
    note: string;
  }>,
) =>
  apiFetch<YardItem>(`/api/v1/yards/${id}`, {
    method: "PUT",
    body: JSON.stringify(d),
  });
export const deleteYardApi = (id: string) =>
  apiFetch<{ message: string }>(`/api/v1/yards/${id}`, { method: "DELETE" });
export const checkInYardApi = (
  id: string,
  d: { entityType: string; entityId: string; note?: string },
) =>
  apiFetch<any>(`/api/v1/yards/${id}/check-in`, {
    method: "POST",
    body: JSON.stringify(d),
  });
export const checkOutYardApi = (id: string, occupancyId: string) =>
  apiFetch<{ message: string }>(
    `/api/v1/yards/${id}/check-out/${occupancyId}`,
    { method: "PUT" },
  );
export const transferYardApi = (
  id: string,
  occupancyId: string,
  d: { toYardId: string; note?: string },
) =>
  apiFetch<{ message: string }>(`/api/v1/yards/${id}/transfer/${occupancyId}`, {
    method: "POST",
    body: JSON.stringify(d),
  });

// ==================== CONTAINERS ADVANCED (D&D + Hải quan) ====================

export interface ContainerDnDItem {
  id: string;
  orderItemId: string;
  kind: "DETENTION" | "DEMURRAGE";
  freeDays: number;
  gateDate: string;
  dueDate: string;
  overdueDays: number;
  ratePerDay: number;
  amount: number;
  currency: string;
  orderCostId?: string | null;
  disbursementId?: string | null;
  note?: string | null;
  createdAt: string;
  updatedAt: string;
  orderItem?: {
    id: string;
    containerNo?: string | null;
    sealNo?: string | null;
    order?: { id: string; code: string; branchId?: string | null };
  };
}
export type DeclarationStatus =
  | "DRAFT"
  | "IN_PROGRESS"
  | "CLEARED"
  | "CANCELLED";
export interface CustomsStepItem {
  id: string;
  orderIndex: number;
  key: string;
  label: string;
  skipped: boolean;
  completedAt?: string | null;
  completedBy?: string | null;
  note?: string | null;
}
export interface CustomsDeclarationItem {
  id: string;
  orderId: string;
  code?: string | null;
  declNo?: string | null;
  channel?: "GREEN" | "YELLOW" | "RED" | null;
  declType?: string | null;
  status: DeclarationStatus;
  clearedAt?: string | null;
  clearedBy?: string | null;
  assigneeId?: string | null;
  note?: string | null;
  taxImport?: string | number | null;
  taxVat?: string | number | null;
  feeCustoms?: string | number | null;
  // Fields mới
  taxPaidDate?: string | null;
  taxReceiptNo?: string | null;
  registrationDate?: string | null;
  receiptNo?: string | null;
  fileId?: string | null;
  cancelReason?: string | null;
  cancelledAt?: string | null;
  cancelledBy?: string | null;
  syncBackStatus?: string | null;
  syncBackError?: string | null;
  steps?: CustomsStepItem[];
  file?: {
    id: string;
    url: string;
    fileName: string;
    fileSize: number;
    mimeType: string;
    createdAt?: string;
  } | null;
  /** Tiến độ checklist chứng từ của đơn — null khi đơn chưa có checklist. */
  docProgress?: DocRequirementProgress | null;
  /** Tờ khai của đơn gấp còn phải làm (Nháp / Đang xử lý) — API danh sách tính, 22/10/2026. */
  urgentPinned?: boolean;
  createdAt: string;
  updatedAt: string;
  /**
   * Thông tin đơn hàng kèm theo. Danh sách chỉ trả nhóm field cơ bản; chi tiết
   * (GET /customs/:id) trả thêm toàn bộ dữ liệu lô hàng để người khai lên tờ
   * khai mà không cần quyền `orders:read`.
   */
  order?: CustomsOrderContext;
}
/**
 * Ngữ cảnh đơn hàng cho một tờ khai. Các field ngoài nhóm cơ bản chỉ có ở
 * endpoint chi tiết — ở danh sách chúng là `undefined`, không phải `null`.
 */
export interface CustomsOrderContext {
  id: string;
  code: string;
  branchId?: string | null;
  isUrgent?: boolean;
  urgentNote?: string | null;
  transportType?: string | null;
  customsType?: string | null;
  hsCode?: string | null;
  // ── Chỉ có ở chi tiết ──
  incoterm?: string | null;
  invoiceNo?: string | null;
  invoiceDate?: string | null;
  /** Decimal của Prisma → serialize thành chuỗi. Luôn Number() trước khi tính. */
  invoiceValue?: string | number | null;
  invoiceCurrency?: string | null;
  shipperName?: string | null;
  consigneeName?: string | null;
  countryOfOrigin?: string | null;
  customsRequired?: boolean;
  customsNo?: string | null;
  customsDate?: string | null;
  customsStatus?: string | null;
  customsFee?: number | null;
  customsNote?: string | null;
  bookingNo?: string | null;
  blNo?: string | null;
  vessel?: string | null;
  voyage?: string | null;
  eta?: string | null;
  etd?: string | null;
  closingTime?: string | null;
  shippingLine?: { id: string; code: string; name: string } | null;
  origin?: string | null;
  destination?: string | null;
  pickupDate?: string | null;
  deliveryDate?: string | null;
  emptyReturnLocation?: string | null;
  customer?: {
    id: string;
    code: string;
    name: string;
    displayName?: string | null;
    taxCode?: string | null;
  } | null;
  refNos?: { type: "BOOKING" | "BL"; value: string; seq: number }[];
  items?: CustomsOrderGoodsItem[];
  documentSets?: CustomsDocumentSet[];
}
export interface CustomsOrderGoodsItem {
  id: string;
  seq: number;
  containerNo?: string | null;
  sealNo?: string | null;
  hsCode?: string | null;
  cargoName?: string | null;
  weight?: number | null;
  volume?: number | null;
  quantity?: number | null;
  unit?: string | null;
  containerType?: { id: string; code: string; name: string } | null;
  cargoType?: { id: string; code: string; name: string } | null;
}
export type CustomsDocCategory =
  | "BL"
  | "CO"
  | "CI"
  | "PL"
  | "CUSTOMS"
  | "INSURANCE"
  | "FUMIGATION"
  | "QUARANTINE"
  | "DO"
  | "EIR"
  | "DELIVERY_RECORD"
  | "OTHER";
export interface CustomsDocumentSet {
  id: string;
  code: string;
  status: string;
  title: string;
  documents: CustomsShippingDocument[];
}
export interface CustomsShippingDocument {
  id: string;
  category: CustomsDocCategory;
  name: string;
  documentNo?: string | null;
  issueDate?: string | null;
  expiryDate?: string | null;
  issuer?: string | null;
  fileUrl?: string | null;
  fileName?: string | null;
  fileSize?: number | null;
}
export interface CustomsOrderItem {
  id: string;
  code: string;
  customsStatus?: string | null;
  customsType?: string | null;
  customsNo?: string | null;
  customsDate?: string | null;
  hsCode?: string | null;
  _count?: { customsDeclarations: number };
}
export interface CustomsStats {
  DRAFT: number;
  IN_PROGRESS: number;
  CLEARED: number;
  CANCELLED: number;
  total: number;
  /** Tờ khai CÒN PHẢI LÀM (DRAFT/IN_PROGRESS) thuộc đơn gấp. */
  urgent: number;
}
export interface DnDListResponse {
  data: ContainerDnDItem[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}
export interface CustomsListResponse {
  data: CustomsDeclarationItem[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}
export interface CustomsOrderListResponse {
  data: CustomsOrderItem[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}

// ─── D&D ───
export const getDnDApi = (p?: Record<string, string>) =>
  apiFetch<DnDListResponse>(
    `/api/v1/containers-advanced/dnd${p ? "?" + new URLSearchParams(p) : ""}`,
  );
export const getDnDAlertsApi = (days = 3) =>
  apiFetch<ContainerDnDItem[]>(
    `/api/v1/containers-advanced/dnd/alerts?days=${days}`,
  );
export const createDnDApi = (d: {
  orderItemId: string;
  kind: string;
  gateDate: string;
  freeDays?: number;
  ratePerDay: number;
  note?: string;
}) =>
  apiFetch<ContainerDnDItem>("/api/v1/containers-advanced/dnd", {
    method: "POST",
    body: JSON.stringify(d),
  });
export const updateDnDApi = (
  id: string,
  d: Partial<{
    gateDate: string;
    freeDays: number;
    ratePerDay: number;
    note: string;
  }>,
) =>
  apiFetch<ContainerDnDItem>(`/api/v1/containers-advanced/dnd/${id}`, {
    method: "PUT",
    body: JSON.stringify(d),
  });
export const linkDnDCostApi = (
  id: string,
  d?: { paidBy?: "CUSTOMER" | "COMPANY" },
) =>
  apiFetch<ContainerDnDItem>(
    `/api/v1/containers-advanced/dnd/${id}/link-cost`,
    { method: "POST", body: JSON.stringify(d ?? {}) },
  );
/** Tạo phiếu chi hộ (trả cảng/hãng tàu) từ bản ghi D&D — cần quyền disbursements.create. */
export const createDisbursementFromDnDApi = (
  dndId: string,
  d: { supplierId?: string; note?: string },
) =>
  apiFetch<DisbursementItem>(`/api/v1/disbursements/from-dnd/${dndId}`, {
    method: "POST",
    body: JSON.stringify(d),
  });
export const deleteDnDApi = (id: string) =>
  apiFetch<{ message: string }>(`/api/v1/containers-advanced/dnd/${id}`, {
    method: "DELETE",
  });

// ─── Customs ───
export const getCustomsApi = (p?: Record<string, string>) =>
  apiFetch<CustomsListResponse>(
    `/api/v1/containers-advanced/customs${p ? "?" + new URLSearchParams(p) : ""}`,
  );
export const createCustomsApi = (d: {
  orderId: string;
  declNo: string;
  channel?: string;
  declType?: string;
  clearedAt?: string;
  assigneeId?: string;
  note?: string;
}) =>
  apiFetch<CustomsDeclarationItem>("/api/v1/containers-advanced/customs", {
    method: "POST",
    body: JSON.stringify(d),
  });
export const updateCustomsApi = (
  id: string,
  d: Partial<{
    declNo: string;
    channel: string;
    declType: string;
    clearedAt: string;
    assigneeId: string;
    note: string;
    registrationDate: string;
    receiptNo: string;
  }>,
) =>
  apiFetch<CustomsDeclarationItem>(
    `/api/v1/containers-advanced/customs/${id}`,
    { method: "PUT", body: JSON.stringify(d) },
  );
export const deleteCustomsApi = (id: string) =>
  apiFetch<{ message: string }>(`/api/v1/containers-advanced/customs/${id}`, {
    method: "DELETE",
  });

/**
 * Người khai hải quan tự bổ sung dữ liệu lô hàng. Ghi ngược lên Order nhưng
 * đứng dưới quyền `customs.update` — vai trò khai hải quan không có
 * `orders.update`, và API chỉ nhận đúng nhóm trường dưới đây.
 */
export interface UpdateShipmentInfoPayload {
  shipperName?: string | null;
  consigneeName?: string | null;
  invoiceNo?: string | null;
  invoiceDate?: string | null;
  invoiceValue?: number | null;
  invoiceCurrency?: string | null;
  incoterm?: string | null;
  countryOfOrigin?: string | null;
  hsCode?: string | null;
  shippingLineId?: string | null;
  vessel?: string | null;
  voyage?: string | null;
  etd?: string | null;
  eta?: string | null;
  closingTime?: string | null;
}
export const updateShipmentInfoApi = (
  id: string,
  d: UpdateShipmentInfoPayload,
) =>
  apiFetch<CustomsDeclarationItem>(
    `/api/v1/containers-advanced/customs/${id}/shipment-info`,
    { method: "PUT", body: JSON.stringify(d) },
  );

// ─── Customs workflow (customs-declaration-workflow) ───
export const getCustomsDeclApi = (id: string) =>
  apiFetch<CustomsDeclarationItem>(`/api/v1/containers-advanced/customs/${id}`);
export const getCustomsStatsApi = () =>
  apiFetch<CustomsStats>("/api/v1/containers-advanced/customs/stats");
export const getCustomsOrdersApi = (p?: Record<string, string>) =>
  apiFetch<CustomsOrderListResponse>(
    `/api/v1/containers-advanced/customs/orders${p ? "?" + new URLSearchParams(p) : ""}`,
  );
export const completeCustomsStepApi = (
  id: string,
  key: string,
  d: {
    note?: string;
    channel?: string;
    taxImport?: number;
    taxVat?: number;
    feeCustoms?: number;
    taxPaidDate?: string;
    taxReceiptNo?: string;
    registrationDate?: string;
    receiptNo?: string;
  },
) =>
  apiFetch<CustomsDeclarationItem>(
    `/api/v1/containers-advanced/customs/${id}/steps/${key}/complete`,
    { method: "POST", body: JSON.stringify(d) },
  );
/** Sửa GHI CHÚ một bước mà KHÔNG ghi đè completedAt (khác completeCustomsStepApi). */
export const updateCustomsStepNoteApi = (
  id: string,
  key: string,
  note: string,
) =>
  apiFetch<{ message: string }>(
    `/api/v1/containers-advanced/customs/${id}/steps/${key}/note`,
    { method: "PUT", body: JSON.stringify({ note }) },
  );
export const clearCustomsApi = (id: string) =>
  apiFetch<CustomsDeclarationItem>(
    `/api/v1/containers-advanced/customs/${id}/clear`,
    { method: "POST" },
  );
export const cancelCustomsApi = (id: string, reason: string) =>
  apiFetch<CustomsDeclarationItem>(
    `/api/v1/containers-advanced/customs/${id}/cancel`,
    { method: "POST", body: JSON.stringify({ reason }) },
  );

/** Upload PDF tờ khai đã thông quan (multipart — NOT JSON). */
export async function uploadCustomsDocumentApi(
  id: string,
  file: File,
): Promise<{ message: string; file: FileUploadItem }> {
  const token = _accessToken;
  const fd = new FormData();
  fd.append("file", file);
  const res = await fetch(
    `${API_URL}/api/v1/containers-advanced/customs/${id}/document`,
    {
      method: "POST",
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...branchHeader(),
      },
      body: fd, // NO Content-Type — browser sets boundary
    },
  );
  if (!res.ok) {
    const e = await res.json().catch(() => ({}));
    throw new Error(e.message || "Tải file thất bại");
  }
  return res.json();
}

/**
 * Biên lai nộp thuế — endpoint RIÊNG, không phải `uploadCustomsDocumentApi`.
 *
 * Endpoint kia ghi vào `decl.fileId` (file tờ khai đã thông quan, điều kiện để
 * bấm Hoàn tất) và chỉ nhận PDF. Biên lai là file độc lập, nhiều file được, và
 * nhận cả ảnh vì biên lai nộp thuế điện tử thường là ảnh chụp màn hình.
 */
export async function uploadTaxReceiptApi(
  id: string,
  file: File,
): Promise<{ message: string; file: FileUploadItem }> {
  const token = _accessToken;
  const fd = new FormData();
  fd.append("file", file);
  const res = await fetch(
    `${API_URL}/api/v1/containers-advanced/customs/${id}/tax-receipts`,
    {
      method: "POST",
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...branchHeader(),
      },
      body: fd, // NO Content-Type — browser sets boundary
    },
  );
  if (!res.ok) {
    const e = await res.json().catch(() => ({}));
    throw new Error(e.message || "Tải file thất bại");
  }
  return res.json();
}

export interface CustomsTaxReceipt {
  id: string;
  fileName: string;
  url: string;
  mimeType: string;
  fileSize: number;
  createdAt: string;
}
export const getTaxReceiptsApi = (id: string) =>
  apiFetch<CustomsTaxReceipt[]>(
    `/api/v1/containers-advanced/customs/${id}/tax-receipts`,
  );
export const deleteTaxReceiptApi = (id: string, fileId: string) =>
  apiFetch<{ message: string }>(
    `/api/v1/containers-advanced/customs/${id}/tax-receipts/${fileId}`,
    { method: "DELETE" },
  );

/** Tệp của tờ khai (biên lai, file tờ khai thông quan) — cùng shape. */
export type CustomsFileItem = CustomsTaxReceipt;
/**
 * File tờ khai thông quan hiện hành (nhiều file, 15/10/2026): bản chính
 * (`decl.fileId`) đứng đầu, rồi các file nối thêm. Tải lên vẫn qua
 * `uploadCustomsDocumentApi` — file đầu thành bản chính, file sau nối thêm.
 */
export const getCustomsDocumentsApi = (id: string) =>
  apiFetch<CustomsFileItem[]>(
    `/api/v1/containers-advanced/customs/${id}/documents`,
  );
export const deleteCustomsDocumentApi = (id: string, fileId: string) =>
  apiFetch<{ message: string }>(
    `/api/v1/containers-advanced/customs/${id}/documents/${fileId}`,
    { method: "DELETE" },
  );

/**
 * Tải chứng từ lô hàng (Invoice, Packing List, B/L, C/O) ngay từ trang hải quan.
 * Backend tự tạo bộ chứng từ gắn đơn nếu đơn chưa có bộ nào.
 */
export async function uploadShipmentDocumentApi(
  id: string,
  file: File,
  meta: { category: CustomsDocCategory; name?: string; documentNo?: string },
): Promise<{
  message: string;
  document: {
    id: string;
    category: string;
    name: string;
    fileUrl?: string | null;
  };
}> {
  const token = _accessToken;
  const fd = new FormData();
  fd.append("file", file);
  fd.append("category", meta.category);
  if (meta.name) fd.append("name", meta.name);
  if (meta.documentNo) fd.append("documentNo", meta.documentNo);
  const res = await fetch(
    `${API_URL}/api/v1/containers-advanced/customs/${id}/shipment-documents`,
    {
      method: "POST",
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...branchHeader(),
      },
      body: fd, // NO Content-Type — browser sets boundary
    },
  );
  if (!res.ok) {
    const e = await res.json().catch(() => ({}));
    throw new Error(e.message || "Tải file thất bại");
  }
  return res.json();
}

export const deleteShipmentDocumentApi = (id: string, docId: string) =>
  apiFetch<{ message: string }>(
    `/api/v1/containers-advanced/customs/${id}/shipment-documents/${docId}`,
    { method: "DELETE" },
  );

// ==================== CUSTOMER PORTAL (R14) ====================

export interface PortalOrderItem {
  id: string;
  code: string;
  status: string;
  transportType: string;
  origin?: string | null;
  destination?: string | null;
  pickupDate?: string | null;
  deliveryDate?: string | null;
  bookingNo?: string | null;
  blNo?: string | null;
  vessel?: string | null;
  voyage?: string | null;
  eta?: string | null;
  etd?: string | null;
  createdAt: string;
}
export interface PortalOrderListResponse {
  data: PortalOrderItem[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}
export interface PortalLoginResponse {
  accessToken: string;
  user: { id: string; email: string; fullName: string; customerId: string };
}

/**
 * FE-07: token cổng khách hàng trước đây nằm trong `localStorage`, sống 1 ngày,
 * và `portalFetch` gặp 401 không xoá gì — trái nguyên tắc "access token chỉ
 * nằm trong memory" (`apps/web/AGENTS.md`), và khách hàng hết hạn phiên bị kẹt
 * ở dashboard báo lỗi tới khi tự bấm Đăng xuất.
 *
 * Đổi sang `sessionStorage` thay vì memory thuần: cổng khách hàng KHÔNG có cơ
 * chế refresh bằng cookie httpOnly như `apps/web` nội bộ (`vgt.rt`) — nếu chỉ
 * giữ trong memory thì F5 trang là mất phiên ngay, trải nghiệm tệ hơn hẳn so
 * với rủi ro chấp nhận được: `sessionStorage` tự xoá khi đóng tab/trình duyệt
 * (khác `localStorage` sống vô thời hạn), và bề mặt XSS của `apps/portal` là
 * một trang tĩnh nhỏ không có `dangerouslySetInnerHTML` hay nội dung do người
 * dùng khác nhập. Đây là lựa chọn có ý thức, không phải quên đổi — nếu sau
 * này portal cần bảo vệ ngang mức nội bộ thì chuyển sang cookie httpOnly
 * riêng (`vgt.pt`?) như `vgt.rt`/`vgt.ft`, cần đổi cả backend.
 */
const PORTAL_TOKEN_KEY = "portal_access_token";
let _portalToken: string | null = null;

export const getPortalToken = (): string | null => {
  if (_portalToken) return _portalToken;
  if (typeof window === "undefined") return null;
  try {
    _portalToken = sessionStorage.getItem(PORTAL_TOKEN_KEY);
  } catch {
    // sessionStorage bị chặn (chế độ ẩn danh nghiêm ngặt...) — coi như chưa đăng nhập.
    _portalToken = null;
  }
  return _portalToken;
};

export const setPortalToken = (t: string): void => {
  _portalToken = t;
  try {
    sessionStorage.setItem(PORTAL_TOKEN_KEY, t);
  } catch {
    // ignore quota/disabled storage — token vẫn sống trong memory cho tab hiện tại.
  }
};

export const clearPortalToken = (): void => {
  _portalToken = null;
  try {
    sessionStorage.removeItem(PORTAL_TOKEN_KEY);
  } catch {
    // ignore
  }
};

/** Sự kiện toàn app: phiên portal hết hạn (401) → trang portal tự đăng xuất. */
export const PORTAL_SESSION_EXPIRED_EVENT = "vgt:portal-session-expired";

async function portalFetch<T>(path: string, opts?: RequestInit): Promise<T> {
  const token = getPortalToken();
  const res = await fetch(`${API_URL}${path}`, {
    ...opts,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(opts?.headers || {}),
    },
  });
  if (!res.ok) {
    // FE-07: 401 = phiên hết hạn hoặc token không hợp lệ — tự xoá và báo cho
    // UI đăng xuất, thay vì để người dùng kẹt ở dashboard báo lỗi.
    if (res.status === 401 && !path.startsWith("/api/v1/portal/login")) {
      clearPortalToken();
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent(PORTAL_SESSION_EXPIRED_EVENT));
      }
    }
    const body = await res.json().catch(() => ({}));
    throw new Error((body as any)?.message || `Lỗi ${res.status}`);
  }
  return res.json();
}

export const portalLoginApi = (email: string, password: string) =>
  portalFetch<PortalLoginResponse>("/api/v1/portal/login", {
    method: "POST",
    body: JSON.stringify({ email, password }),
  });
export const portalListOrdersApi = (p?: Record<string, string>) =>
  portalFetch<PortalOrderListResponse>(
    `/api/v1/portal/orders${p ? "?" + new URLSearchParams(p) : ""}`,
  );
export const portalGetOrderApi = (id: string) =>
  portalFetch<any>(`/api/v1/portal/orders/${id}`);
export const portalTrackContainerApi = (containerNo: string) =>
  portalFetch<any[]>(
    `/api/v1/portal/containers/${encodeURIComponent(containerNo)}`,
  );

// ==================== ACCOUNTING — ĐÃ GỠ (GĐ3 lộ trình Fast BO) ====================
// Sổ sách kế toán chính thức chuyển sang Fast Business Online.
// Xem docs/VGT_ERP_Fast_Ra_Soat_Code_Plan.md — adapter Fast sẽ thay thế ở GĐ5.

// ==================== HR PRESENCE (tình trạng nhân sự) ====================
export interface PresenceSummary {
  working: number;
  leave: number;
  businessTrip: number;
  outside: number;
  total: number;
  unassignedBranch: number;
}
export interface PresenceItem {
  employeeCode?: string;
  /** true = nhân sự chưa được gán chi nhánh nào, cần HCNS bổ sung */
  unassignedBranch?: boolean;
  id: string;
  name: string;
  department: string;
  avatarUrl?: string;
  avatarFallback: string;
  status:
    | "WORKING"
    | "ON_LEAVE"
    | "BUSINESS_TRIP"
    | "OUTSIDE"
    | "WORK_HOME"
    | "OFF";
  statusLabel: string;
  branch: string;
  checkInTime?: string;
  expectedReturn?: string;
  generalLocation?: string;
  note?: string;
  workStatusSince?: string;
  isOverdue?: boolean;
}
export interface PresenceDetail extends PresenceItem {
  position?: string;
  leavePeriod?: { fromDate: string; toDate: string };
}
export const getPresenceSummaryApi = () =>
  apiFetch<PresenceSummary>("/api/v1/hr/presence/summary");
export interface PresenceListResponse {
  data: PresenceItem[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}
export interface PresenceListParams {
  status?: string;
  page?: number;
  limit?: number;
  search?: string;
  includeInactive?: boolean;
  unassignedOnly?: boolean;
}
export const getPresenceApi = (p: PresenceListParams = {}) => {
  const q = new URLSearchParams();
  if (p.status && p.status !== "all") q.set("status", p.status);
  if (p.page) q.set("page", String(p.page));
  if (p.limit) q.set("limit", String(p.limit));
  if (p.search?.trim()) q.set("search", p.search.trim());
  if (p.includeInactive) q.set("includeInactive", "true");
  if (p.unassignedOnly) q.set("unassignedOnly", "true");
  const qs = q.toString();
  return apiFetch<PresenceListResponse>(
    `/api/v1/hr/presence${qs ? "?" + qs : ""}`,
  );
};
export const getPresenceDetailApi = (id: string) =>
  apiFetch<PresenceDetail>(`/api/v1/hr/presence/${id}`);
export const setWorkStatusApi = (
  userId: string,
  d: {
    workStatus: string;
    note?: string;
    checkInAt?: string;
    expectedReturn?: string;
    presenceLocation?: string;
  },
) =>
  apiFetch<any>(`/api/v1/hr/work-status/${userId}`, {
    method: "PUT",
    body: JSON.stringify(d),
  });

// ==================== INTERNAL NEWS (bản tin nội bộ) ====================
export type NewsReactionKind =
  | "LIKE"
  | "LOVE"
  | "CELEBRATE"
  | "AGREE"
  | "CARE"
  | "WOW";
export interface NewsPostDto {
  id: string;
  authorName: string;
  department: string;
  avatarFallback: string;
  publishedAt: string;
  visibility: "COMPANY" | "DEPARTMENT" | "PRIVATE_GROUP";
  visibilityLabel: string;
  title: string;
  content: string;
  contentHtml?: string;
  attachment?: { type: string; name: string; size: string; url: string };
  attachments?: {
    id: string;
    name: string;
    size: string;
    url: string;
    mimeType: string;
  }[];
  images?: { id: string; name: string; url: string }[];
  reactions: Partial<Record<NewsReactionKind, number>>;
  reactionCount: number;
  commentCount: number;
  currentUserReaction?: NewsReactionKind;
  isPinned: boolean;
  isNew: boolean;
  canEdit: boolean;
  canDelete: boolean;
  createdAt: string;
}
export interface NewsCommentDto {
  id: string;
  content: string;
  authorName: string;
  avatarFallback: string;
  createdAt: string;
}
export const getNewsApi = (limit?: number) =>
  apiFetch<NewsPostDto[]>(`/api/v1/news${limit ? "?limit=" + limit : ""}`);
export const getNewsPostApi = (id: string) =>
  apiFetch<NewsPostDto>(`/api/v1/news/${id}`);
export const createNewsApi = (d: {
  title: string;
  content: string;
  contentHtml?: string;
  visibility?: string;
  departmentId?: string;
  branchId?: string;
  isPinned?: boolean;
  /** Gửi thông báo "Tin nội bộ mới" cho người xem được bài (chỉ khi đăng mới). */
  notify?: boolean;
  attachmentType?: string;
  attachmentName?: string;
  attachmentSize?: string;
  attachmentUrl?: string;
}) =>
  apiFetch<NewsPostDto>("/api/v1/news", {
    method: "POST",
    body: JSON.stringify(d),
  });
export const updateNewsApi = (
  id: string,
  d: Partial<{
    title: string;
    content: string;
    contentHtml?: string;
    visibility: string;
    isPinned: boolean;
    attachmentType: string;
    attachmentName: string;
    attachmentSize: string;
    attachmentUrl: string;
  }>,
) =>
  apiFetch<NewsPostDto>(`/api/v1/news/${id}`, {
    method: "PUT",
    body: JSON.stringify(d),
  });
export const deleteNewsApi = (id: string) =>
  apiFetch<{ message: string }>(`/api/v1/news/${id}`, { method: "DELETE" });
export const togglePinNewsApi = (id: string) =>
  apiFetch<NewsPostDto>(`/api/v1/news/${id}/pin`, { method: "PUT" });
export const reactNewsApi = (id: string, type?: NewsReactionKind | null) =>
  apiFetch<{
    reactions: Record<string, number>;
    reactionCount: number;
    currentUserReaction?: string;
  }>(`/api/v1/news/${id}/reactions`, {
    method: "POST",
    body: JSON.stringify({ type: type ?? undefined }),
  });
export const getNewsCommentsApi = (id: string) =>
  apiFetch<NewsCommentDto[]>(`/api/v1/news/${id}/comments`);
export const addNewsCommentApi = (id: string, content: string) =>
  apiFetch<NewsCommentDto>(`/api/v1/news/${id}/comments`, {
    method: "POST",
    body: JSON.stringify({ content }),
  });

// ==================== DISBURSEMENTS (CHI HỘ KHÁCH HÀNG) ====================

/** Một dòng khoản chi trong phiếu chi hộ — `total` do server tính. */
/** Chứng từ đính kèm của một dòng khoản chi — file lưu trên Cloudflare R2. */
export interface DisbursementLineFile {
  id: string;
  fileKey: string;
  fileName: string;
  createdAt: string;
}

/** Khớp enum `DisbursementInvoiceTo` của Prisma / `DISBURSEMENT_INVOICE_TO` trong @vgt/shared. */
export type DisbursementInvoiceTo = 'VGT' | 'CUSTOMER';

export interface DisbursementLineItem {
  id: string;
  code: string;
  name: string;
  supplierId: string | null;
  quantity: number;
  amount: number;
  vatIncluded: boolean;
  vatRate: number;
  total: number;
  receiptNo: string | null;
  /** @deprecated Chứng từ đơn lẻ cũ; dùng `files`. */
  fileKey: string | null;
  /** @deprecated dùng `files`. */
  fileName: string | null;
  note: string | null;
  /** NCC xuất hoá đơn cho VGT hay thẳng cho khách (VGT vẫn trả tiền); null = chưa rõ. */
  invoiceTo: DisbursementInvoiceTo | null;
  createdAt: string;
  /** Nhiều chứng từ cho một dòng. */
  files: DisbursementLineFile[];
}

/** Tóm tắt đơn hàng backend join sẵn cho mỗi phiếu — đủ để bảng hiện tổng quan đơn. */
export interface DisbursementOrderSummary {
  id: string;
  code: string;
  status: string;
  transportType: string;
  origin: string | null;
  destination: string | null;
  isUrgent: boolean;
  pickupDate: string | null;
  deliveryDate: string | null;
  bookingNo: string | null;
  blNo: string | null;
  /** Không có khi người xem thiếu quyền Xem giá đơn (28/10/2026). */
  grandTotal?: number;
  disbursementAmount: number;
  /** Số dòng hàng của đơn. */
  itemCount: number;
  /** Đếm theo loại container, theo thứ tự xuất hiện: `[{ code: '40HC', count: 2 }]`. */
  containers: { code: string; count: number }[];
}

export interface DisbursementItem {
  id: string;
  code: string;
  orderId: string | null;
  orderCode: string | null;
  /** Tóm tắt đơn gắn với phiếu; `null` khi phiếu chưa gắn đơn hoặc đơn không còn. */
  order?: DisbursementOrderSummary | null;
  customerId: string;
  /** Tên khách hàng đầy đủ, backend join sẵn. */
  customerName: string | null;
  /** Tên rút gọn để hiện trong bảng, backend join sẵn. */
  customerDisplayName?: string | null;
  branchId: string | null;
  status: string;
  totalAmount: number;
  paidAt: string | null;
  reimbursedAt: string | null;
  note: string | null;
  createdBy: string | null;
  /** Họ tên người tạo phiếu, backend join sẵn. */
  createdByName: string | null;
  approvedBy: string | null;
  approvedAt: string | null;
  rejectedReason: string | null;
  createdAt: string;
  updatedAt: string;
  lines: DisbursementLineItem[];
  /** Chỉ có khi backend bổ sung field cầu nối AP — hiện chưa nằm trong select. */
  apBridgeError?: string | null;
  apBridgeFailedAt?: string | null;
}

export interface DisbursementListResponse {
  data: DisbursementItem[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}

export interface DisbursementStatBucket {
  count: number;
  amount: number;
}

export interface DisbursementStats {
  total: DisbursementStatBucket;
  draft: DisbursementStatBucket;
  pendingApproval: DisbursementStatBucket;
  approved: DisbursementStatBucket;
  paid: DisbursementStatBucket;
  reimbursed: DisbursementStatBucket;
  cancelled: DisbursementStatBucket;
}

/** Payload dòng gửi lên server — tuyệt đối không kèm `total`. */
export interface DisbursementLinePayload {
  code: string;
  name?: string;
  supplierId?: string;
  quantity?: number;
  amount: number;
  vatIncluded?: boolean;
  vatRate?: number;
  receiptNo?: string;
  /** @deprecated dùng `files`. */
  fileKey?: string;
  /** @deprecated dùng `files`. */
  fileName?: string;
  note?: string;
  /** Bỏ qua hoặc null = chưa rõ. */
  invoiceTo?: DisbursementInvoiceTo | null;
  /** Danh sách chứng từ đã upload lên R2. */
  files?: { fileKey: string; fileName: string }[];
}

export interface CreateDisbursementPayload {
  customerId: string;
  orderId?: string;
  note?: string;
  lines: DisbursementLinePayload[];
}

export type UpdateDisbursementPayload = Partial<CreateDisbursementPayload>;

export const getDisbursementsApi = (p?: Record<string, string>) =>
  apiFetch<DisbursementListResponse>(
    `/api/v1/disbursements${p ? "?" + new URLSearchParams(p) : ""}`,
  );
export const getDisbursementStatsApi = () =>
  apiFetch<DisbursementStats>("/api/v1/disbursements/stats");
export const getDisbursementApi = (id: string) =>
  apiFetch<DisbursementItem>(`/api/v1/disbursements/${id}`);
export const createDisbursementApi = (d: CreateDisbursementPayload) =>
  apiFetch<DisbursementItem>("/api/v1/disbursements", {
    method: "POST",
    body: JSON.stringify(d),
  });
export const updateDisbursementApi = (
  id: string,
  d: UpdateDisbursementPayload,
) =>
  apiFetch<DisbursementItem>(`/api/v1/disbursements/${id}`, {
    method: "PUT",
    body: JSON.stringify(d),
  });
export const deleteDisbursementApi = (id: string) =>
  apiFetch<{ message: string }>(`/api/v1/disbursements/${id}`, {
    method: "DELETE",
  });
/** Chỉ cho phiếu lập trước 07/10/2026 còn ở luồng duyệt cũ. */
export const markDisbursementPaidApi = (id: string) =>
  apiFetch<DisbursementItem>(`/api/v1/disbursements/${id}/mark-paid`, {
    method: "PUT",
  });
export const markDisbursementReimbursedApi = (id: string) =>
  apiFetch<DisbursementItem>(`/api/v1/disbursements/${id}/mark-reimbursed`, {
    method: "PUT",
  });

// ==================== DEBIT NOTE / PAYMENT REQUEST ====================

/** Một dòng đã tính (server tính `amount`/`vatAmount`) — dùng cho preview và chi tiết. */
export interface DebitNoteComputedLine {
  seq: number;
  description: string;
  unit: string | null;
  quantity: number;
  unitPrice: number;
  amount: number;
  vatRate: number;
  vatAmount: number;
  remarks: string | null;
  isManagementFee: boolean;
  sourceType: string | null;
  sourceId: string | null;
}

/** Payload một dòng gửi lên server — không có `seq`/`vatAmount` (server tự tính). */
export interface DebitNoteLinePayload {
  description: string;
  unit?: string;
  quantity?: number;
  unitPrice?: number;
  amount?: number;
  vatRate?: number;
  remarks?: string;
  isManagementFee?: boolean;
  sourceType?: string;
  sourceId?: string;
}

/** Field header dùng chung cho tạo/sửa — toàn bộ snapshot tại thời điểm phát hành. */
export interface DebitNoteHeaderPayload {
  /** Ngôn ngữ nhãn in trên chứng từ: EN (mặc định) | ZH | VI. */
  language?: string;
  toName?: string;
  buyerTaxCode?: string;
  sellerName?: string;
  sellerTaxCode?: string;
  sellerAddress?: string;
  sellerPhone?: string;
  sellerEmail?: string;
  contractNo?: string;
  /** Người lập — server set lúc tạo, chỉ đọc. */
  preparedByName?: string;
  toAddress?: string;
  attn?: string;
  tel?: string;
  projectTitle?: string;
  commercialInvoiceNo?: string;
  blNo?: string;
  cargoVolume?: string;
  vessel?: string;
  vesselLoading?: string;
  portOfLoading?: string;
  placeOfDelivery?: string;
  /** ISO string */
  serviceFinishedDate?: string;
  exchangeRate?: number;
  paymentTerm?: string;
  /** ISO string */
  dueDate?: string;
  bankAccountNo?: string;
  bankName?: string;
  /** Chi nhánh ngân hàng nhận, in dưới tên ngân hàng. */
  bankBranch?: string;
  bankBeneficiary?: string;
  bankSwiftCode?: string;
  /** Mã NAPAS 6 số của ngân hàng nhận — cần để dựng mã QR VietQR. */
  bankBin?: string;
  /** In mã QR thanh toán cạnh khối ngân hàng. */
  showPaymentQr?: boolean;
  note?: string;
}

export interface DebitNoteDocumentPayload {
  kind: "PAYMENT_REQUEST" | "DEBIT_NOTE";
  managementFeeRate?: number;
  lines: DebitNoteLinePayload[];
}

/**
 * Chế độ tạo chứng từ (07/10/2026): COMBINED = một Debit Note gộp cước + chi hộ
 * + phí quản lý (mặc định); SPLIT = Payment Request + Debit Note cùng số như trước.
 */
export type DebitNoteMode = "COMBINED" | "SPLIT";

export interface CreateDebitNotePayload extends DebitNoteHeaderPayload {
  orderId: string;
  mode?: DebitNoteMode;
  documents: DebitNoteDocumentPayload[];
}

export interface UpdateDebitNotePayload extends DebitNoteHeaderPayload {
  managementFeeRate?: number;
  lines?: DebitNoteLinePayload[];
}

export interface DebitNoteItem extends DebitNoteHeaderPayload {
  id: string;
  docNo: string;
  seq: number;
  kind: "PAYMENT_REQUEST" | "DEBIT_NOTE";
  status: "DRAFT" | "ISSUED" | "PAID" | "CANCELLED";
  orderId: string;
  orderCode: string | null;
  customerId: string;
  branchId: string | null;
  subTotal: number;
  vatAmount: number;
  total: number;
  amountInWords: string;
  managementFeeRate: number | null;
  createdBy: string | null;
  issuedAt: string | null;
  paidAt: string | null;
  cancelledReason: string | null;
  createdAt: string;
  updatedAt: string;
  lines: DebitNoteComputedLine[];
  /** Backend join sẵn. */
  customerName: string | null;
  /** Tên rút gọn để hiện trong bảng, backend join sẵn. */
  customerDisplayName?: string | null;
  createdByName: string | null;
}

/** Chỉ có ở `GET /debit-notes/:id` — chứng từ cặp cùng `docNo`, khác `kind`. */
export interface DebitNoteDetail extends DebitNoteItem {
  sibling: { id: string; kind: string; status: string; total: number } | null;
}

export interface DebitNoteListResponse {
  data: DebitNoteItem[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}

export interface DebitNoteStatBucket {
  count: number;
  amount: number;
}

export interface DebitNoteStats {
  total: DebitNoteStatBucket;
  draft: DebitNoteStatBucket;
  issued: DebitNoteStatBucket;
  paid: DebitNoteStatBucket;
  cancelled: DebitNoteStatBucket;
}

export interface DebitNotePreviewOrder {
  id: string;
  code: string;
  status: string;
  customerId: string;
  customerName: string;
  customerShortName: string | null;
  grandTotal: number;
  disbursementAmount: number;
}

export interface DebitNotePreviewDocument {
  lines: DebitNoteComputedLine[];
  totals: {
    subTotal: number;
    vatAmount: number;
    total: number;
    amountInWords: string;
  };
}

/** Chứng từ còn sống đang giữ dòng của đơn — lý do preview thiếu/trống dòng. */
export interface DebitNotePreviewBilledDoc {
  docNo: string;
  debitNoteId: string;
  /** Số dòng (cước/phụ phí/chi hộ) của đơn đã nằm trong chứng từ này. */
  lines: number;
}

export interface DebitNotePreviewResponse {
  order: DebitNotePreviewOrder;
  header: DebitNoteHeaderPayload;
  managementFeeRate: number;
  mode: DebitNoteMode;
  paymentRequest: DebitNotePreviewDocument;
  debitNote: DebitNotePreviewDocument;
  /** Một Debit Note gộp: cước/phụ phí → chi hộ → phí quản lý; totals = PR + DN. */
  combined: DebitNotePreviewDocument;
  alreadyBilled: DebitNotePreviewBilledDoc[];
  warnings: string[];
}

export const getDebitNotesApi = (p?: Record<string, string>) =>
  apiFetch<DebitNoteListResponse>(
    `/api/v1/debit-notes${p ? "?" + new URLSearchParams(p) : ""}`,
  );
export const getDebitNoteStatsApi = () =>
  apiFetch<DebitNoteStats>("/api/v1/debit-notes/stats");
export const getDebitNoteApi = (id: string) =>
  apiFetch<DebitNoteDetail>(`/api/v1/debit-notes/${id}`);
export const previewDebitNoteApi = (d: {
  orderId: string;
  managementFeeRate?: number;
  language?: string;
  mode?: DebitNoteMode;
}) =>
  apiFetch<DebitNotePreviewResponse>("/api/v1/debit-notes/preview", {
    method: "POST",
    body: JSON.stringify(d),
  });
/** Dịch AI: ngôn ngữ đích (VI là gốc nên không dịch). */
export type DebitNoteTranslateLanguage = "EN" | "ZH";
/** address: địa chỉ · place: cảng/địa danh · text: diễn giải dòng. */
export type DebitNoteTranslateKind = "address" | "place" | "text";
export interface DebitNoteTranslateItem {
  key: string;
  text: string;
  kind?: DebitNoteTranslateKind;
}
export interface DebitNoteTranslateResponse {
  language: DebitNoteTranslateLanguage;
  items: { key: string; text: string }[];
}

/** Đơn đã có chứng từ còn hiệu lực (không tính đã hủy) — trang tạo mới ẩn khỏi combobox. */
export const getDebitNoteBilledOrderIdsApi = () =>
  apiFetch<{ orderIds: string[] }>("/api/v1/debit-notes/billed-order-ids");
/** Máy chủ có cấu hình khoá AI hay chưa — quyết định nút dịch có bấm được. */
export interface DebitNoteTranslateStatus {
  available: boolean;
  /** Nhà cung cấp gọi đầu tiên ("Claude" | "Perplexity" | "OpenAI"), null khi tắt. */
  provider: string | null;
  model: string | null;
  /** Thứ tự gọi, gồm cả nhà cung cấp dự phòng. */
  chain: AiChainLink[];
}
export const getDebitNoteTranslateStatusApi = () =>
  apiFetch<DebitNoteTranslateStatus>("/api/v1/debit-notes/translate/status");
export const translateDebitNoteApi = (d: {
  language: DebitNoteTranslateLanguage;
  items: DebitNoteTranslateItem[];
}) =>
  apiFetch<DebitNoteTranslateResponse>("/api/v1/debit-notes/translate", {
    method: "POST",
    body: JSON.stringify(d),
  });
export const createDebitNoteApi = (d: CreateDebitNotePayload) =>
  apiFetch<{ docNo: string; documents: DebitNoteItem[] }>(
    "/api/v1/debit-notes",
    {
      method: "POST",
      body: JSON.stringify(d),
    },
  );
export const updateDebitNoteApi = (id: string, d: UpdateDebitNotePayload) =>
  apiFetch<DebitNoteDetail>(`/api/v1/debit-notes/${id}`, {
    method: "PUT",
    body: JSON.stringify(d),
  });
export const issueDebitNoteApi = (id: string) =>
  apiFetch<DebitNoteDetail>(`/api/v1/debit-notes/${id}/issue`, {
    method: "PUT",
  });
export const markPaidDebitNoteApi = (id: string) =>
  apiFetch<DebitNoteDetail>(`/api/v1/debit-notes/${id}/mark-paid`, {
    method: "PUT",
  });
export const cancelDebitNoteApi = (id: string, cancelledReason: string) =>
  apiFetch<DebitNoteDetail>(`/api/v1/debit-notes/${id}/cancel`, {
    method: "PUT",
    body: JSON.stringify({ cancelledReason }),
  });
export interface DebitNoteActivityItem {
  id: string;
  action: string;
  details: Record<string, unknown> | null;
  createdAt: string;
  userName: string | null;
}

export const getDebitNoteActivityApi = (id: string) =>
  apiFetch<DebitNoteActivityItem[]>(`/api/v1/debit-notes/${id}/activity`);

export const deleteDebitNoteApi = (id: string) =>
  apiFetch<{ message: string }>(`/api/v1/debit-notes/${id}`, {
    method: "DELETE",
  });

/** Tải Excel/PDF chứng từ debit — cùng pattern `downloadPdfApi` ở trên. */
export const downloadDebitNoteExcelApi = (id: string, docNo: string) =>
  downloadPdfApi(
    `/api/v1/export/debit-note/${id}/excel`,
    `${docNo.replace(/\//g, "-")}.xlsx`,
  );
export const downloadDebitNotePdfApi = (id: string, docNo: string) =>
  downloadPdfApi(
    `/api/v1/export/debit-note/${id}/pdf`,
    `${docNo.replace(/\//g, "-")}.pdf`,
  );

// ─── Tìm kiếm toàn cục ───────────────────────────────────────────────
export interface SearchHit {
  /** Khớp module quyền: 'orders' | 'customers' | 'partners' | 'vehicles' | 'trailers' */
  type: string;
  typeLabel: string;
  id: string;
  title: string;
  subtitle?: string;
  /** Vì sao bản ghi khớp khi tiêu đề không chứa từ khoá — "Booking: ABC123" */
  matchedOn?: string;
  href: string;
}

export interface GlobalSearchResponse {
  query: string;
  hits: SearchHit[];
  skipped: string[];
}

export const globalSearchApi = (q: string, signal?: AbortSignal) =>
  apiFetch<GlobalSearchResponse>(`/api/v1/search?q=${encodeURIComponent(q)}`, {
    signal,
  });

// ============ ĐỢT 2D CHỨNG TỪ — todos[]/notApplicable/counters + phân loại EIR + nhắc tài xế ============
// chung-tu.md §13.1 "todos[]/notApplicable/suggestion" + "Nhắc tài xế" + B10.
// Field CỘNG THÊM vào `OrderDocumentsPage`/`OrderDocumentsOverview` đã có ở trên — không đổi field cũ.

/** IMPORT | EXPORT | BULK | DOMESTIC — `docFlowFor()` bên API (chung-tu.md §4/§6). */
export type DocFlow = "IMPORT" | "EXPORT" | "BULK" | "DOMESTIC";

export interface OrderDocTodoTarget {
  kind: TripDocSlotKind;
  /** 'order' | 'dispatch' | 'trip' — quyết định `dispatchId`/`tripId` có mặt hay không (chung-tu.md §13.1). */
  scope: "order" | "dispatch" | "trip";
  /** Có khi `scope` là 'dispatch' hoặc 'trip'. */
  dispatchId?: string;
  /** CHỈ có khi `scope: 'trip'`. */
  tripId?: string;
  docId: string | null;
}

export interface OrderDocTodo {
  status: "missing" | "flagged" | "classify";
  title: string;
  meta: string;
  who: "office" | "driver";
  /** `attach` = mở hộp thoại "Gắn container" cho chứng từ cũ chưa gắn chuyến. */
  action: "submit" | "view" | "replace" | "classify" | "remind" | "attach";
  target: OrderDocTodoTarget;
  suggestion?: { direction: "IN" | "OUT"; reason: string } | null;
}

export interface OrderDocCounters {
  officeTodo: number;
  driverWaiting: number;
  needsReclassify: number;
  needsReupload: number;
  /**
   * Tổng số việc (`todos.length`, vá 26/09/2026) — MỘT nguồn duy nhất cho
   * badge "Bộ chứng từ" và số tab Vận chuyển, luôn khớp `res.todos.length`.
   * Optional: route cũ (`documents/orders/:orderId/page`) có thể chưa trả
   * field này nếu chưa deploy đợt vá — dùng `todos.length` làm đường lùi.
   */
  todoCount?: number;
}

export interface OrderDocProgress {
  done: number;
  total: number;
  dispatchesComplete: number;
  dispatchesTotal: number;
}

/** Field mới trên `orderOverview()` (dùng bởi `orderDocumentsPage`) — chung-tu.md 13.1. */
export interface OrderDocsOverviewExtra {
  transportFlow: DocFlow;
  /** Ô cấp ĐƠN không phụ thuộc chuyến — khối "Chứng từ chung của đơn" khi đơn chưa có chuyến (28/09/2026). */
  orderCells?: TripDocCell[];
  counters: OrderDocCounters;
  progress: OrderDocProgress;
  todos: OrderDocTodo[];
  notApplicable: string | null;
  /**
   * B2 (28/09/2026, chung-tu.md §13.5) — hai cờ MỚI ở gốc `orderOverview()`,
   * server tự tính (không tự suy từ `customsType`/`transportFlow` ở client):
   * hàng rời (BULK) khai hải quan vẫn không hiện khối "Thông tin tàu chuyến"
   * (Booking/B/L/vessel/voyage chỉ có nghĩa với container); nội địa (DOMESTIC)
   * không có cả hai khối dù đơn có thể set `customsRequired` do dữ liệu cũ.
   */
  showCustoms: boolean;
  showShipInfo: boolean;
  /** Cột chứng từ cấp chuyến của ĐƠN, `applicable` theo luồng hàng — có cả khi đơn chưa có lệnh. */
  columns: Array<{ kind: TripDocSlotKind; label: string; applicable: boolean }>;
  /** Số B/L của đơn theo thứ tự nhập (nhiều B/L, 15/10/2026 — `OrderRefNo` type BL, rơi về `blNo`). */
  order: { blNos: string[] };
  /**
   * MỌI bản B/L sống cấp đơn, cũ trước mới sau (nhiều B/L, 15/10/2026). Ô B/L
   * trong `orderCells`/`trips[].cells` chỉ mang MỘT bản đại diện — khối
   * "Chứng từ chung của đơn" và chip trang đơn dựng mỗi B/L một dòng từ đây.
   */
  blDocuments: TripDocFile[];
}

/** `getOrderDocumentsPageApi` trả thêm các field này (cộng thêm, không đổi field cũ). */
export type OrderDocumentsPageV2 = OrderDocumentsPage & OrderDocsOverviewExtra;

/** B10 — phân loại chiều EIR chưa rõ (chung-tu.md §13.1). Quyền documents.update + scope. */
export const classifyEirDirectionApi = (
  documentId: string,
  direction: "IN" | "OUT",
) =>
  apiFetch<ShippingDocumentItem>(
    `/api/v1/documents/${documentId}/eir-direction`,
    {
      method: "PUT",
      body: JSON.stringify({ direction }),
    },
  );

/** Gắn chứng từ chỉ-chuyến đang nằm ở cấp lệnh vào đúng chuyến; 409 khi chuyến đã có bản cùng loại. */
export const attachDocumentToTripApi = (
  dispatchId: string,
  documentId: string,
  tripId: string,
) =>
  apiFetch<ShippingDocumentItem>(
    `/api/v1/documents/dispatches/${dispatchId}/documents/${documentId}/attach-trip`,
    { method: "PUT", body: JSON.stringify({ tripId }) },
  );

/** "Nhắc tài xế" (chung-tu.md §13.1) — 1 lần/15 phút cho (chuyến, loại giấy); 429 khi vượt. */
export const remindDriverDocumentApi = (
  tripId: string,
  kind: TripDocSlotKind,
) =>
  apiFetch<{ lastRemindedAt: string }>(
    `/api/v1/dispatch-trips/${tripId}/documents/remind`,
    {
      method: "POST",
      body: JSON.stringify({ kind }),
    },
  );

// ── Danh sách chứng từ gom theo đơn — bucket canvas List (chốt 26/09/2026, chung-tu.md 13.1) ──

export type OrderDocsBucket =
  | "office_todo"
  | "driver_waiting"
  | "classify"
  | "complete";

export interface OrderDocsNextV2 {
  /** `pending` = tài xế chưa tới bước ("Chờ tài xế giao hàng"). */
  status: "missing" | "flagged" | "classify" | "pending" | "ok";
  text: string;
  who: "office" | "driver" | null;
}

/** Một dòng của `GET /api/v1/documents/orders` — field MỚI cộng thêm (canvas List). */
export interface OrderDocsListRowV2 {
  id: string;
  code: string;
  status: string;
  transportType: string | null;
  isUrgent: boolean;
  customer: {
    id: string;
    name: string;
    shortName?: string | null;
    displayName?: string | null;
  } | null;
  bookingNo: string | null;
  blNo: string | null;
  deliveryDate: string | null;
  dispatchCount: number;
  tripCount: number;
  lastDocAt: string | null;
  transportFlow: DocFlow;
  /** "2 lệnh · 3 cont" hoặc (hàng rời) "3 chuyến". */
  scale: string;
  progress: { done: number; total: number };
  next: OrderDocsNextV2;
  buckets: OrderDocsBucket[];
}

export interface OrderDocsListMetaV2 {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  bucketCounts: Record<OrderDocsBucket, number>;
  truncated: boolean;
}

export interface OrderDocsListResponseV2 {
  data: OrderDocsListRowV2[];
  meta: OrderDocsListMetaV2;
}

/** Query mới: `bucket` thay cho `focus` cũ — xem `OrderDocsListQuery` bên API. */
export const getOrderDocsListV2Api = (params: Record<string, string>) =>
  apiFetch<OrderDocsListResponseV2>(
    `/api/v1/documents/orders?${new URLSearchParams(params).toString()}`,
  );

/**
 * Field `sub` sắp thêm vào `OrderDocsDispatchEntry.dispatch` (dòng phụ khối
 * lệnh trên trang Chứng từ của đơn, canvas OrderDocs — chốt 25/09/2026: BE
 * đang mở rộng, vd "4 container · 0/4 đủ · Giấy riêng lệnh: EIR nhận"). CHƯA
 * chạy — dùng type mở rộng này thay vì sửa `OrderDocsDispatchEntry` gốc để
 * giữ nguyên tắc chỉ THÊM. Xoá type này và gộp thẳng vào field gốc khi BE
 * xong và field đã ổn định.
 */
/** Dòng phụ của lệnh do server dựng ("4 container · 0/4 đủ · Giấy riêng lệnh: EIR nhận"). */
export type OrderDocsDispatchEntryWithSub = OrderDocsDispatchEntry & {
  sub?: string;
};

// ==================== VÒNG ĐỜI TÀI SẢN: BÁN XE / LỊCH SỬ BIỂN SỐ ====================

export type FleetStatus = "DRAFT" | "IN_FLEET" | "PENDING_HANDOVER" | "SOLD" | "OUT_OF_FLEET";
export type FleetAssetKind = "vehicles" | "trailers";
export type FleetListScope = "active" | "sold" | "all";

export interface PlateAssignment {
  id: string;
  plateRaw: string;
  plateKey: string;
  validFrom: string | null;
  validTo: string | null;
  fromPrecision: "DATETIME" | "DATE" | "UNKNOWN";
  status: "CONFIRMED" | "NEEDS_REVIEW" | "VOID";
  startReason?: string | null;
  endReason?: string | null;
  endPending: boolean;
  evidenceNote?: string | null;
  voidReason?: string | null;
  createdAt: string;
}
export interface FleetPeriod {
  id: string;
  startedAt: string | null;
  startPrecision?: "DATETIME" | "DATE" | "UNKNOWN";
  startReason?: string | null;
  endedAt: string | null;
  endReason?: string | null;
  saleId?: string | null;
  status?: string;
  voidReason?: string | null;
  createdAt: string;
}
export interface AssetSale {
  id: string;
  contractDate: string | null;
  handoverAt: string;
  operationEndAt: string;
  handoverOdometer: number | null;
  plateDisposition: "CONFIRMED_ENDED" | "PENDING";
  note?: string | null;
  status: "CONFIRMED" | "CORRECTED";
  correctionReason?: string | null;
  correctedAt?: string | null;
  createdAt: string;
}
export interface IdentityChange {
  id: string;
  field: "VIN" | "ENGINE_NUMBER";
  oldValue: string | null;
  newValue: string | null;
  kind?: string;
  reason?: string | null;
  createdAt: string;
}
export interface FleetHistory {
  asset: {
    id: string;
    code: string | null;
    plateNumber: string;
    fleetStatus: FleetStatus;
    status: string;
    vin?: string | null;
    engineNumber?: string | null;
  };
  plates: PlateAssignment[];
  periods: FleetPeriod[];
  sales: AssetSale[];
  identityChanges: IdentityChange[];
  branchPeriods: BranchPeriod[];
  currentSale: {
    id: string;
    handoverAt: string;
    operationEndAt: string;
    plateDisposition: "CONFIRMED_ENDED" | "PENDING";
  } | null;
}

// ---- Lịch sử chi nhánh quản lý (xe / moóc / tài xế) ----

export type BranchSubjectKind = "vehicles" | "trailers" | "drivers";
export interface BranchPeriod {
  id: string;
  branchId: string;
  branch: { id: string; code: string; name: string };
  /** null = không rõ (dữ liệu cũ). */
  validFrom: string | null;
  /** null = đang quản lý. */
  validTo: string | null;
  fromPrecision: "DATETIME" | "DATE" | "UNKNOWN";
  toPrecision: "DATETIME" | "DATE" | "UNKNOWN" | null;
  status: "CONFIRMED" | "NEEDS_REVIEW" | "VOID";
  startReason: string;
  endReason: string | null;
  note: string | null;
  voidReason: string | null;
  replacedById: string | null;
  createdAt: string;
}
export interface BranchHistoryResponse {
  subject: {
    kind: "vehicle" | "trailer" | "driver";
    id: string;
    label: string;
    branchId: string | null;
  };
  periods: BranchPeriod[];
}
export const getBranchHistoryApi = (kind: BranchSubjectKind, id: string) =>
  apiFetch<BranchHistoryResponse>(`/api/v1/fleet-lifecycle/${kind}/${id}/branch-history`);
export const transferBranchApi = (
  kind: BranchSubjectKind,
  id: string,
  body: { toBranchId: string; effectiveDate?: string; reason?: string },
) =>
  apiFetch<{ message?: string; period?: BranchPeriod }>(
    `/api/v1/fleet-lifecycle/${kind}/${id}/branch-transfer`,
    { method: "POST", body: JSON.stringify(body) },
  );
export const unassignBranchApi = (
  kind: BranchSubjectKind,
  id: string,
  body: { effectiveDate?: string; reason: string },
) =>
  apiFetch<{ message?: string }>(
    `/api/v1/fleet-lifecycle/${kind}/${id}/branch-unassign`,
    { method: "POST", body: JSON.stringify(body) },
  );
export const correctBranchPeriodApi = (
  periodId: string,
  body: {
    branchId?: string;
    validFrom?: string | null;
    validTo?: string | null;
    note?: string;
    reason: string;
  },
) =>
  apiFetch<{ message?: string; period?: BranchPeriod }>(
    `/api/v1/fleet-lifecycle/branch-periods/${periodId}/correct`,
    { method: "PUT", body: JSON.stringify(body) },
  );

export interface SaleImpactItem {
  type: string;
  label: string;
  href?: string;
}
export interface SaleImpact {
  blockers: SaleImpactItem[];
  warnings: SaleImpactItem[];
}
export interface RecordSaleBody {
  contractDate?: string;
  handoverAt: string;
  operationEndAt: string;
  handoverOdometer?: number;
  /** Bỏ trống = biển kết thúc tại mốc bàn giao (bán là thu hồi luôn). */
  plateEndAt?: string;
  plateEvidence?: string;
  note?: string;
}

export const getFleetHistoryApi = (kind: FleetAssetKind, id: string) =>
  apiFetch<FleetHistory>(`/api/v1/fleet-lifecycle/${kind}/${id}/history`);
export const getSaleImpactApi = (
  kind: FleetAssetKind,
  id: string,
  operationEndAt: string,
) =>
  apiFetch<SaleImpact>(
    `/api/v1/fleet-lifecycle/${kind}/${id}/sale-impact?${new URLSearchParams({ operationEndAt })}`,
  );
export const recordFleetSaleApi = (
  kind: FleetAssetKind,
  id: string,
  body: RecordSaleBody,
  idempotencyKey: string,
) =>
  apiFetch<{ message?: string }>(`/api/v1/fleet-lifecycle/${kind}/${id}/sale`, {
    method: "POST",
    headers: { "Idempotency-Key": idempotencyKey },
    body: JSON.stringify(body),
  });
export const correctFleetSaleApi = (saleId: string, reason: string) =>
  apiFetch<{ message: string }>(`/api/v1/fleet-lifecycle/sales/${saleId}/correct`, {
    method: "PUT",
    body: JSON.stringify({ reason }),
  });
/** Đọc danh sách `blockers` từ lỗi 409 khi ghi nhận bán. */
export function readSaleBlockers(err: unknown): SaleImpactItem[] {
  if (!(err instanceof ApiError) || err.status !== 409) return [];
  const b = (err.payload as { blockers?: unknown } | undefined)?.blockers;
  return Array.isArray(b) ? (b as SaleImpactItem[]) : [];
}

// ---- P4: đổi biển, thu hồi biển, đổi định danh, tiếp nhận lại, tra cứu biển ----

export interface PlateLookupMatch {
  assignmentId: string;
  kind: "vehicle" | "trailer";
  assetId: string;
  code: string | null;
  currentPlate: string;
  fleetStatus: FleetStatus;
  deleted: boolean;
  plateRaw: string;
  validFrom: string | null;
  validTo: string | null;
  endPending: boolean;
  status: "CONFIRMED" | "NEEDS_REVIEW" | "VOID";
}
export interface PlateLookupResult {
  matches: PlateLookupMatch[];
  busyOutsideScope: boolean;
}
export type IdentityChangeKind = "DATA_FIX" | "ENGINE_REPLACEMENT" | "DOCUMENT_UPDATE";

export const lookupPlateApi = (plate: string) =>
  apiFetch<PlateLookupResult>(
    `/api/v1/fleet-lifecycle/plates/lookup?${new URLSearchParams({ plate })}`,
  );
export const changeFleetPlateApi = (
  kind: FleetAssetKind,
  id: string,
  body: { plateNumber: string; validFrom: string; evidenceNote?: string },
) =>
  apiFetch<{ message?: string }>(`/api/v1/fleet-lifecycle/${kind}/${id}/plates`, {
    method: "POST",
    body: JSON.stringify(body),
  });
export const endFleetPlateApi = (
  assignmentId: string,
  body: { validTo: string; evidenceNote?: string },
) =>
  apiFetch<{ message?: string }>(`/api/v1/fleet-lifecycle/plates/${assignmentId}/end`, {
    method: "PUT",
    body: JSON.stringify(body),
  });
export const repurchaseFleetApi = (
  kind: FleetAssetKind,
  id: string,
  body: {
    startedAt: string;
    plateNumber: string;
    plateValidFrom: string;
    verificationNote: string;
    engineNumber?: string;
    /** Chi nhánh quản lý sau tiếp nhận — bỏ trống = về chi nhánh cuối trước khi bán. */
    branchId?: string;
  },
) =>
  apiFetch<{ message?: string }>(`/api/v1/fleet-lifecycle/${kind}/${id}/repurchase`, {
    method: "POST",
    body: JSON.stringify(body),
  });
export const changeFleetIdentityApi = (
  kind: FleetAssetKind,
  id: string,
  body: {
    field: "VIN" | "ENGINE_NUMBER";
    newValue: string;
    kind: IdentityChangeKind;
    effectiveAt?: string;
    reason: string;
    evidenceNote?: string;
  },
) =>
  apiFetch<{ message?: string }>(`/api/v1/fleet-lifecycle/${kind}/${id}/identity`, {
    method: "POST",
    body: JSON.stringify(body),
  });
